// Lightweight i18n for the customer-facing surfaces.
//
// Why a custom one and not a library: zero new deps; we support three
// locales (ru, en, es); only Russian has the irregular one/few/many
// plural rules — Spanish and English collapse onto `.one` / `.many`;
// bundle size matters because PWA precaches the JS for low-bandwidth
// re-loads.
//
// How it works:
//   <LangProvider lang={user.language || "ru"}>...</LangProvider>
// then in any descendant:
//   const t = useT();
//   <button>{t("post.iWantThis")}</button>
//
// If a key is missing in the active language we fall back to English; if
// English is missing too we surface the key itself so dev notices.
//
// Admin/manager-only UI is intentionally NOT translated — the only admin
// is the operator and English is faster for ops.
import { createContext, useContext } from "react";

export const STRINGS = {
  ru: {
    // App.jsx — welcome / empty states
    "welcome.title": "Добро пожаловать в BuyerGram",
    "welcome.customer": "Открой «Каналы», чтобы посмотреть подборки товаров, или «Чаты», чтобы написать менеджеру об оплаченном заказе.",
    "welcome.staff": "Выбери чат или канал в боковой панели, чтобы начать.",
    "welcome.browseChannels": "Открыть каналы",

    // Sidebar tabs + search
    "tab.chats": "Чаты",
    "tab.channels": "Каналы",
    // Sidebar grouping for the brand-first channel architecture (migration 0055).
    "sidebar.group.brands":     "Бренды",
    "sidebar.group.categories": "Категории",
    // Gender filter chips above the channel feed (migration 0055).
    "channelfeed.gender.all":    "Все",
    "channelfeed.gender.women":  "Женское",
    "channelfeed.gender.men":    "Мужское",
    "channelfeed.gender.kids":   "Детское",
    "channelfeed.gender.unisex": "Унисекс",
    "post.variants.title.one":  "{n} цвет в наличии",
    "post.variants.title.few":  "{n} цвета в наличии",
    "post.variants.title.many": "{n} цветов в наличии",
    "post.variants.sizes":      "Размеры в наличии:",
    "post.variants.soldOut":    "Распродано",
    "post.variants.lowStock.one":  "Остался {n} шт",
    "post.variants.lowStock.few":  "Осталось {n} шт",
    "post.variants.lowStock.many": "Осталось {n} шт",
    "sidebar.brand.empty":      "(пусто)",
    "sidebar.brand.showEmpty":  "Показать пустые бренды",
    "sidebar.brand.hideEmpty":  "Скрыть пустые бренды",
    "tab.groups": "Группы",
    "tab.admin": "Админ",
    "tab.search": "Поиск",
    "tab.search.noMatchDms": "Нет чатов по запросу",

    // Empty: channels
    "empty.channels.title": "Здесь пока пусто",
    "empty.channels.customer": "Скоро здесь появятся подборки товаров. Загляни сюда позже.",

    // Empty: DMs
    "empty.dms.title": "Нет переписок",
    "empty.dms.customer": "Напиши менеджеру, чтобы оформить заказ, или нажми «Хочу это» под любым постом во вкладке «Каналы».",
    "empty.dms.cta.customer": "Написать менеджеру",

    // Empty: channel feed
    "empty.channelFeed.title": "Здесь пока нет постов",
    "empty.channelFeed.customer": "Новые сделки появятся, как только их добавят. Включи Telegram-уведомления в настройках, чтобы не пропустить.",

    // Empty: chat history
    "empty.dm.title": "Поздоровайся",
    "empty.dm.body": "Напиши первое сообщение, чтобы начать разговор.",
    "empty.topic.title": "Здесь пока нет сообщений",
    "empty.topic.body": "Будь первым, кто напишет в этот топик.",

    // Channel feed
    "channel.subtitle.posts.one": "канал · {n} пост",
    "channel.subtitle.posts.few": "канал · {n} поста",
    "channel.subtitle.posts.many": "канал · {n} постов",
    "post.iWantThis": "Хочу это →",
    "post.getCashbackLink": "Получить кешбэк-ссылку",
    "post.directLink": "Прямая ссылка",

    // Auth
    "auth.signInTagline": "Войти, чтобы продолжить",
    // Editorial hero + tagline shown above the AuthScreen form. One pair per mode.
    "auth.hero.login":      "С возвращением.",
    "auth.hero.register":   "Твой личный закупщик в США.",
    "auth.hero.confirm":    "Подтверди email.",
    "auth.hero.forgot":     "Восстановим пароль.",
    "auth.hero.reset":      "Придумай новый пароль.",
    "auth.tagline.login":    "Подборка скидок американских магазинов для реселлеров — пересылай своим клиентам, закупку и доставку берём на себя.",
    "auth.tagline.register": "BuyerGram — это не магазин. Это прямой канал к человеку, который покупает товары в США и отправляет тебе.",
    "auth.tagline.confirm":  "Письмо с кодом отправлено. Если не пришло — проверь спам.",
    "auth.tagline.forgot":   "Введи email — пришлём ссылку для сброса пароля.",
    "auth.tagline.reset":    "Пароль должен быть не короче 12 символов.",
    // Field labels + placeholders used inside the AuthScreen form.
    "auth.field.fullName":          "ФИО",
    "auth.field.fullName.ph":       "Иван Иванович Иванов",
    "auth.field.nickname":          "Никнейм",
    "auth.field.nickname.ph":       "ivan_ivanov",
    "auth.field.phone":             "Телефон",
    "auth.field.phone.ph":          "+7 999 1234567",
    "auth.field.confirmPassword":   "Подтверждение пароля",
    "auth.field.inviteCode":        "Код приглашения · опционально",
    "auth.field.codeFromEmail":     "Код из письма",
    "auth.field.newPassword":       "Новый пароль",
    "auth.codeSent":                "Код отправлен",
    // Inline form errors / validation messages.
    "auth.error.passwordsDontMatch":     "Пароли не совпадают",
    "auth.error.passwordMismatch":       "Пароль и подтверждение не совпадают.",
    "auth.error.phoneFormat":            "Укажите телефон в международном формате, например +79991234567.",
    "auth.error.pickNickname":           "Выберите свободный никнейм.",
    "auth.error.acceptTerms":            "Подтвердите согласие с Политикой конфиденциальности и Условиями использования.",
    "auth.info.forgotPlaceholder":       "Пока эта функция не подключена. Напиши на r.dolgalev@gmail.com — сброшу вручную в течение часа.",
    "auth.info.resetPlaceholder":        "Пока эта функция не подключена. Используй ссылку из письма или напиши менеджеру.",
    // Buttons / loading states.
    "auth.btn.justAMoment":      "Минутку…",
    "auth.btn.confirm":          "Подтвердить",
    "auth.btn.sendLink":         "Прислать ссылку",
    "auth.btn.sending":          "Отправляем…",
    "auth.btn.savePassword":     "Сохранить пароль",
    "auth.btn.saving":           "Сохраняем…",
    "auth.btn.forgotPassword":   "Забыл пароль?",
    // Back-links between modes.
    "auth.back.toRegister":   "← Назад к регистрации",
    "auth.back.toLogin":      "← Вернуться к входу",
    // Terms checkbox copy (prefix + linked text + connector).
    "auth.terms.prefix":   "Я ознакомлен и согласен с",
    "auth.terms.privacy": "Политикой конфиденциальности",
    "auth.terms.tos":     "Условиями использования",
    "auth.terms.and":     "и",
    "auth.registerTagline": "Создать аккаунт",
    "auth.confirmTagline": "Подтверди email",
    "auth.displayName": "Имя",
    "auth.displayName.placeholder": "Твоё имя",
    "auth.email": "Email",
    "auth.password": "Пароль",
    "auth.inviteCode": "Промокод друга (необязательно)",
    "auth.inviteCode.placeholder": "Если есть",
    "auth.confirmCode": "Код подтверждения",
    "auth.confirmCode.placeholder": "6-значный код",
    "auth.confirmCodeSent": "Код отправлен на",
    "auth.devCode": "Код для теста:",
    "auth.confirmEmail": "Подтвердить",
    "auth.verifying": "Проверяем…",
    "auth.back": "← Назад",
    "auth.createAccount": "Создать аккаунт",
    "auth.signIn": "Войти",
    "auth.pleaseWait": "Подожди…",
    "auth.alreadyHave": "Уже есть аккаунт?",
    "auth.noAccount": "Нет аккаунта?",
    "auth.toggleSignIn": "Войти",
    "auth.toggleRegister": "Зарегистрироваться",
    "auth.captchaRequired": "Пройди проверку",

    // Settings modal — customer-facing labels only
    "settings.title": "Настройки",
    "settings.tab.profile": "Профиль",
    "settings.tab.password": "Пароль",
    "settings.tab.subs": "Подписки",
    "settings.displayName": "Имя",
    "settings.email": "Email",
    "settings.language": "Язык",
    "settings.save": "Сохранить",
    "settings.avatar.hint":     "JPG / PNG / WebP — обрежу до круга автоматически",
    "settings.avatar.upload":   "Загрузить фото",
    "settings.avatar.change":   "Поменять фото",
    "settings.avatar.remove":   "Удалить",
    "settings.avatar.uploaded": "Фото обновлено",
    "settings.avatar.removed":  "Фото удалено",
    "settings.saving": "Сохраняем…",
    "settings.saved": "Сохранено",
    "settings.curPassword": "Текущий пароль",
    "settings.newPassword": "Новый пароль (от 12 символов)",
    "settings.changePassword": "Сменить пароль",
    "settings.changing": "Меняем…",
    "settings.passwordChanged": "Пароль изменён",
    "settings.subs.help": "Выбери каналы, по которым приходят уведомления в Telegram. В веб-приложении видны все каналы.",
    "settings.subs.empty": "Пока нет каналов.",
    "settings.subs.tgPush": "Telegram-уведомления",

    // Cashback / balance
    "balance.title": "Кешбэк-баланс",
    "balance.loading": "Загружаем…",
    "balance.available": "Доступно",
    "balance.minRedeem": "Минимум для вывода: $5.00",
    "balance.redeem": "Вывести",
    "balance.cancel": "Отмена",
    "balance.requestRedemption": "Запрос на вывод",
    "balance.amount": "Сумма ($)",
    "balance.method": "Способ",
    "balance.method.orderDiscount": "Зачесть в следующий заказ",
    "balance.method.amazonGift": "Подарочная карта Amazon (на email)",
    "balance.method.walmartGift": "Подарочная карта Walmart (на email)",
    "balance.sendGiftTo": "Отправить подарочную карту на",
    "balance.submit": "Отправить запрос",
    "balance.submitting": "Отправляем…",
    "balance.referral.title": "Твоя реферальная ссылка",
    "balance.referral.body": "Поделись ссылкой с друзьями. С каждого их заказа, который дошёл до статуса «Доставлен», ты получишь 1% от стоимости товаров (без оргсбора BuyerGram) на свой баланс. Это твой бонус — он не уменьшает кешбэк друга. Кешбэк используется только для оплаты товаров и услуг на BuyerGram.",
    "balance.usageHint": "Кешбэк можно потратить только на оплату товаров и услуг на BuyerGram. Вывод на внешние счета и подарочные карты не предусмотрен.",

    // ─── Help / FAQ ──────────────────────────────────────────────────
    // Static, hardcoded Q&A. Edits require a deploy (intentional — copy
    // changes are deliberate). Categories follow user-task arcs:
    // start → catalog/forwards → orders/cashback → curated → account → trouble.
    "help.title":                "Помощь",
    "help.subtitle":             "Ответы на частые вопросы. Не нашёл свой? Напиши нам.",
    "help.search":               "Поиск по вопросам…",
    "help.empty":                "Ничего не нашли. Сформулируй иначе или напиши в поддержку.",
    "help.contact.title":        "Нужна помощь от человека?",
    "help.contact.body":         "Если вопрос не покрыт здесь — напиши нам напрямую. Откроется чат со специалистом BuyerGram.",
    "help.contact.cta":          "Связаться с поддержкой",
    "help.contact.opening":      "Открываем чат…",
    "help.contact.fail":         "Не получилось открыть чат. Попробуй позже или напиши на support@buyergram.app.",

    "help.cat.start":            "Начало работы",
    "help.cat.catalog":          "Каталог и пересылка",
    "help.cat.orders":           "Заказы и кешбэк",
    "help.cat.curated":          "Закупки под ключ",
    "help.cat.account":          "Аккаунт и доступ",
    "help.cat.trouble":          "Неполадки",

    // Категория 1 — Начало работы
    "help.q.what":               "Что такое BuyerGram?",
    "help.a.what":                "BuyerGram — закрытая B2B-витрина для реселлеров. Мы агрегируем товары крупных американских ретейлеров (Nordstrom Rack, Walmart и другие), выкупаем их и доставляем в Россию. Реселлер — это ты: ты пересылаешь карточки своим клиентам в Telegram/MAX/любой другой канал, добавляешь свою наценку, мы обрабатываем заказ и платим тебе кешбэк.",
    "help.q.start":              "С чего начать после одобрения?",
    "help.a.start":               "1) Открой «Мой магазин» → «Настройки пересылки» и привяжи свой канал Telegram или MAX. 2) Зайди в любой канал (например, «Curated Shoes»), выбери пару карточек и нажми «Переслать» — карточка отправится в твой канал с твоей подписью и наценкой. 3) Когда клиент захочет купить — оформи заказ через «Мой магазин» → «Клиенты» или попроси клиента связаться с тобой.",
    "help.q.commission":         "Сколько BuyerGram берёт с заказа?",
    "help.a.commission":          "Оргсбор BuyerGram добавляется к цене товара и виден в карточке заказа отдельной строкой. Твоя наценка — это то, что ты сам ставишь сверху для своего клиента; она не делится с BuyerGram.",

    // Категория 2 — Каталог и пересылка
    "help.q.forward":            "Как переслать карточку клиенту?",
    "help.a.forward":             "На любой карточке в канале есть кнопка «Переслать». При первом использовании ты настроишь, куда отправлять (Telegram-канал, MAX-чат, или несколько целей). Перед отправкой можно отредактировать подпись и проверить превью — клиент увидит карточку с твоей наценкой, без упоминания BuyerGram.",
    "help.q.markup":              "Как настроить наценку?",
    "help.a.markup":              "В «Мой магазин» → «Настройки пересылки» задай свой курс USD→RUB и процент наценки. Курс берётся из последнего обновления ЦБ РФ; ты можешь добавить свою маржу сверху. В превью пересылки увидишь итоговую рублёвую цену, которую увидит клиент.",
    "help.q.bgcode":              "Что такое «BG-код» на карточке?",
    "help.a.bgcode":               "BG-код — внутренний идентификатор товара в BuyerGram (например, BG-A4F2K9). Используй его, когда клиент хочет уточнить заказ или повторить покупку: ссылка на исходный товар у ретейлера видна только в премиум-тарифе, а BG-код работает везде.",
    "help.q.search":              "Как искать товар по коду?",
    "help.a.search":               "В верхней панели любого канала есть строка поиска. Введи BG-код или часть названия — увидишь карточку, даже если она в другом канале.",

    // Категория 3 — Заказы и кешбэк
    "help.q.order":               "Как оформить заказ за клиента?",
    "help.a.order":                "В «Мой магазин» → «Клиенты» открой карточку клиента, нажми «Новый заказ», добавь товары по BG-коду или ссылке. Мы выкупаем и отправляем; статус заказа меняется по мере прохождения этапов (оплачен → выкуплен → отправлен → доставлен).",
    "help.q.cashback":             "Когда я получаю кешбэк?",
    "help.a.cashback":              "Кешбэк начисляется автоматически при переходе заказа в статус «Доставлен». Размер — это процент от стоимости товара (без оргсбора BuyerGram). Видно в «Кешбэк-баланс».",
    "help.q.cashbackUse":          "На что можно потратить кешбэк?",
    "help.a.cashbackUse":           "Только на оплату следующих заказов внутри BuyerGram. Вывода на внешние счета или подарочные карты нет — это политика площадки.",
    "help.q.referral":             "Что такое реферальная ссылка?",
    "help.a.referral":              "Поделись своей ссылкой с другом-реселлером. Когда он зарегистрируется и пройдёт одобрение — с каждого его заказа, дошедшего до «Доставлен», ты получаешь 1% от стоимости товара (без оргсбора) к своему балансу. Это твой бонус — он не уменьшает кешбэк друга.",

    // Категория 4 — Закупки под ключ
    "help.q.curated":              "Что такое «Закупки» в Мой магазин?",
    "help.a.curated":               "Это сервис, когда мы выкупаем товары по твоему запросу — даже если их нет в каталоге. Ты присылаешь ссылки или дашь нам доступ к своему аккаунту в магазине, мы оформляем заказ, выставляем инвойс в USDT/USDC/RUB и доставляем.",
    "help.q.curated.start":        "Как создать закупку?",
    "help.a.curated.start":         "Открой «Мой магазин» → «Закупки» → «Новая заявка». Выбери тип (ссылки на товары или доступ к аккаунту), укажи количество и предполагаемую сумму, выбери валюту инвойса. Мы согласуем точную цену, выставим инвойс, выкупим, отправим.",
    "help.q.curated.status":       "Какие бывают статусы?",
    "help.a.curated.status":        "Отправлена → Согласована (цена подтверждена) → Инвойс выставлен → Оплачена → Куплена → Получена нами → Отправлена тебе → Завершена. На любом из первых двух этапов заявку можно отменить.",

    // Категория 5 — Аккаунт и доступ
    "help.q.application":          "Почему я вижу скрытые цены?",
    "help.a.application":           "Доступ к ценам и кнопкам пересылки открывается после одобрения заявки. Пока статус «На рассмотрении» или «Отклонена» — карточки видны, но цены замаскированы. Это защищает интересы уже одобренных реселлеров.",
    "help.q.approve":               "Как долго рассматривают заявку?",
    "help.a.approve":                "Обычно 1–2 рабочих дня. Если заявка отклонена — ты увидишь причину в баннере на главной и сможешь подать повторно с новым подтверждением канала.",
    "help.q.lang":                  "Как сменить язык?",
    "help.a.lang":                   "Настройки → Профиль → Язык. Переключение мгновенное, все экраны переведутся.",
    "help.q.nick":                  "Как изменить никнейм?",
    "help.a.nick":                   "Настройки → Профиль → Никнейм. Никнейм должен быть уникальным; ФИО видишь только ты и админ — клиенты и другие реселлеры видят только @nickname.",
    "help.q.logout":                 "Как выйти из аккаунта?",
    "help.a.logout":                  "Иконка «Выйти» в правом верхнем углу боковой панели. На iPhone после выхода страница перезагрузится — это нормально.",

    // Категория 6 — Неполадки
    "help.q.trouble.notif":          "Не приходят уведомления.",
    "help.a.trouble.notif":           "Открой Настройки → Уведомления и проверь, что push-подписка активна. На iPhone уведомления работают только если PWA добавлена на главный экран. Если бот в Telegram молчит — проверь, что не отправлял ему /stop.",
    "help.q.trouble.forward":        "Не получается переслать карточку.",
    "help.a.trouble.forward":         "Сначала убедись, что в «Настройках пересылки» привязан хотя бы один канал и в нём есть BuyerGram-бот с правом постить. Если бот удалён — кнопка покажет пустое состояние и попросит добавить заново. Подробнее в «Настройках пересылки».",
    "help.q.trouble.pwa":            "Что-то сломалось / белый экран.",
    "help.a.trouble.pwa":             "Обычно это устаревший кеш на iPhone. Закрой вкладку, открой её снова. Если не помогло — Настройки iOS → Safari → Очистить историю и данные сайтов. На macOS/Windows — Ctrl/Cmd+Shift+R для жёсткой перезагрузки.",
    "help.q.trouble.ru":             "Сайт медленно открывается из России.",
    "help.a.trouble.ru":              "Мы хостимся в Европе (OVH, Франция) и не используем Cloudflare — он у нас отключён намеренно, чтобы РКН не замедлял загрузку. Если всё равно тормозит — проверь, не упирается ли это в твоего провайдера; альтернативно установи нашу PWA на главный экран, она работает быстрее.",

    "balance.copy": "Скопировать",
    "balance.copied": "Скопировано!",
    "balance.telegram.title": "Доставка в Telegram",
    "balance.telegram.linked": "Привязан к",
    "balance.telegram.note": "Ты будешь получать новые подборки в Telegram, даже если веб-приложение недоступно.",
    "balance.telegram.unlink": "Отвязать",
    "balance.telegram.unlinkConfirm": "Отвязать Telegram? Бот перестанет отправлять сообщения.",
    "balance.telegram.openBot": "Открой",
    "balance.telegram.thenSend": "в Telegram и отправь:",
    "balance.telegram.codeExpires": "Код действует {n} минут. Открой это окно заново, чтобы получить новый.",
    "balance.telegram.unsetHelp": "Привяжи Telegram, чтобы получать новые сделки, если веб-приложение блокируется. Последний резервный канал доставки.",
    "balance.telegram.getCode": "Получить код привязки",
    "balance.pendingRedemptions": "Запросы на вывод",
    "balance.requestedOn": "запрошен",
    "balance.recentLedger": "История начислений",
    "balance.ledger.empty": "Пока пусто. Нажми «Получить кешбэк-ссылку» на любом посте и оформи покупку — кешбэк зачисляется в течение 1–60 дней после подтверждения сделки магазином.",
    "balance.recentTransactions": "Последние транзакции",
    "balance.transactions.empty": "Пока нет транзакций.",
    "balance.sale": "продажа",

    // Common
    "common.cancel": "Отмена",
    "common.ok": "OK",
    "common.loading": "Загружаем…",
    "common.confirm": "Подтвердить",

    // First-run intro — BuyerGram reseller flow. v2 rewrite (the older
    // CloudGram copy talked about "I want this" + DM-a-manager, which
    // isn't the reseller workflow).
    "intro.kicker": "От редакции",
    "intro.title": "Добро пожаловать в BuyerGram",
    "intro.lead": "Здесь вы выбираете товары со скидкой из США и пересылаете их в свой канал — клиенты видят вашу цену в рублях.",
    "intro.step1": "Поставьте свой курс $→₽ — мы автоматически переведём цену каждой карточки в рубли с вашей наценкой.",
    "intro.step2": "Добавьте @Buyergrambot админом в свой Telegram-канал, чтобы он мог отправлять туда карточки.",
    "intro.step3": "Откройте любой канал, выберите карточки и нажмите «Переслать» — мы отправим их в ваш Telegram автоматически.",
    "intro.cta": "Завершить настройку",
    "intro.later": "Позже",
    // Legacy key — kept so older clients that haven't reloaded the
    // bundle yet don't crash on a missing translation.
    "intro.gotIt": "Понятно",

    // Setup checklist banner — shown above the feed until the reseller
    // has both an FX rate and at least one enabled forward target.
    "setup.banner.aria": "Завершите настройку BuyerGram",
    "setup.banner.both": "Завершите настройку, чтобы начать пересылку.",
    "setup.banner.needFx": "Остался один шаг: поставьте свой курс $→₽.",
    "setup.banner.needTarget": "Остался один шаг: добавьте Telegram-канал для пересылки.",
    "setup.banner.detail": "Без этого кнопка «Переслать» не сможет отправить карточки в ваш канал.",
    "setup.banner.cta": "Настроить",

    // Altcha PoW captcha
    "altcha.loading": "Готовим проверку…",
    "altcha.solving": "Проверка браузера…",
    "altcha.ready": "Проверка пройдена",
    "altcha.error": "Ошибка проверки",

    // Orders (Phase 1A)
    "post.order": "Заказать",
    "order.modal.title": "Новый заказ",
    "order.variant": "Вариант (размер/цвет/объём)",
    "order.variant.placeholder": "Например: M, синий · 1000 МЕ x60 · 256 ГБ Black",
    "order.qty": "Количество",
    "order.notes": "Комментарий",
    "order.notes.placeholder": "Особые пожелания, адрес доставки, и т.п.",
    "order.total": "Итого",
    "order.place": "Оформить заказ",
    "order.placing": "Оформляем…",
    "order.placed.ok": "Заказ оформлен. Я свяжусь с тобой скоро.",
    "cart.title": "Корзина",
    "cart.add": "В корзину",
    "cart.added": "Добавлено в корзину",
    "cart.remove": "Убрать",
    "cart.each": "за ед.",
    "cart.empty": "Корзина пуста",
    "cart.emptyBody": "Корзина пуста. Открой канал и добавь товары.",
    "cart.checkout": "Оформить заказ",
    "cart.subtotalN": "Товары ({n})",
    "cart.fee": "Сервисный сбор",
    "cart.cashback": "Кэшбэк",
    "cart.placed": "Заказ №{num} оформлен ({n} тов.)",
    "cart.placedSomeSkipped": "Заказ №{num}: оформлено {ok}, пропущено {skipped} (недоступны)",
    "orders.group.items": "{n} тов. в заказе",
    "cart.rublePrice": "Цена ₽",
    "cart.fxSuggested": "по курсу {rate} ₽/$",
    "order.rublePrice.title": "Цена для моего клиента (₽)",
    "order.rublePrice.label": "Моя цена",
    "order.rublePrice.unset": "не указана",
    "order.rublePrice.set": "указать",
    "order.rublePrice.saved": "Цена сохранена",
    "order.rublePrice.invalid": "Неверная цена",
    "invoice.cta": "Счёт",
    "invoice.title": "Счёт",
    "invoice.from": "От",
    "invoice.to": "Клиент",
    "invoice.item": "Товар",
    "invoice.qty": "Кол-во",
    "invoice.price": "Цена",
    "invoice.total": "Итого",
    "invoice.copy": "Копировать текст",
    "invoice.pdf": "Скачать PDF",
    "invoice.copied": "Счёт скопирован",
    "invoice.copyFail": "Не удалось скопировать",
    "invoice.popupBlocked": "Разрешите всплывающие окна для PDF",
    "mystore.tab.batches": "Закупки",
    "admin.tab.batches": "Закупки",
    "batch.heading": "Закупки",
    "batch.new": "Новая закупка",
    "batch.empty": "Закупок пока нет. Создайте, чтобы объединить заказы одного магазина.",
    "batch.unnamed": "Без названия",
    "batch.itemsN": "{n} тов.",
    "batch.retailer": "Магазин",
    "batch.retailer.ph": "Напр.: Nordstrom Rack",
    "batch.supplierRef": "№ заказа поставщика",
    "batch.supplierRef.ph": "Напр.: отправлено на выкуп",
    "batch.tariff": "Тариф ₽/кг",
    "batch.statusLabel": "Статус",
    "batch.status.open": "Открыта",
    "batch.status.ordered": "Заказана",
    "batch.status.shipped": "Отправлена",
    "batch.status.closed": "Закрыта",
    "batch.status.cancelled": "Отменена",
    "batch.noMembers": "В закупке пока нет товаров.",
    "batch.kg": "кг",
    "batch.goods": "Товары",
    "batch.delivery": "Доставка",
    "batch.grand": "Итого",
    "batch.noCandidates": "Нет подходящих заказов для добавления.",
    "batch.addSelected": "Добавить ({n})",
    "batch.addOrders": "Добавить заказы",
    "batch.delete": "Удалить закупку",
    "batch.delete.confirm": "Удалить закупку? Заказы останутся, но выйдут из неё.",
    "orders.mine.title": "Мои заказы",
    "orders.mine.empty": "Пока нет заказов. Открой канал и нажми «Заказать» под товаром.",
    "orders.deletedPost": "(товар удалён)",
    "order.status.placed":    "Принят",
    "order.status.confirmed": "Подтверждён",
    "order.status.paid":      "Оплачен",
    "order.status.purchased": "Куплен",
    "order.status.received":  "На складе",
    "order.status.shipped":   "Отправлен",
    "order.status.delivered": "Доставлен",
    "order.status.cancelled": "Отменён",
    "order.status.unavailable": "Нет в наличии",
    "order.fulfilled.of": "куплено {fulfilled} из {total}",
    "order.shipping.title": "Получатель и адрес доставки",
    "order.recipient.name": "Имя получателя",
    "order.recipient.name.placeholder": "Имя того, кто получит посылку",
    "order.recipient.phone": "Телефон получателя",
    "order.address": "Адрес",
    "order.address.placeholder": "Улица, дом, кв., город, индекс",
    "order.country": "Страна",
    "order.recipient.required": "Укажи имя получателя",
    "order.address.required": "Укажи адрес доставки",

    // ─── Address book + picker (migration 0054) ──────────────────
    "order.address.source.label":    "Адрес доставки",
    "order.address.source.my":       "Мой адрес",
    "order.address.source.customer": "Адрес клиента",
    "order.address.source.manual":   "Ввести вручную",
    "order.address.empty.my":        "У тебя пока нет сохранённых адресов. Заполни поля ниже — после оформления заказа я предложу сохранить.",
    "order.address.empty.customer":  "У этого клиента пока нет сохранённых адресов.",
    "order.address.empty.noCustomer":"Сначала выбери клиента в верхней части формы.",
    "order.address.savePrompt.title":   "Сохранить этот адрес?",
    "order.address.savePrompt.bodyMy":  "Добавлю его в твою адресную книгу — в следующий раз можно будет выбрать в один клик.",
    "order.address.savePrompt.bodyCustomer": "Добавлю его к клиенту «{name}» — в следующий раз появится в выпадающем списке.",
    "order.address.savePrompt.label":    "Название (необязательно)",
    "order.address.savePrompt.labelPh":  "Дом, Офис, Мама…",
    "order.address.savePrompt.save":     "Сохранить",
    "order.address.savePrompt.skip":     "Не сохранять",
    "order.address.savePrompt.saved":    "Адрес сохранён",
    "order.address.savePrompt.saveFail": "Не получилось сохранить адрес",
    "order.address.picker.default":      "по умолчанию",
    "order.address.picker.primary":      "основной",

    // ─── Settings → Адресная книга (own) ─────────────────────────
    "settings.tab.addresses":      "Адресная книга",
    "settings.addresses.title":    "Мои адреса",
    "settings.addresses.subtitle": "Сохрани адреса, на которые часто заказываешь. Они появятся в выпадающем списке при оформлении заказа.",
    "settings.addresses.empty":    "Адресов пока нет. Добавь первый ниже.",
    "settings.addresses.add":      "Добавить адрес",
    "settings.addresses.label":    "Название",
    "settings.addresses.labelPh":  "Дом, Офис, Мама…",
    "settings.addresses.recipient":"Имя получателя",
    "settings.addresses.phone":    "Телефон",
    "settings.addresses.address":  "Адрес",
    "settings.addresses.country":  "Страна",
    "settings.addresses.default":  "по умолчанию",
    "settings.addresses.setDefault":"Сделать основным",
    "settings.addresses.edit":     "Изменить",
    "settings.addresses.delete":   "Удалить",
    "settings.addresses.save":     "Сохранить",
    "settings.addresses.cancel":   "Отмена",
    "settings.addresses.deleteConfirm": "Удалить этот адрес из книги?",

    // ─── Customer card → Адреса ──────────────────────────────────
    "customer.addresses.title":      "Адреса",
    "customer.addresses.empty":      "Адресов пока нет. Добавь первый ниже.",
    "customer.addresses.add":        "Добавить адрес",
    "customer.addresses.primary":    "основной",
    "customer.addresses.setPrimary": "Сделать основным",
    "customer.addresses.deleteConfirm": "Удалить этот адрес?",

    // Shipments / consolidation (Phase 3)
    "orders.tab.orders": "Заказы",
    "orders.tab.shipments": "Посылки",
    "orders.consolidate.hint": "Выбери товары на складе, чтобы объединить в одну посылку",
    "orders.consolidate.selected": "Выбрано: {n}",
    "orders.consolidate.cta": "Объединить в посылку",
    "shipment.label": "Посылка",
    "shipment.itemsCount": "товаров",
    "shipment.status.building":  "Собирается",
    "shipment.status.ready":     "Готова к отправке",
    "shipment.status.shipped":   "Отправлена",
    "shipment.status.delivered": "Доставлена",
    "shipment.status.cancelled": "Отменена",
    "shipment.ready.cta": "Готово, отправляйте",
    "shipment.ready.ok": "Передано на отправку",
    "shipment.ready.waiting": "Жди — менеджер скоро отправит и пришлёт трек-номер.",
    "shipment.cancel": "Отменить",
    "shipment.cancel.confirm": "Отменить посылку? Заказы вернутся в список и можно будет собрать заново.",
    "shipment.cancelled.ok": "Посылка отменена; заказы вернулись в список.",
    "shipment.created.ok": "Посылка создана. Открой вкладку «Посылки», чтобы посмотреть или отредактировать.",
    "shipments.mine.empty": "Пока нет посылок. Когда заказы будут готовы к отправке, объедини их во вкладке «Заказы».",

    // Payment confirmation flow (Feature 1)
    "common.copied":             "Скопировано",
    "order.payment.cta":         "Оплатить заказ",
    "order.payment.requestQr":      "Запросить QR-код или ссылку",
    "order.payment.requestQr.note": "Откроется чат с продавцом — он пришлёт QR или ссылку для оплаты",
    "order.payment.orPayDirect":    "или оплатите напрямую",
    "order.payment.requestSent":    "Запрос отправлен в чат",
    "order.payment.usdt":        "USDT (TRC20)",
    "order.payment.sbp":         "СБП (по номеру телефона)",
    "order.payment.card":        "Перевод на карту",
    "order.payment.amountDue":   "К оплате",
    "order.payment.noMethods":   "Реквизиты ещё не настроены — напиши менеджеру.",
    "order.payment.pickMethodFirst": "Сначала выбери способ оплаты.",
    "order.payment.attachScreenshot": "Прикрепить скриншот оплаты",
    "order.payment.uploadHint":  "PNG/JPG, до 5 МБ",
    "order.payment.uploaded.ok": "Скриншот загружен, менеджер скоро проверит.",
    "order.payment.awaitingReview": "Оплата получена, ждём подтверждения менеджера.",
    "order.payment.viewScreenshot": "Скриншот",

    // СП group-buy (Feature 2)
    "post.locked":            "Закупка закрыта",
    "post.gb.locked":         "Закупка закрыта",
    "post.gb.open":           "Совместная закупка",
    "post.gb.expired":        "Время истекло",
    "post.gb.committed":      "Заказано: {n}",

    // Customs / declared value (Feature 3)
    "common.edit":                       "изменить",
    "common.save":                       "Сохранить",
    "shipment.customs.declared":         "К декларации",
    "shipment.customs.overThreshold":    "⚠ Свыше 200 € — будет пошлина. Разбей посылку на части или укажи меньшую заявленную стоимость.",
    "shipment.customs.declaredItem":     "Заявлено",
    "shipment.customs.usingPrice":       "= цена покупки",
    "shipment.customs.saved":            "Стоимость сохранена",
    "shipment.customs.invalidValue":     "Введи число ≥ 0",

    // Organizer fee (Feature 7)
    "order.subtotal":      "Товары",
    "order.facilitate.title":    "Покупка",
    "order.facilitate.byUs":     "Закупаем мы",
    "order.facilitate.self":     "Закупаю сам",
    "order.facilitate.selfNote": "Вы заказываете сами — без нашей комиссии. BuyerGram ведёт учёт заказа.",
    "order.organizerFee":  "Оргсбор ({pct}%)",

    // In-house BuyerGram cashback (migration 0028)
    "order.cashback.pending": "+${amt} кешбэк ({pct}%) после доставки",
    "order.cashback.earned":  "✓ +${amt} кешбэк зачислен",
    "order.cashback.preview": "Кешбэк {pct}% после доставки",
    "channel.cashbackBadge":  "{pct}% кешбэк",

    // ─── Admin panel ────────────────────────────────────────────
    // Top tabs
    "admin.tab.overview":      "Главная",
    "admin.tab.applications":  "Заявки",
    "admin.tab.curated":       "Закупки",
    "admin.tab.subscriptions": "Подписки",
    "admin.tab.orders":        "Заказы",
    "admin.tab.shipments":     "Поставки",
    "admin.tab.settings":      "Настройки",
    // Sub-nav pills under Настройки
    "admin.sub.payments":      "Платежи / FX",
    "admin.sub.channels":      "Каналы",
    "admin.sub.users":         "Пользователи",
    "admin.sub.groups":        "Группы и темы",
    "admin.sub.wa":            "WhatsApp",
    "admin.sub.tokens":        "Сервисные токены",
    "admin.sub.templates":     "Шаблоны сообщений",
    "admin.sub.redemptions":   "Возвраты",
    "admin.sub.audit":         "Журнал действий",
    // AdminHome
    "admin.home.kicker":          "АДМИНИСТРАЦИЯ",
    "admin.home.greeting.night":  "Доброй ночи",
    "admin.home.greeting.morning":"Доброе утро",
    "admin.home.greeting.day":    "Добрый день",
    "admin.home.greeting.evening":"Добрый вечер",
    "admin.home.subtitle":        "Очереди, требующие внимания. Кликните на карточку, чтобы открыть.",
    "admin.home.loading":         "Загружаем…",
    "admin.home.card.applications.label": "Заявки на проверке",
    "admin.home.card.applications.empty": "Очередь пуста",
    "admin.home.card.applications.hint":  "Подтвердить или отклонить",
    "admin.home.card.curated.label":      "Активные закупки",
    "admin.home.card.curated.empty":      "Все закрыты",
    "admin.home.card.curated.hint":       "{n0} новых · {n1} ждут оплаты",
    "admin.home.card.subscriptions.label":"Подписки",
    "admin.home.card.subscriptions.hint": "К очереди",
    "admin.home.bucket.submitted": "Новые",
    "admin.home.bucket.priced":    "Согласовано",
    "admin.home.bucket.purchased": "Куплено",
    "admin.home.bucket.invoiced":  "Ждут оплаты",
    "admin.home.bucket.paid":      "Оплачено",
    "admin.home.recent.apps":      "Свежие заявки",
    "admin.home.recent.curated":   "Свежие закупки",
    "admin.home.recent.openAll":   "Открыть всё →",
    "admin.home.channelLink":      "Канал →",
    "admin.home.timeAgo.now":      "только что",
    "admin.home.timeAgo.min":      "{n} мин назад",
    "admin.home.timeAgo.hour":     "{n} ч назад",
    "admin.home.timeAgo.day":      "{n} дн назад",
    "admin.home.items.one":  "товар",
    "admin.home.items.few":  "товара",
    "admin.home.items.many": "товаров",

    // Settings → Profile (nickname / ФИО / claim widget)
    "settings.avatar.label":          "Фото профиля",
    "settings.nickname.public":       "Никнейм (публичный)",
    "settings.nickname.publicHint":   "Это видят остальные пользователи BuyerGram. Чтобы изменить — напишите в поддержку.",
    "settings.nickname.claim.title":  "Выберите публичный никнейм",
    "settings.nickname.claim.hint":   "Сейчас вас видят как @{nick} — это временный идентификатор. Закрепите никнейм один раз; изменить его потом можно только через поддержку.",
    "settings.nickname.claim.placeholder": "мой_никнейм",
    "settings.nickname.claim.cta":    "Закрепить никнейм",
    "settings.nickname.err.reserved": "Этот никнейм зарезервирован.",
    "settings.nickname.err.taken":    "Этот никнейм уже занят.",
    "settings.nickname.err.format":   "3-30 символов: a-z, 0-9, нижнее подчёркивание.",
    "settings.nickname.claimed.ok":   "Никнейм @{nick} закреплён.",
    "settings.fullName.label":        "ФИО (приватно)",
    "settings.language.ru":           "Русский",
    "settings.language.en":           "English",
    "settings.language.es":           "Español",
    "settings.subs.posts.one":  "пост",
    "settings.subs.posts.few":  "поста",
    "settings.subs.posts.many": "постов",

    // Sidebar empty states (Channels / Groups)
    "sidebar.empty.channels.admin":  "Каналы — это подборки товаров. Создать первый можно в Настройки → Каналы.",
    "sidebar.empty.groups.title":    "Пока нет групп",
    "sidebar.empty.groups.admin":    "Группы — приватные рабочие пространства для команды. Создать можно в Настройки → Группы и темы.",
    "sidebar.empty.groups.customer": "Вас ещё не добавили ни в одну группу. Администратор может добавить вас.",

    // ─── Admin: Orders tab ──────────────────────────────────────
    "admin.orders.filter.all":         "Все статусы",
    // Order status human labels — used by the filter dropdown and
    // the inline status pickers on each order row.
    "admin.orders.status.placed":      "размещён",
    "admin.orders.status.confirmed":   "подтверждён",
    "admin.orders.status.paid":        "оплачен",
    "admin.orders.status.purchased":   "куплен",
    "admin.orders.status.received":    "на складе",
    "admin.orders.status.shipped":     "отправлен",
    "admin.orders.status.delivered":   "доставлен",
    "admin.orders.status.cancelled":   "отменён",
    "admin.orders.status.unavailable": "недоступен",
    // Shipment statuses
    "admin.ships.status.building":     "формируется",
    "admin.ships.status.ready":        "готов",
    "admin.ships.status.shipped":      "отправлен",
    "admin.ships.status.delivered":    "доставлен",
    "admin.ships.status.cancelled":    "отменён",
    "admin.orders.refresh":            "Обновить",
    "admin.orders.refreshing":         "Загружаю…",
    "admin.orders.empty":              "Заказов под этот фильтр нет.",
    "admin.orders.deletedPost":        "(удалённый пост)",
    "admin.orders.qtyShort":           "кол-во {n}",
    "admin.orders.source":             "Купить",
    "admin.orders.itemNo":             "Артикул",
    "admin.orders.chat":               "Чат с клиентом",
    "admin.orders.chatOpened":         "Чат открыт — см. «Сообщения»",
    "admin.orders.fulfilledShort":     "(куплено {n})",
    "admin.orders.paymentBadge.needReview": "✓ Нужна проверка",
    "admin.orders.paymentBadge.uploaded":   "✓ Чек загружен",
    "admin.orders.paymentBadge.open":       "Открыть →",
    "admin.orders.cancel.placeholder":      "Почему отменить заказ?",
    "admin.orders.cancel.reasonTitle":      "Причина отмены?",
    "admin.orders.unavailable.placeholder": "Какая позиция / магазин / почему?",
    "admin.orders.unavailable.reasonTitle": "Причина статуса «нет в наличии»?",
    "admin.orders.received.ok":             "Отмечен как получен — добавьте фото для клиента.",
    "admin.orders.purchased.prompt":        "Сколько из {qty} вы купили? (0 = нет в наличии)",
    "admin.orders.purchased.error":         "Введите число от 0 до {qty}",
    "admin.orders.reasonLabel":             "Причина:",
    "admin.orders.receipt.title":           "Чеки и фото получения ({n})",
    "admin.orders.receipt.add":             "+ фото",
    "admin.orders.receipt.added":           "Добавлено фото: {n}",
    "admin.orders.receipt.uploading":       "…",

    // ─── Admin: Applications tab ────────────────────────────────
    "admin.apps.filter.submitted": "Ждут проверки",
    "admin.apps.filter.pending":   "Ещё не отправили",
    "admin.apps.filter.approved":  "Одобренные",
    "admin.apps.filter.rejected":  "Отклонённые",
    "admin.apps.refresh":          "Обновить",
    "admin.apps.count.submitted":  "ждут решения",
    "admin.apps.count.other":      "в этой выборке",
    "admin.apps.empty":            "Очередь пуста.",
    "admin.apps.submittedAt":      "Отправлено {when}",
    "admin.apps.verificationCode": "код подтверждения",
    "admin.apps.field.channel":    "Канал",
    "admin.apps.field.platform":   "Площадка",
    "admin.apps.field.audience":   "Аудитория",
    "admin.apps.field.categories": "Категории",
    "admin.apps.field.contact":    "Контакт",
    "admin.apps.field.verificationPost": "Пост с кодом",
    "admin.apps.rejectReason":     "Причина отказа:",
    "admin.apps.approve":          "Одобрить",
    "admin.apps.approved.ok":      "Одобрено",
    "admin.apps.reject":           "Отклонить…",
    "admin.apps.reject.confirm":   "Подтвердить отказ",
    "admin.apps.reject.cancel":    "Отмена",
    "admin.apps.reject.placeholder": "Причина отказа (обязательно)",
    "admin.apps.reject.required":  "Нужна причина отказа",
    "admin.apps.reject.ok":        "Отклонено",

    // ─── Admin: Subscriptions tab ───────────────────────────────
    "admin.subs.hint":            "Внутренние заявки на подписку. Одобряйте после получения платежа вне приложения; отказ позволяет пользователю подать заявку заново. Одобрение автоматически продлевает срок premium и устанавливает тариф.",
    "admin.subs.empty":           "Заявок на подписку пока нет.",
    "admin.subs.period.annual":   "годовая",
    "admin.subs.period.monthly":  "месячная",
    "admin.subs.adminPrefix":     "Админ:",
    "admin.subs.filed":           "Создано {when}",
    "admin.subs.decided":         " · решение {when}",
    "admin.subs.approve":         "Одобрить",
    "admin.subs.reject":          "Отклонить",
    "admin.subs.approve.confirm": "Одобрить {tier} {period} для «{user}»?",
    "admin.subs.approve.placeholder": "Заметка администратора (например, ID транзакции)",
    "admin.subs.approve.ok":      "Одобрено",
    "admin.subs.reject.confirm":  "Отклонить заявку? Причина будет видна пользователю.",
    "admin.subs.reject.placeholder": "Причина (например, платёж не получен)",
    "admin.subs.reject.ok":       "Отклонено",

    // ─── Admin: Shipments tab ───────────────────────────────────
    "admin.ships.filter.all":     "Все статусы",
    "admin.ships.refresh":        "Обновить",
    "admin.ships.refreshing":     "Загружаю…",
    "admin.ships.empty":          "Под этот фильтр посылок нет.",
    "admin.ships.deletedItem":    "(удалено)",
    "admin.ships.itemsCount":     "{n} позиц(ий):",
    "admin.ships.itemQty":        "кол-во",
    "admin.ships.customs.over":   "⚠ Таможня:",
    "admin.ships.customs.normal": "К декларации:",
    "admin.ships.printPickList":  "🖨 Печать пик-листа / упаковочного листа",
    "admin.ships.tracking.carrier":  "Перевозчик",
    "admin.ships.tracking.number":   "Трек-номер",
    "admin.ships.tracking.weight":   "кг",
    "admin.ships.tracking.url":      "URL отслеживания (опционально)",
    "admin.ships.tracking.save":     "Сохранить трек",
    "admin.ships.tracking.saving":   "Сохраняю…",
    "admin.ships.tracking.saved":    "Трек сохранён",
    "admin.ships.tracking.required": "Сначала укажите трек-номер",
    "admin.ships.markShipped":       "Отметить «отправлено»",
    "admin.ships.markDelivered":     "Отметить «доставлено»",
    "admin.ships.markShipped.confirm":   "Отметить посылку #{id} как отправленную? Все позиции ({n}) перейдут в статус shipped.",
    "admin.ships.markShipped.ok":        "Отправлена",
    "admin.ships.markDelivered.confirm": "Отметить посылку #{id} как доставленную?",
    "admin.ships.markDelivered.ok":      "Доставлена",

    // ─── Admin: Settings (Payments / FX) ────────────────────────
    "admin.fx.hint":              "Курс применяется к ценам в USD для отображения в RUB на карточках и сохраняется в новых заказах. Старые заказы остаются на своём курсе — только новые используют новый.",
    "admin.fx.title":             "Курс USD → RUB",
    "admin.fx.base":              "Базовый курс (forex)",
    "admin.fx.markup":            "Наценка %",
    "admin.fx.effective":         "Эффективный курс",
    "admin.fx.currentSaved":      "Сейчас сохранено: база {base} + {markup}% = ",
    "admin.fx.save":              "Сохранить",
    "admin.fx.saving":            "Сохраняю…",
    "admin.fx.saved":             "Курс обновлён",
    "admin.fx.error.base":        "База должна быть положительной",
    "admin.fx.error.markup":      "Наценка должна быть ≥ 0",
    "admin.fx.auto.title":        "ЦБ авто-обновление",
    "admin.fx.auto.on":           "Вкл — каждый час",
    "admin.fx.auto.off":          "Выкл — на паузе",
    "admin.fx.auto.enabled":      "Авто-обновление включено",
    "admin.fx.auto.disabled":     "Авто-обновление отключено",
    "admin.fx.auto.lastFetch":    "Последний запрос",
    "admin.fx.auto.status":       "Статус",
    "admin.fx.auto.status.ok":    "OK",
    "admin.fx.auto.status.fail":  "Сбой — {err}",
    "admin.fx.auto.status.unknown": "неизвестно",
    "admin.fx.auto.cbrRate":      "Курс ЦБ",
    "admin.fx.auto.cbrRate.value": "1$ = {rate}₽ (до наценки)",
    "admin.fx.auto.lastChange":   "Последнее изменение",
    "admin.fx.auto.noChange":     "изменений ещё не было",
    "admin.fx.auto.source":       "Источник: cbr-xml-daily.ru · публикуется около 12:30 МСК",
    "admin.fx.auto.refreshNow":   "Обновить сейчас",
    "admin.fx.auto.fetching":     "Запрашиваю…",
    "admin.fx.auto.cbrToast":     "ЦБ: 1$ = {rate}₽",
    "admin.fx.auto.cbrFailed":    "Не удалось получить курс ЦБ: {err}",
    "admin.fx.auto.never":        "никогда",
    "admin.fx.auto.relSec":       "{n} с назад",
    "admin.fx.auto.relMin":       "{n} мин назад",
    "admin.fx.auto.relHour":      "{n} ч назад",
    "admin.fx.auto.relDay":       "{n} д назад",
    "admin.pm.title":             "Способы оплаты",
    "admin.pm.hint":              "Показываются клиентам при оплате подтверждённого заказа. Оставьте поле пустым, чтобы скрыть способ. Номера и кошельки НЕ пишутся в журнал аудита — только имена полей и их длины.",
    "admin.pm.loading":           "Загружаю…",
    "admin.pm.usdt.title":        "USDT (несколько сетей)",
    "admin.pm.usdc.title":        "USDC (несколько сетей)",
    "admin.pm.sbp.title":         "СБП (быстрые платежи)",
    "admin.pm.sbp.phone":         "Телефон",
    "admin.pm.sbp.name":          "Имя получателя",
    "admin.pm.card.title":        "Перевод на карту",
    "admin.pm.card.number":       "Номер карты",
    "admin.pm.card.bank":         "Банк",
    "admin.pm.card.holder":       "Держатель карты",
    "admin.pm.instructions.title": "Инструкции (под списком способов)",
    "admin.pm.instructions.ru":   "Русский",
    "admin.pm.instructions.en":   "Английский",
    "admin.pm.instructions.placeholderRu": "Можно платить в любой валюте…",
    "admin.pm.instructions.placeholderEn": "USDT preferred…",
    "admin.pm.save":              "Сохранить способы оплаты",
    "admin.pm.saving":            "Сохраняю…",
    "admin.pm.saved":             "Способы оплаты обновлены",
    "admin.pm.networkHint":       "одна строка на сеть · ярлыки сетей сохраняются в ВЕРХНЕМ регистре",
    "admin.pm.removeNetwork":     "Удалить эту запись",
    "admin.pm.addNetwork":        "+ Добавить сеть",
    "admin.pm.placeholder.networkAddress": "адрес кошелька в этой сети",

    // ─── Admin: Groups & Topics ────────────────────────────────
    "admin.groups.create":        "+ Создать новую группу",
    "admin.groups.members":       "{n} участник(ов)",
    "admin.groups.addTopic":      "+ Тема",
    "admin.groups.delete":        "Удалить",
    "admin.groups.edit":          "Изменить",
    "admin.groups.save":          "Сохранить",
    "admin.groups.cancel":        "Отмена",
    "admin.groups.saved":         "Сохранено",
    "admin.groups.namePlaceholder": "Название",
    "admin.groups.descPlaceholder": "Описание (необязательно)",
    "admin.groups.deleteConfirm":   "Удалить группу «{name}» вместе со всеми темами и сообщениями?",
    "admin.groups.error.nameRequired": "Название обязательно",
    "admin.groups.error.topicNameRequired": "Название темы обязательно",
    "admin.groups.topic.edit":     "Переименовать тему",
    "admin.groups.topic.delete":   "Удалить тему",
    "admin.groups.topic.saved":    "Тема переименована",
    "admin.groups.topic.deleteConfirm": "Удалить тему «{name}»?",

    // ─── Admin: Users ──────────────────────────────────────────
    "admin.users.role.admin":    "ADMIN",
    "admin.users.role.manager":  "MANAGER",
    "admin.users.role.premium":  "PREMIUM",
    "admin.users.role.banned":   "ЗАБАНЕН",
    "admin.users.you":           "(вы)",
    "admin.users.banReason":     "Причина:",
    "admin.users.role.customer.opt": "Клиент",
    "admin.users.role.manager.opt":  "Менеджер",
    "admin.users.role.admin.opt":    "Админ",
    "admin.users.premium.label":   "Premium…",
    "admin.users.premium.1mo":     "+ 1 месяц",
    "admin.users.premium.3mo":     "+ 3 месяца",
    "admin.users.premium.12mo":    "+ 12 месяцев",
    "admin.users.premium.revoke":  "Отозвать",
    "admin.users.premium.grant":   "Выдать {months} мес. Premium пользователю «{name}»?",
    "admin.users.premium.revoke.confirm": "Отозвать Premium у «{name}»?",
    "admin.users.unban":           "Разбанить",
    "admin.users.ban":             "Бан",
    "admin.users.ban.prompt":      "Забанить «{name}»? Необязательная причина:",
    "admin.users.ban.placeholder": "Причина (опционально)",
    "admin.users.premium.until":   "Premium до {date}",
    "admin.users.premium.free":    "Бесплатный тариф",

    // ─── Admin: Channels tab ───────────────────────────────────
    "admin.channels.hint":       "Оргсбор — ваша маржа поверх курса. Кешбэк клиенту платится из этой маржи при доставке заказа — например, сбор 20% + кешбэк 3% = чистыми 17%. Изменения применяются ТОЛЬКО к новым заказам; исторические заказы сохраняют свой snapshot.",
    "admin.channels.empty":      "Каналов пока нет.",
    "admin.channels.posts":      "{n} постов",
    "admin.channels.edit":       "Изменить",
    "admin.channels.close":      "Закрыть",
    "admin.channels.delete":     "Удалить",
    "admin.channels.deleting":   "Удаляю…",
    "admin.channels.editTitle.collapse": "Свернуть",
    "admin.channels.editTitle.expand":   "Изменить имя / описание / владельца",
    "admin.channels.deleteTitle": "Удалить канал (каскадом удалятся посты)",
    "admin.channels.name":       "Название",
    "admin.channels.name.placeholder": "Название канала",
    "admin.channels.description": "Описание",
    "admin.channels.description.placeholder": "Видно клиентам в списке каналов",
    "admin.channels.owner":      "Владелец (менеджер / админ)",
    "admin.channels.owner.current": "Текущий владелец (uid: {uid}…)",
    "admin.channels.fee":        "Оргсбор %",
    "admin.storeFees.title":     "Комиссия по магазину",
    "admin.storeFees.hint":      "Задайте комиссию сразу для всех каналов магазина. Отдельные каналы можно донастроить ниже.",
    "admin.storeFees.count":     "каналов: {n}",
    "admin.storeFees.mixed":     "разные значения",
    "admin.storeFees.apply":     "Применить",
    "admin.storeFees.applied":   "Комиссия применена к {n} каналам ({store})",
    "admin.channels.cashback":   "Кешбэк клиенту %",
    "admin.channels.save":       "Сохранить изменения",
    "admin.channels.saving":     "Сохраняю…",
    "admin.channels.saved":      "Сохранено",
    "admin.channels.savedShort": "Сохранено",
    "admin.channels.netMargin": "Чистая маржа:",
    "admin.channels.netNegative": " — кешбэк больше сбора, вы будете терять деньги на каждом заказе",
    "admin.channels.error.fee":    "Сбор должен быть 0..100",
    "admin.channels.error.cashback": "Кешбэк должен быть 0..100",
    "admin.channels.error.name":   "Название минимум 2 символа",
    "admin.channels.delete.confirm": "Удалить канал «{name}»?\n\nЭто каскадом удалит ВСЕ его посты ({n} опубликовано).\nВведите название канала для подтверждения.",
    "admin.channels.delete.mismatch": "Название не совпало — ничего не удалено.",
    "admin.channels.delete.ok":     "Канал «{name}» удалён",

    // ─── Admin: WhatsApp tab ───────────────────────────────────
    "admin.wa.hint":              "Пайплайн приёма WhatsApp. Номер каждого представителя магазина можно сопоставить с каналом — входящие изображения товаров + подписи автоматически разбираются (цена, размеры) и становятся черновиком поста в этом канале. Включите «авто-публикацию», когда станете доверять разбору этого отправителя.",
    "admin.wa.configured":        "Настроенные отправители",
    "admin.wa.configured.empty":  "Маршрутов нет — выберите отправителя ниже или добавьте вручную.",
    "admin.wa.recent":            "Недавние отправители (за 30 дней)",
    "admin.wa.recent.empty":      "Сообщений ещё не было. Отправьте WhatsApp на свой бизнес-номер, чтобы начать.",
    "admin.wa.msgsLast":          "{n} сообщений · последнее: \"{text}\"",
    "admin.wa.routed":            "✓ маршрут есть",
    "admin.wa.addRoute":          "+ Маршрут",
    "admin.wa.addRoute.prompt":   "Отображаемое имя для {phone}?",
    "admin.wa.addRoute.placeholder": "например, Магазин Karl Lagerfeld",
    "admin.wa.addRoute.ok":       "Маршрут добавлен — выберите канал ниже",
    "admin.wa.route.displayName": "Отображаемое имя",
    "admin.wa.route.channelDefault": "(канал по умолчанию)",
    "admin.wa.route.autopublish":  "Авто-публикация (только при высокой уверенности)",
    "admin.wa.route.delete":      "Удалить",
    "admin.wa.route.delete.confirm": "Удалить маршрут для {phone}?",
    "admin.wa.route.deleted":     "Удалено",
    "admin.wa.route.save":        "Сохранить",
    "admin.wa.route.saving":      "…",
    "admin.wa.route.saved":       "Сохранено",

    // ─── Admin: Tokens tab ─────────────────────────────────────
    "admin.tokens.newToken":      "Новый токен (скопируйте сейчас — больше не покажем)",
    "admin.tokens.copy":          "Скопировать",
    "admin.tokens.dismiss":       "Скрыть",
    "admin.tokens.create":        "Создать токен",
    "admin.tokens.name":          "Имя",
    "admin.tokens.name.placeholder": "shopper-bot-prod",
    "admin.tokens.scopes":        "Скоупы (через запятую)",
    "admin.tokens.scopes.placeholder": "posts.write",
    "admin.tokens.generate":      "Сгенерировать",
    "admin.tokens.active":        "Активные токены",
    "admin.tokens.active.empty":  "Нет.",
    "admin.tokens.createdAt":     "создан {date}",
    "admin.tokens.lastUsedAt":    " · последний раз использован {date}",
    "admin.tokens.revoke":        "Отозвать",
    "admin.tokens.revoke.confirm": "Отозвать «{name}»? Действие необратимо.",
    "admin.tokens.revoked.section": "Отозванные ({n})",
    "admin.tokens.revoked.row":     "{name} · отозван {date}",

    // ─── Admin: Redemptions tab ────────────────────────────────
    "admin.reds.empty":           "Ожидающих выводов нет.",
    "admin.reds.requested":       "запрошен {when}",
    "admin.reds.sendTo":          "Отправить на:",
    "admin.reds.fulfill":         "Выполнить",
    "admin.reds.fulfill.confirm": "Отметить {amount} как выполненный для {email}?",
    "admin.reds.fulfill.notePrompt": "Заметка о выполнении (опционально)",
    "admin.reds.fulfill.placeholder": "код карты, трек-номер и т.п.",
    "admin.reds.cancel":          "Отменить",
    "admin.reds.cancel.title":    "Причина отмены?",
    "admin.reds.cancel.placeholder": "Причина отмены",

    // ─── Admin: DM Templates tab ───────────────────────────────
    "admin.templates.hint.prefix":     "Многоразовые DM-сообщения. Используйте ",
    "admin.templates.hint.suffix":     " как плейсхолдеры — они подставляются при выборе шаблона из чата. Менеджеры могут пользоваться шаблонами; создавать и менять — только админы.",
    "admin.templates.new":             "+ Новый шаблон",
    "admin.templates.empty":           "Шаблонов пока нет. Создайте первый.",
    "admin.templates.delete":          "Удалить",
    "admin.templates.create.namePrompt": "Имя шаблона?",
    "admin.templates.create.namePlaceholder": "Прибыл на склад",
    "admin.templates.create.bodyPrompt":   "Тело сообщения (русский)?",
    "admin.templates.create.bodyPlaceholder": "Привет, {customer_name}! Заказ прибыл.",
    "admin.templates.create.ok":       "Шаблон создан",
    "admin.templates.delete.confirm":  "Удалить шаблон «{name}»?",
    "admin.templates.delete.ok":       "Удалён",

    // ─── Admin: Audit tab ──────────────────────────────────────
    "admin.audit.action.all":     "Все действия",
    "admin.audit.target.all":     "Все типы целей",
    "admin.audit.reload":         "Обновить",
    "admin.audit.reloading":      "Загружаю…",
    "admin.audit.empty":          "Под текущие фильтры записей нет.",
    "admin.audit.system":         "(система)",
    "admin.audit.req":            "req: {id}",

    // ─── Admin: Curated (staff) ────────────────────────────────
    "admin.curated.title":            "Очередь закупок",
    "admin.curated.settings":         "Настройки",
    "admin.curated.filter.open":      "Открытые (по умолч.)",
    "admin.curated.filter.submitted": "Отправлено",
    "admin.curated.filter.priced":    "Цена согласована",
    "admin.curated.filter.purchased": "Куплено",
    "admin.curated.filter.invoiced":  "Выставлен счёт",
    "admin.curated.filter.paid":      "Оплачено",
    "admin.curated.filter.completed": "Завершено",
    "admin.curated.filter.cancelled": "Отменено",
    "admin.curated.filter.rejected":  "Отклонено",
    "admin.curated.refresh":          "Обновить",
    "admin.curated.count":            "заявок: {n}",
    "admin.curated.loading":          "Загружаю…",
    "admin.curated.empty":            "Очередь пуста.",
    "admin.curated.row.items":        "{n} позиций · заявлено {stated}",
    "admin.curated.row.final":        " · итог {final}",
    "admin.curated.row.account":      "🔐 аккаунт",
    "admin.curated.row.links":        "🔗 ссылки",
    "admin.curated.back":             "← Назад в очередь",
    "admin.curated.created":          "создано {when}",
    "admin.curated.facts.type":       "Тип",
    "admin.curated.facts.site":       "Сайт",
    "admin.curated.facts.recipient":  "Получатель",
    "admin.curated.facts.address":    "Адрес",
    "admin.curated.facts.items":      "Позиции",
    "admin.curated.facts.stated":     "Заявленная сумма",
    "admin.curated.facts.final":      "Итоговая сумма",
    "admin.curated.facts.currency":   "Желаемая валюта",
    "admin.curated.facts.notes":      "Комментарий резеллера",
    "admin.curated.facts.links":      "Ссылки",
    "admin.curated.creds.title":      "🔐 Данные аккаунта",
    "admin.curated.creds.purged":     "Удалены.",
    "admin.curated.creds.none":       "Не предоставлены.",
    "admin.curated.creds.login":      "Логин:",
    "admin.curated.creds.password":   "Пароль:",
    "admin.curated.creds.audited":    "Просмотр запротоколирован. Не пересылайте в чат.",
    "admin.curated.creds.reveal":     "Показать (записывается в аудит)",
    "admin.curated.invoice.waiting":  "Ожидается оплата — {amount} {currency}",
    "admin.curated.invoice.expires":  "Истекает {when}",
    "admin.curated.invoice.markPaid": "Отметить как оплачено",
    "admin.curated.invoice.markPaid.ok": "Отмечено как оплачено",
    "admin.curated.complete":         "Завершить и удалить данные",
    "admin.curated.complete.confirm": "Завершить и удалить данные доступа?",
    "admin.curated.complete.ok":      "Завершено",
    "admin.curated.reject.label":     "Причина отказа (обязательно, видно резеллеру)",
    "admin.curated.reject.placeholder": "Причина…",
    "admin.curated.reject.confirm":   "Подтвердить отказ",
    "admin.curated.reject.cancel":    "Отмена",
    "admin.curated.reject.start":     "Отклонить…",
    "admin.curated.reject.required":  "Нужна причина",
    "admin.curated.reject.ok":        "Отклонено",
    "admin.curated.price.title":      "Установить итоговую сумму (USD)",
    "admin.curated.price.notes":      "Заметка для резеллера (опционально)",
    "admin.curated.price.notes.placeholder": "напр., доставка вышла дороже, чем ожидалось",
    "admin.curated.price.save":       "Сохранить цену",
    "admin.curated.price.invalid":    "Неверная сумма",
    "admin.curated.price.ok":         "Цена согласована",
    "admin.curated.purchased.title":  "Отметить как купленное в магазине",
    "admin.curated.purchased.receipt": "Чек:",
    "admin.curated.purchased.uploaded": "Чек загружен",
    "admin.curated.purchased.mark":   "Отметить как куплено",
    "admin.curated.purchased.ok":     "Отмечено: куплено",
    "admin.curated.invoice.title":    "Выставить счёт",
    "admin.curated.invoice.reissue":  "Пере-выставить счёт (срок истёк)",
    "admin.curated.invoice.currency": "Валюта",
    "admin.curated.invoice.wallet":   "Выбрать кошелёк (из админ-настроек)",
    "admin.curated.invoice.wallet.placeholder": "— выберите кошелёк —",
    "admin.curated.invoice.target.rub": "Ссылка на оплату (одноразовая, 20 мин)",
    "admin.curated.invoice.target.crypto": "Адрес кошелька",
    "admin.curated.invoice.placeholder.rub": "https://…",
    "admin.curated.invoice.placeholder.crypto": "0x… или T…",
    "admin.curated.invoice.preview":  "Резеллер увидит: ",
    "admin.curated.invoice.previewExpires": " · истекает через 20 мин",
    "admin.curated.invoice.send":     "Отправить счёт",
    "admin.curated.invoice.resend":   "Пере-выставить счёт",
    "admin.curated.invoice.required": "Нужна цель оплаты",
    "admin.curated.invoice.ok":       "Счёт выставлен",
    "admin.curated.invoice.reissued": "Пере-выставлен",
    "admin.curated.settings.back":    "← Назад в очередь",
    "admin.curated.settings.title":   "Настройки закупок",
    "admin.curated.settings.fxTitle": "КУРС RUB / USD ДЛЯ ЗАКУПОК",
    "admin.curated.settings.cbrBase": "Курс ЦБ (база)",
    "admin.curated.settings.markup": "Наценка %",
    "admin.curated.settings.effective": "Эффективный курс",
    "admin.curated.settings.refreshCbr": "Обновить курс ЦБ",
    "admin.curated.settings.refreshing": "Обновляем…",
    "admin.curated.settings.refreshedToast": "Курс ЦБ обновлён",
    "admin.curated.settings.updatedAt": "Обновлён {when}",
    "admin.curated.settings.fxHint": "Резеллер видит эффективный курс при оформлении счёта в рублях. Базовый курс тянется из того же ЦБ-источника, что и розничный FX, и обновляется автоматически раз в час.",
    "admin.curated.settings.payInstructions": "Инструкция по оплате для резеллера (опционально)",
    "admin.curated.settings.wallets": "Крипто-кошельки",
    "admin.curated.settings.addWallet": "+ Добавить кошелёк",
    "admin.curated.settings.save":    "Сохранить",
    "admin.curated.settings.saved":   "Сохранено",
    "admin.curated.settings.adminOnly": "Только для админа",
    "admin.curated.settings.wallet.address": "адрес",
    "admin.curated.settings.wallet.label": "метка",

    // ─── Reseller-side CuratedPanel ────────────────────────────
    "admin.curated.reseller.title":       "Закупки",
    "admin.curated.reseller.subtitle":    "Поручите BuyerGram сделать закупку за вас — оплата после получения счёта в USDT, USDC или рублях.",
    "admin.curated.reseller.new":         "+ Новая закупка",
    "admin.curated.reseller.empty":       "У вас пока нет закупок. Создайте первую — это занимает 2 минуты.",
    "admin.curated.reseller.loading":     "Загружаю…",
    "admin.curated.reseller.row.account": "Доступ к аккаунту",
    "admin.curated.reseller.row.links":   "Список ссылок",
    "admin.curated.reseller.row.items.one":  "товар",
    "admin.curated.reseller.row.items.few":  "товара",
    "admin.curated.reseller.row.items.many": "товаров",
    "admin.curated.reseller.status.submitted": "Отправлено",
    "admin.curated.reseller.status.priced":    "Цена согласована",
    "admin.curated.reseller.status.purchased": "Куплено",
    "admin.curated.reseller.status.invoiced":  "Ожидаем оплату",
    "admin.curated.reseller.status.paid":      "Оплачено",
    "admin.curated.reseller.status.completed": "Завершено",
    "admin.curated.reseller.status.cancelled": "Отменено",
    "admin.curated.reseller.status.rejected":  "Отклонено",
    "admin.curated.reseller.back":        "← Назад к списку",
    "admin.curated.reseller.new.title":   "Новая закупка",
    "admin.curated.reseller.mode.label":  "Способ передачи деталей",
    "admin.curated.reseller.mode.links.title": "Список ссылок",
    "admin.curated.reseller.mode.links.desc":  "Я пришлю ссылки на товары — BuyerGram добавит в корзину и купит.",
    "admin.curated.reseller.mode.account.title": "Доступ к аккаунту",
    "admin.curated.reseller.mode.account.desc":  "Корзина уже собрана — я даю логин/пароль, остаётся только оплатить.",
    "admin.curated.reseller.account.site": "Сайт магазина",
    "admin.curated.reseller.account.login": "Логин",
    "admin.curated.reseller.account.password": "Пароль",
    "admin.curated.reseller.account.hint": "Логин и пароль хранятся в зашифрованном виде и удаляются автоматически после завершения или отмены заявки. Если на сайте включена двухфакторная — мы напишем вам в момент покупки.",
    "admin.curated.reseller.links.label": "Ссылки на товары",
    "admin.curated.reseller.links.qty":   "кол-во",
    "admin.curated.reseller.links.add":   "+ Добавить ссылку",
    "admin.curated.reseller.delivery.name": "Имя получателя (ФИО)",
    "admin.curated.reseller.delivery.name.placeholder": "Иванов Иван Иванович",
    "admin.curated.reseller.delivery.address": "Адрес доставки",
    "admin.curated.reseller.delivery.address.placeholder": "Город, улица, дом, кв., индекс, телефон",
    "admin.curated.reseller.itemCount": "Количество товаров",
    "admin.curated.reseller.statedTotal": "Ожидаемая сумма (USD)",
    "admin.curated.reseller.currency": "Способ оплаты счёта",
    "admin.curated.reseller.currency.rub": "Рубли (по курсу BuyerGram)",
    "admin.curated.reseller.notes": "Комментарий (опционально)",
    "admin.curated.reseller.notes.placeholder": "Размер, цвет, нюансы…",
    "admin.curated.reseller.submit": "Отправить заявку",
    "admin.curated.reseller.submitting": "Отправляю…",
    "admin.curated.reseller.cancel": "Отмена",
    "admin.curated.reseller.error.amount": "Укажите ожидаемую сумму в USD",
    "admin.curated.reseller.error.noLinks": "Добавьте хотя бы одну ссылку",
    "admin.curated.reseller.created.ok": "Заявка создана",
    "admin.curated.reseller.detail.expired": "Срок действия ссылки истёк",
    "admin.curated.reseller.detail.expiredHint": "Напишите менеджеру в чат — он выпустит новую ссылку.",
    "admin.curated.reseller.detail.paid": "Оплата получена",
    "admin.curated.reseller.detail.paidHint": "Закупка обрабатывается.",
    "admin.curated.reseller.field.type": "Тип заявки",
    "admin.curated.reseller.field.site": "Сайт",
    "admin.curated.reseller.field.products": "Товары",
    "admin.curated.reseller.field.recipient": "Получатель",
    "admin.curated.reseller.field.address": "Адрес",
    "admin.curated.reseller.field.itemCount": "Кол-во товаров",
    "admin.curated.reseller.field.statedTotal": "Заявленная сумма",
    "admin.curated.reseller.field.finalTotal": "Итоговая сумма",
    "admin.curated.reseller.field.currency": "Валюта счёта",
    "admin.curated.reseller.field.notes": "Ваш комментарий",
    "admin.curated.reseller.field.managerNotes": "Заметка менеджера",
    "admin.curated.reseller.field.rejectionReason": "Причина отказа",
    "admin.curated.reseller.field.receipt": "Чек о покупке",
    "admin.curated.reseller.receipt.open": "Открыть",
    "admin.curated.reseller.cancelBtn": "Отменить заявку",
    "admin.curated.reseller.cancel.confirm": "Отменить заявку? Это действие нельзя отменить.",
    "admin.curated.reseller.cancel.ok": "Заявка отменена",
    "admin.curated.reseller.invoice.title": "Счёт к оплате",
    "admin.curated.reseller.invoice.minutesLeft": "Осталось ~{n} мин на оплату",
    "admin.curated.reseller.invoice.cryptoAddress": "Адрес для перевода ({currency}):",
    "admin.curated.reseller.invoice.payCta": "Перейти к оплате →",
    "admin.curated.reseller.invoice.refresh": "Обновить статус",

    // ─── ApplicationModal (reseller) ───────────────────────────
    "admin.application.title":      "Подтверждение канала продаж",
    "admin.application.loading":    "Загружаем…",
    "admin.application.copy":       "Копировать",
    "admin.application.copied":     "Скопировано ✓",
    "admin.application.step1.label": "Шаг 1 · Код подтверждения",
    "admin.application.step1.hint": "Опубликуйте этот код в вашем основном канале продаж (Telegram, MAX, Instagram, VK или собственный сайт) — это подтверждает, что канал ваш. Пост можно удалить сразу после одобрения заявки.",
    "admin.application.step2.label": "Шаг 2 · Ссылка на канал",
    "admin.application.platform":   "Площадка",
    "admin.application.platform.vk": "ВКонтакте",
    "admin.application.platform.ok": "Одноклассники",
    "admin.application.platform.website": "Собственный сайт",
    "admin.application.platform.other": "Другое",
    "admin.application.audience":   "Размер аудитории",
    "admin.application.audience.lt100":  "Меньше 100",
    "admin.application.audience.100_500": "100–500",
    "admin.application.audience.500_2k": "500–2 000",
    "admin.application.audience.2k_10k": "2 000–10 000",
    "admin.application.audience.10kp":   "Больше 10 000",
    "admin.application.categories": "Категории товаров",
    "admin.application.category.women.shoes": "Женская обувь",
    "admin.application.category.men.shoes":   "Мужская обувь",
    "admin.application.category.women.clothing": "Женская одежда",
    "admin.application.category.men.clothing":   "Мужская одежда",
    "admin.application.category.kids":        "Детская одежда / обувь",
    "admin.application.category.beauty":      "Косметика и уход",
    "admin.application.category.electronics": "Электроника",
    "admin.application.category.home":        "Дом и кухня",
    "admin.application.category.bags":        "Сумки и аксессуары",
    "admin.application.category.other":       "Другое",
    "admin.application.contact":    "Контакт для связи (Telegram-хэндл или email)",
    "admin.application.step3.label": "Шаг 3 · Ссылка на пост с кодом",
    "admin.application.step3.hint": "Пост должен быть публичным, чтобы администратор мог открыть его и увидеть код. Если канал закрытый — отправьте код в DM @buyergram_support и приложите скриншот.",
    "admin.application.submit":     "Отправить заявку",
    "admin.application.submitting": "Отправляем…",
    "admin.application.submitted.title": "Заявка на проверке",
    "admin.application.submitted.body":  "Обычно проверяем в течение 12 часов в рабочее время. Как только одобрим, цены и пересылка станут доступны автоматически.",
    "admin.application.submitted.sent":  "Что вы отправили",
    "admin.application.rejected.title": "Заявка отклонена",
    "admin.application.rejected.noReason": "Без указания причины. Свяжитесь с поддержкой.",
    "admin.application.rejected.newCode": "Мы выпустили новый код подтверждения. Опубликуйте его в канале и отправьте новую ссылку на пост.",
    "admin.application.field.channel": "Канал",
    "admin.application.field.audience": "Аудитория",
    "admin.application.field.categories": "Категории",
    "admin.application.field.contact": "Контакт",
    "admin.application.field.verificationPost": "Пост с кодом",

    // ─── Dashboard (reseller home) ─────────────────────────────
    "dashboard.menu":              "Меню",
    "dashboard.kicker":            "Главная",
    "dashboard.loading":           "Загрузка…",
    "dashboard.loadError":         "Не удалось загрузить дашборд: {err}",
    "dashboard.subtitle":          "За последние 7 дней — что происходит у вас в магазине.",
    "dashboard.greeting.night":    "Доброй ночи",
    "dashboard.greeting.morning":  "Доброе утро",
    "dashboard.greeting.day":      "Добрый день",
    "dashboard.greeting.evening":  "Добрый вечер",
    "dashboard.attention.events": "непрочитанных событий",
    "dashboard.attention.cta":     "Открыть →",
    "dashboard.kpi.forwards":      "Пересылок",
    "dashboard.kpi.delivered":     "Доставлено",
    "dashboard.kpi.orders":        "Заказов",
    "dashboard.kpi.hint.noForwards":  "Пока ничего не пересылали",
    "dashboard.kpi.hint.forwardsOk":  "{n} успешных доставок",
    "dashboard.kpi.hint.deliveriesOk":"Карточек дошло до подписчиков",
    "dashboard.kpi.hint.noDeliveries":"Целей ещё нет",
    "dashboard.kpi.hint.ordersOk":    "Размещено вашим магазином",
    "dashboard.kpi.hint.noOrders":    "Заказы ещё не размещали",
    "dashboard.section.pending":   "Заказы в работе",
    "dashboard.section.pending.allOrders": "Все заказы",
    "dashboard.section.pending.empty":     "Все заказы доставлены или ещё не размещены.",
    "dashboard.section.pending.browseCta": "Открыть каталог",
    "dashboard.section.reengage":  "К кому стоит обратиться",
    "dashboard.section.reengage.allCustomers": "Все клиенты",
    "dashboard.section.activity":  "Активность по клиентам",
    "dashboard.section.activity.empty":     "Привяжите заказ к клиенту в карточке заказа — здесь появится последняя активность по каждому.",
    "dashboard.section.activity.openCustomers": "Открыть Клиентов",
    "dashboard.emptyState.title": "Запустите свой магазин за 5 минут.",
    "dashboard.emptyState.body":  "Настройте курс и канал для пересылки, потом выбирайте товары из каталога и отправляйте подписчикам.",
    "dashboard.emptyState.setup":  "Настроить пересылку",
    "dashboard.emptyState.browse": "Открыть каталог",
    "dashboard.row.untitled":     "(без названия)",
    "dashboard.row.openCta":      "Открыть →",
    "dashboard.row.ago":          "назад",
    "dashboard.row.now":          "только что",
    "dashboard.status.placed":    "размещён",
    "dashboard.status.confirmed": "подтверждён",
    "dashboard.status.paid":      "оплачен",
    "dashboard.status.purchased": "куплен",
    "dashboard.status.received":  "на складе",
    "dashboard.status.shipped":   "отправлен",
    "dashboard.status.delivered": "доставлен",
    "dashboard.status.cancelled": "отменён",
    "dashboard.status.unavailable": "недоступен",
    "dashboard.unit.min":  "мин",
    "dashboard.unit.hour": "ч",
    "dashboard.unit.day":  "д",
    "dashboard.unit.mo":   "мес",

    // ─── MyStore (reseller business surface) ───────────────────
    "mystore.menu":              "Меню",
    "mystore.heading":           "Мой магазин",
    "mystore.adminHeading":      "Admin",
    "mystore.tab.home":          "Главная",
    "mystore.tab.customers":     "Клиенты",
    "mystore.tab.forward":       "Пересылка",
    "mystore.tab.curated":       "Закупки",
    "mystore.tab.stats":         "Аналитика",
    "mystore.section.customers": "Клиенты",
    "mystore.section.forward":   "Пересылка",
    "mystore.section.stats":     "Аналитика",

    // ─── Forward modal ─────────────────────────────────────────
    "forward.title": "Переслать {n}",
    "forward.cards.one":  "карточку",
    "forward.cards.few":  "карточки",
    "forward.cards.many": "карточек",
    "forward.allFilteredNote": "Все карточки текущего фильтра",
    "forward.loadingSettings": "Загрузка настроек…",
    "forward.hint.noFx.title":   "Сначала укажите курс $ → ₽",
    "forward.hint.noFx.body":    "Без курса мы не можем посчитать ₽-цену для подписи. Откройте настройки пересылки и задайте курс, по которому хотите видеть рубли у себя в канале.",
    "forward.hint.openSettings": "Открыть настройки",
    "forward.hint.noTarget.title": "Добавьте канал для пересылки",
    "forward.hint.noTarget.body":  "Привяжите Telegram-канал (или несколько), куда мы будем отправлять выбранные карточки. Бот должен быть админом в этом канале.",
    "forward.hint.allDisabled.title": "Все каналы выключены",
    "forward.hint.allDisabled.body":  "У вас есть каналы в настройках, но ни один не отмечен галочкой как активный. Включите хотя бы один, чтобы продолжить.",
    "forward.hint.setup": "Настроить",
    "forward.preview.label":   "Что увидят подписчики (1-я карточка)",
    "forward.preview.noImage": "нет картинки",
    "forward.preview.emptyCaption": "(подпись пустая — настройте шаблон)",
    "forward.preview.rate":      "Курс: ₽{rate} за $1",
    "forward.preview.batchSize": " · всего карточек: {n}",
    "forward.target.label":     "Куда отправить",
    "forward.target.kind.tg":   "Telegram",
    "forward.target.kind.max":  "MAX",
    "forward.target.soon":      "скоро",
    "forward.cancel":     "Отмена",
    "forward.send":       "Отправить {n}",
    "forward.sending":    "Отправка…",
    "forward.result.failed":   "Ничего не отправилось",
    "forward.result.allOk":    "Все {n} отправок успешны",
    "forward.result.partial":  "{ok} из {total} отправок успешны",
    "forward.result.done":     "Готово",
    "forward.result.notSent":  "Не отправлено ({n})",
    "forward.result.andMore":  "…и ещё {n}",

    // ─── Forward history ───────────────────────────────────────
    "forward.history.title":   "История пересылок",
    "forward.history.subtitle":"Нажмите на товар, чтобы открыть исходную страницу — там можно оформить заказ",
    "forward.history.tab.items":   "Товары",
    "forward.history.tab.batches": "Отправки",
    "forward.history.search.placeholder": "Поиск по бренду, модели, SKU",
    "forward.history.loading": "Загрузка…",
    "forward.history.empty":   "Вы ещё ничего не пересылали. Выберите карточки в канале и нажмите «Переслать выбранные» — пересланные товары появятся здесь.",
    "forward.history.foundOf": "{n} из {total} товаров",
    "forward.history.count.one":  "товар",
    "forward.history.count.few":  "товара",
    "forward.history.count.many": "товаров",
    "forward.history.noMatch":  "Ничего не найдено по запросу «{q}».",
    "forward.history.toSource": "К источнику ↗",
    "forward.history.sendsCount": "{n} отправок",
    "forward.history.dash":       "—",
    "forward.history.unitMin":  "мин назад",
    "forward.history.unitHour": "ч назад",
    "forward.history.unitDay":  "дн назад",
    "forward.history.justNow":  "только что",
    "forward.history.batchAllSent": "Все {n} отправлены",
    "forward.history.batchPartial": "{ok} из {total} отправлены",
    "forward.history.postMissing":  "пост удалён или недоступен",

    // ─── Notifications ─────────────────────────────────────────
    "notifications.title": "События",
    "notifications.push.title": "Включить push?",
    "notifications.push.body":  "Браузер будет показывать уведомления, даже когда BG закрыт.",
    "notifications.push.enable":"Включить",
    "notifications.feed.label": "Лента уведомлений",
    "notifications.markAll":    "Отметить все",
    "notifications.loading":    "Загрузка…",
    "notifications.empty.title": "Пока тихо.",
    "notifications.empty.body":  "Как только заказ обновится, поступит кешбэк или клиент подключится к боту — появится здесь.",
    "notifications.loadMore":    "Загрузить ещё",
    "notifications.loadingMore": "Загрузка…",
    "notifications.bucket.today": "Сегодня",
    "notifications.bucket.week":  "На этой неделе",
    "notifications.bucket.older": "Раньше",
    "notifications.row.unit.min":  "мин",
    "notifications.row.unit.hour": "ч",
    "notifications.type.order": "Заказ {status}",
    "notifications.type.credit": "Начислено ${amt}",
    "notifications.type.credit.referral":  "Реферальный бонус",
    "notifications.type.credit.cashback":  "Кешбэк за заказ",
    "notifications.type.customerOrdered":  "Новый заказ от {name}",
    "notifications.type.paymentRequested": "{name} просит QR/ссылку для оплаты",
    "notifications.type.paymentUploaded":  "{name} загрузил(а) оплату — проверьте",
    "notifications.type.customerOrdered.unknown": "клиента",
    "notifications.type.forward":  "Пересылка · {n} {noun}",
    "notifications.type.forward.cards.one":  "карточка",
    "notifications.type.forward.cards.few":  "карточек",
    "notifications.type.forward.cards.many": "карточек",
    "notifications.type.forward.ok":   "{n} доставлено",
    "notifications.type.forward.none": "ни одной не доставлено",
    "notifications.type.forward.failed": " · {n} с ошибкой",
    "notifications.type.reminder":  "Напомнить о {count} клиент{suffix}",
    "notifications.type.reminder.suffix.one":  "е",
    "notifications.type.reminder.suffix.many": "ах",
    "notifications.type.reminder.andMore":  " и ещё…",
    "notifications.type.botLinked":  "{name} подключился к боту",
    "notifications.type.botLinked.unknown": "Клиент",
    "notifications.type.botLinked.body":   "Теперь сообщения летят напрямую",
    "notifications.type.reengage":  "{n} клиентов ждут вашего внимания",
    "notifications.status.confirmed":   "подтверждён",
    "notifications.status.received":    "у нас на складе",
    "notifications.status.shipped":     "отправлен",
    "notifications.status.delivered":   "доставлен",
    "notifications.status.cancelled":   "отменён",
    "notifications.status.unavailable": "закончился",

    // ─── Onboarding wizard ─────────────────────────────────────
    "onboarding.step.welcome": "Знакомство",
    "onboarding.step.fx":      "Курс",
    "onboarding.step.target":  "Канал",
    "onboarding.step.done":    "Готово",
    "onboarding.stepIndicator": "Шаг {idx} из {total} · {label}",
    "onboarding.loading":    "Загрузка…",
    "onboarding.skip":       "Пропустить",
    "onboarding.back":       "Назад",
    "onboarding.saving":     "Сохраняем…",
    "onboarding.finish":     "Открыть каталог",
    "onboarding.next":       "Далее",
    "onboarding.welcome.title": "BuyerGram — ваш магазин на 100%.",
    "onboarding.welcome.body":  "Мы готовим карточки товаров из США. Вы выбираете нужные и пересылаете подписчикам в Telegram под своим брендом — без наших логотипов и водяных знаков.",
    "onboarding.welcome.foot":  "За пару минут настроим: курс рубля, ваш канал и — поехали.",
    "onboarding.fx.title":     "По какому курсу показываем рубли?",
    "onboarding.fx.body":      "Все карточки в каталоге в долларах. Мы пересчитаем цену в рубли в подписи, которую вы отправите подписчикам. Поставьте курс, который выгоден для вашей маржи.",
    "onboarding.fx.unit":      "₽ / $1",
    "onboarding.fx.example":   "Пример: товар $49.99 = ₽{rub}",
    "onboarding.fx.unusualLow":  "Курс ниже обычного диапазона (60–200 ₽/$). Перепроверьте.",
    "onboarding.fx.unusualHigh": "Курс выше обычного диапазона (60–200 ₽/$). Перепроверьте.",
    "onboarding.fx.err.positive":  "Курс должен быть больше нуля",
    "onboarding.target.title": "Куда отправляем карточки?",
    "onboarding.target.body":  "Укажите ваш канал — добавьте @Buyergrambot админом канала с правом публикации, потом вставьте chat_id или @ник канала. Бот не получит доступ к сообщениям подписчиков.",
    "onboarding.target.platform": "Платформа",
    "onboarding.target.kind.tg":  "Telegram",
    "onboarding.target.kind.max": "MAX",
    "onboarding.target.chatId":   "chat_id или @ник канала",
    "onboarding.target.chatId.placeholder.tg":  "@my_shop  или  -1001234567890",
    "onboarding.target.chatId.placeholder.max": "chat-id MAX-канала",
    "onboarding.target.label":    "Название (необязательно)",
    "onboarding.target.label.placeholder": "Например: Мой основной канал",
    "onboarding.target.err.empty":    "Укажите chat_id или @ник канала",
    "onboarding.target.err.format":   "Это не похоже на @ник канала или chat_id. Проверьте формат: @my_shop или -1001234567890.",
    "onboarding.done.title":  "Готово.",
    "onboarding.done.body":   "Магазин настроен. Откройте каталог в боковом меню, отметьте карточки галочками и нажмите «Отправить» — мы отправим их в ваш канал с пересчитанной ценой.",
    "onboarding.done.foot":   "Заметили опечатку? Все настройки можно поменять в вкладке «Пересылка» вашего магазина.",

    // ─── Code lookup ───────────────────────────────────────────
    "code.lookup.title":    "Найти по коду",
    "code.lookup.hint":     "Введите код, который вам сообщил клиент. Найдём карточку в любом канале.",
    "code.lookup.placeholder": "например, 7F3K9PXY",
    "code.lookup.find":     "Найти",
    "code.lookup.finding":  "…",
    "code.lookup.err.format":   "Код должен быть из 4–12 символов (без 0, 1, I, O).",
    "code.lookup.err.notFound": "Не найдено. Проверьте код и попробуйте снова.",
    "code.lookup.err.generic":  "Ошибка",
    "code.lookup.err.noChannel":"В карточке нет канала",
    "code.lookup.source":   "Источник ↗",
    "code.lookup.sourcePremium": "Источник в Premium ↗",
    "code.lookup.jump":     "Перейти к каналу",

    // ─── modals.jsx (Group / Channel / Topic creators) ─────────
    "modals.group.title":     "Новая группа",
    "modals.group.name":      "Название",
    "modals.group.name.placeholder": "напр. Команда инженеров",
    "modals.group.desc":      "Описание",
    "modals.group.desc.placeholder": "О чём эта группа?",
    "modals.cancel":          "Отмена",
    "modals.group.create":    "Создать",
    "modals.topic.title":     "Новая тема",
    "modals.topic.name":      "Название темы",
    "modals.topic.name.placeholder": "напр. Объявления",
    "modals.topic.add":       "Добавить",
    "modals.channel.title":   "Новый канал",
    "modals.channel.name":    "Название",
    "modals.channel.name.placeholder": "Walmart Deals",
    "modals.channel.slug":    "Slug (URL)",
    "modals.channel.slug.placeholder": "walmart-deals",
    "modals.channel.desc":    "Описание",
    "modals.channel.desc.placeholder": "Необязательно — видят клиенты",
    "modals.channel.fee":     "Оргсбор, %",
    "modals.channel.fee.placeholder": "напр. 15",
    "modals.channel.cashback":         "Кэшбэк клиенту, % (из маржи оргсбора)",
    "modals.channel.cashback.placeholder": "напр. 3",
    "modals.channel.create":  "Создать",
    "modals.channel.creating":"Создаю…",

    // ─── PostModal (admin/manager) ─────────────────────────────
    "post.modal.title":     "Новый пост",
    "post.modal.images":    "Изображения",
    "post.modal.hint.touch": "плюс — добавить с камеры",
    "post.modal.hint.desktop": "⌘V вставить · перетащите из WhatsApp",
    "post.modal.uploading": "Загружаю…",
    "post.modal.add":       "Добавить",
    "post.modal.photo":     "Фото",
    "post.modal.channel":   "Канал",
    "post.modal.title.field":  "Заголовок",
    "post.modal.title.placeholder": "напр. DKNY атласное платье",
    "post.modal.desc":      "Описание (RU)",
    "post.modal.desc.placeholder": "Русское описание поста",
    "post.modal.price":     "Цена ($)",
    "post.modal.origPrice": "Старая цена ($)",
    "post.modal.brand":     "Бренд",
    "post.modal.brand.placeholder": "DKNY",
    "post.modal.category":  "Категория",
    "post.modal.category.placeholder": "apparel",
    "post.modal.sourceUrl": "Ссылка на источник",
    "post.modal.gb.tag":    "СП",
    "post.modal.gb.label":  " — собирать заказы до цели или дедлайна, затем СТОП",
    "post.modal.gb.target": "Цель (опц.)",
    "post.modal.gb.target.placeholder": "напр. 30",
    "post.modal.gb.deadline":           "Дедлайн (опц.)",
    "post.modal.publish":   "Опубликовать сразу",
    "post.modal.publishHint": " (только админы — менеджеры создают черновики)",
    "post.modal.cancel":    "Отмена",
    "post.modal.create":    "Создать пост",
    "post.modal.creating":  "Создаю…",

    // ─── Customer detail (CustomerDetailModal) ─────────────────
    "customer.detail.tab.contact": "Контакты",
    "customer.detail.tab.history": "История",
    "customer.detail.tab.notes":   "Заметки",
    "customer.detail.contacts":    "Контакты",
    "customer.detail.tg.label":    "Telegram:",
    "customer.detail.tg.none":     "Telegram не указан",
    "customer.detail.phone.label": "Телефон:",
    "customer.detail.phone.none":  "Телефон не указан",
    "customer.detail.inBaseSince": "В вашей базе с {date}",
    "customer.detail.stats.orders":  "Заказов",
    "customer.detail.stats.spent":   "На сумму",
    "customer.detail.stats.last":    "Последний",
    "customer.detail.notes":         "Заметки",
    "customer.detail.notes.edit":    "Изменить",
    "customer.detail.notes.placeholder": "Размер одежды, любимые бренды, дни рождения — всё, что поможет обслужить клиента лучше.",
    "customer.detail.notes.help":    "{n}/2000 · Esc — отмена · ⌘↵ — сохранить",
    "customer.detail.notes.cancel":  "Отмена",
    "customer.detail.notes.save":    "Сохранить",
    "customer.detail.notes.saving":  "Сохраняем…",
    "customer.detail.notes.clickToEdit": "Нажмите, чтобы изменить",
    "customer.detail.notes.add":     "+ Добавить заметку",
    "customer.detail.notes.savedToast": "Заметка сохранена",
    "customer.detail.reminders":     "Напоминания",
    "customer.detail.reminders.edit":"Изменить",
    "customer.detail.reminders.add": "+ Напомнить",
    "customer.detail.reminder.in":   "Напомнить через",
    "customer.detail.reminder.days": "дней",
    "customer.detail.reminder.note.placeholder": "Заметка (необязательно)",
    "customer.detail.reminder.cancel": "Отмена",
    "customer.detail.reminder.create": "Создать",
    "customer.detail.reminder.saving": "Сохраняем…",
    "customer.detail.reminders.empty": "Активных напоминаний нет.",
    "customer.detail.reminder.overdue":"Просрочено ",
    "customer.detail.reminder.done":   "Готово",
    "customer.detail.reminder.delete": "Удалить",
    "customer.detail.reminder.createdToast": "Напоминание создано",
    "customer.detail.history":       "История заказов",
    "customer.detail.history.loading": "Загрузка…",
    "customer.detail.history.empty":   "Заказов пока нет. Когда оформите заказ для этого клиента, он появится здесь.",
    "customer.detail.delete":     "Удалить",
    "customer.detail.close":      "Закрыть",
    "customer.detail.edit":       "Изменить",
    "customer.detail.reorderUnavailable": "Этот товар больше недоступен",
    "customer.detail.bot.connected":  "Подключён к боту · с {date}",
    "customer.detail.bot.invite":     "Пригласить в бот",
    "customer.detail.bot.loading":    "…",
    "customer.detail.bot.inviteHint": "Ссылка действует 7 дней. Каждое нажатие создаёт новую.",
    "customer.detail.bot.notConfigured": "Customer-bot ещё не настроен на платформе.",
    "customer.detail.bot.linkCopied":    "Ссылка скопирована — отправьте клиенту.",
    "customer.detail.bot.linkError":     "Не удалось создать ссылку",
    "customer.detail.order.untitled":    "(без названия)",
    "customer.detail.order.dash":        "—",
    "customer.detail.reorder":           "Снова",
    "customer.detail.reorder.loading":   "…",

    // ─── Bulk message modal (in SettingsModal) ─────────────────
    "bulk.title":   "Написать клиентам",
    "bulk.template": "Шаблон сообщения",
    "bulk.template.placeholder": "Привет, {имя}! ...",
    "bulk.template.default":     "Привет, {имя}! Новые поступления в моём канале — загляни.",
    "bulk.template.vars":        "Подставится: {var1} — имя клиента · {var2} — Telegram-ник без @",
    "bulk.summary.bot":          "через бот · одной кнопкой",
    "bulk.summary.bot.sentOf":   "({sent}/{total} отправлено)",
    "bulk.summary.manual":       "по t.me-ссылкам · вручную из списка ниже",
    "bulk.summary.skip":         "без Telegram-ника пропустим",
    "bulk.sending":              "Отправляем…",
    "bulk.sent":                 "Отправлено · {n}",
    "bulk.sendViaBot":           "Отправить через бот · {n}",
    "bulk.openFirst5":           "Открыть первые 5 t.me",
    "bulk.close":                "Закрыть",
    "bulk.list":                 "Список получателей по t.me-ссылкам",
    "bulk.open":                 "Открыть",
    "bulk.copy":                 "Копия",
    "bulk.copy.title":           "Скопировать текст",
    "bulk.copied":               "Скопировано",
    "bulk.sentToast":            "Отправлено через бот: {n}",
    "bulk.failedToast":          "Не доставлено: {n}",
    "bulk.errorToast":           "Не удалось отправить",
    "bulk.openSomeToast":        "Открыли первые 5 из {n}. Остальные — по одному ниже.",

    // ─── OrderModal extras ─────────────────────────────────────
    "order.modal.forCustomer":  "Для какого клиента",
    "order.modal.reorderTag":   " · повторный заказ",
    "order.modal.forMe":        "— Для меня —",
    "order.modal.priceWas":     "было ${amt}",
    "order.modal.priceNow":     " · сейчас ",
    "order.modal.phonePlaceholder": "+7 ...",
    "order.modal.countryDefault":   "Россия",

    // ─── OrdersModal ───────────────────────────────────────────
    "orders.modal.filter.all":   "Все",
    "orders.modal.empty":        "Заказов пока нет.",
    "orders.modal.loading":      "Загрузка…",

    // ─── ChannelFeed extras ────────────────────────────────────
    "channelfeed.menu":          "Меню",
    "channelfeed.search.placeholder": "Поиск по бренду, модели, SKU",
    "channelfeed.search.clear":  "Очистить поиск",
    "channelfeed.filter.all":    "Все",
    "channelfeed.sort.newest":   "Новые",
    "channelfeed.sort.priceAsc": "Цена ↑",
    "channelfeed.sort.priceDesc":"Цена ↓",
    "channelfeed.sort.discount": "Скидка",
    "channelfeed.view.list":     "Список",
    "channelfeed.view.grid":     "Плитка",
    "channelfeed.select":        "Выбрать",
    "channelfeed.selectAll":     "Выбрать всё",
    "channelfeed.deselectAll":   "Снять выделение",
    "channelfeed.forwardSelected":  "Переслать выбранные",
    "channelfeed.forwardFiltered":  "Переслать все",
    "channelfeed.forwardFilteredN": "Переслать все {n} по фильтру",
    "channelfeed.cancelSelect":   "Отмена",
    "channelfeed.selectedCount":  "Выбрано: {n}",
    "channelfeed.selectedShort":  "{n} выбрано",
    "channelfeed.empty.staff":    "Постов в этом канале ещё нет. Создайте первый.",
    "channelfeed.loadMore":       "Загрузить ещё",
    "channelfeed.close":          "Закрыть",
    "channelfeed.newPost":        "Пост",
    "channelfeed.newPost.title":  "Новый пост",
    "channelfeed.history":        "История",
    "channelfeed.history.title":  "История пересылок и ссылки на источники",
    "channelfeed.findByCode":     "Найти по коду",
    "channelfeed.findByCode.title": "Найти карточку по коду, который сообщил клиент",
    "channelfeed.forward.toggle.title": "Включить выбор карточек для пересылки",
    "channelfeed.forward.locked.title": "Доступно после подтверждения канала",
    "channelfeed.forward.locked.label": "🔒 Пересылка",
    "channelfeed.forward.toggle.on":  "Отменить выбор",
    "channelfeed.forward.toggle.off": "Выбрать для пересылки",
    "channelfeed.heroTitle":      "Подборка\nдня",
    "channelfeed.closingSoon.one":  "{n} СП закрывается в ближайшие сутки — успей подключиться",
    "channelfeed.closingSoon.many": "{n} СП закрываются в ближайшие сутки — успей подключиться",
    "channelfeed.filterBar.brand":     "Бренд",
    "channelfeed.filterBar.brand.count": "Бренд · {n}",
    "channelfeed.filterBar.brand.empty":  "Нет брендов",
    "channelfeed.filterBar.priceMin":  "мин",
    "channelfeed.filterBar.priceMax":  "макс",
    "channelfeed.filterBar.anyDiscount":  "Любая скидка",
    "channelfeed.filterBar.fromPct":   "от {n}%",
    "channelfeed.filterBar.sort":      "Сортировка",
    "channelfeed.filterBar.sort.newest":   "Новые сверху",
    "channelfeed.filterBar.sort.discountDesc": "Скидка ↓",
    "channelfeed.filterBar.sort.priceAsc":  "Цена ↑",
    "channelfeed.filterBar.sort.priceDesc": "Цена ↓",
    "channelfeed.filterBar.sort.brand":  "Бренд А-Я",
    "channelfeed.filterBar.resultOf":  "{n} из {total}",
    "channelfeed.filterBar.clear":     "Сбросить",
    "channelfeed.noMatch.title":      "Ничего не подходит под выбранные фильтры",
    "channelfeed.noMatch.clear":      "Сбросить фильтры",
    "channelfeed.priceLocked":        "Цена скрыта",
    "channelfeed.priceLocked.full":   "🔒 Цена скрыта",
    "channelfeed.priceLocked.body":   "Завершите подтверждение канала, чтобы видеть цены и пересылать карточки клиентам.",
    "channelfeed.code.copyTitle":     "Нажмите, чтобы скопировать",
    "channelfeed.code.copied":        "Код скопирован",
    "channelfeed.premiumSource":      "Источник в Premium ↗",
    "channelfeed.premiumSource.title": "Открыть Premium",
    "channelfeed.toast.forwardSent":  "Пересылка отправлена",
    "channelfeed.gb.outOf":           " из {n}",
    "channelfeed.gb.remainDays":      "{d}д {h}ч",
    "channelfeed.gb.remainHours":     "{h}ч {m}м",
    "channelfeed.gb.remainMin":       "{m}м",
    "channelfeed.gb.gbLabel":         "СП",
    "channelfeed.gb.targetLabel":     "СП · ЦЕЛЬ",
    "channelfeed.age.justNow":        "только что",
    "channelfeed.age.min":            "{n} мин назад",
    "channelfeed.age.hour":           "{n} ч назад",
    "channelfeed.age.day":            "{n} дн назад",

    // ─── App.jsx misc ──────────────────────────────────────────
    "app.loading":           "Загрузка…",
    "app.sidebar.home":      "Главная",
    "app.sidebar.store":     "Магазин",
    "app.sidebar.store.title": "Мой магазин",
    "app.fab.code":          "Найти по коду",
    "app.fab.forwards":      "Пересылки",
    "app.fab.history":       "История",
    "app.fab.newChat":       "Начать переписку",
    "app.fab.newGroup":      "Создать группу",
    "app.fab.newChannel":    "Создать канал",
    "app.fab.newTopic":      "Создать тему",
    "app.notif.bell":        "Уведомления",
    "app.notif.bell.title":  "События",
    "app.balance.title":     "Кешбэк-баланс",
    "app.help.title":        "Помощь",
    "app.settings.title":    "Настройки",
    "app.admin.title":       "Админ",
    "app.logout.title":      "Выйти",
    "app.attach.title":      "Прикрепить изображение",
    "app.attach.uploading":  "Загружаю…",
    "app.attach.imageName":  "Изображение",
    "app.send.title":        "Отправить",
    "app.message.placeholder": "Сообщение",
    "app.message.captionPlaceholder": "Подпись к изображению (необязательно)",
    "app.message.edited":    "(изменено)",
    "app.message.editingCancel": "Отмена",
    "app.message.editingSave": "Сохранить",
    "app.message.menu.edit":   "Изменить",
    "app.message.menu.delete": "Удалить",
    "app.message.menu.reply":  "Ответить",
    "app.message.menu.copy":   "Копировать текст",
    "app.message.menu.pin":    "Закрепить",
    "app.message.menu.unpin":  "Открепить",
    "app.message.copied":      "Текст скопирован",
    "app.message.attachment":  "Вложение",
    "app.message.pinned":      "Закреплённое сообщение",
    "app.message.replyingTo":  "Ответ {name}",
    "app.reaction.more":       "Ещё эмодзи",
    "app.compose.emoji":       "Эмодзи",
    "app.chat.scrollDown":     "Вниз",
    "app.voice.record":        "Записать голосовое",
    "app.voice.recording":     "Идёт запись…",
    "app.voice.send":          "Отправить",
    "app.voice.micDenied":     "Нет доступа к микрофону",
    "app.message.deleteConfirm": "Удалить сообщение? Это нельзя отменить.",
    "app.chatSearch.title":   "Поиск по чату",
    "app.chatSearch.placeholder": "Поиск по сообщениям…",
    "app.chatSearch.searching": "Ищу…",
    "app.chatSearch.empty":   "Ничего не найдено",
    "app.chatSub.dm":         "personal message",
    "app.chatTitle.unknown":  "Неизвестно",
    "app.empty.dm.title":     "Поздоровайтесь",
    "app.empty.dm.body":      "Напишите первое сообщение, чтобы открыть разговор.",
    "app.empty.topic.title":  "Пока тихо",
    "app.empty.topic.body":   "Будьте первым, кто напишет в эту тему.",
    "app.empty.staff.dmsCustomer": "Клиенты напишут через «Хочу это» под постом. Можно и самим начать чат.",
    "app.empty.staff.dmsCta":  "Начать чат",
    "app.newDm.title":        "Новое сообщение",
    "app.newDm.empty.customer": "Менеджеров пока нет. Загляни позже.",
    "app.newDm.empty.staff":  "Пока нет других пользователей. Клиенты появятся здесь, когда зарегистрируются.",
    "app.adminPanel.title":   "Админ-панель",
    "app.quickAdd.title":     "Быстро добавить из магазина",
    "quickAdd.store":         "Магазин",
    "quickAdd.register.spec": "Спокойный описательный тон: «60 таблеток по 1000 мкг»",
    "quickAdd.register.editorial": "Редакторский тон: материалы, силуэт, ключевая деталь",
    "quickAdd.photos":        "Фото",
    "quickAdd.camera":        "Камера",
    "quickAdd.library":       "Галерея",
    "quickAdd.photos.max":    "Максимум 4 фото",
    "quickAdd.uploading":     "Загружаю…",
    "quickAdd.upload.failed": "Ошибка загрузки: {err}",
    "quickAdd.brand":         "Бренд",
    "quickAdd.title":         "Модель / название",
    "quickAdd.priceUsd":      "Цена $",
    "quickAdd.msrpUsd":       "Розница $",
    "quickAdd.itemNumber":    "Артикул Costco",
    "quickAdd.itemNumber.hint": "Со ценника. Нужен, чтобы цена обновлялась по купонам автоматически.",
    "quickAdd.upc":           "Штрихкод (UPC)",
    "quickAdd.upc.hint":      "Штрихкод товара. Для поиска сканированием и дедупликации.",
    "quickAdd.scan":          "Сканировать штрихкод",
    "scan.hint":              "Наведите камеру на штрихкод",
    "scan.cancel":            "Отмена",
    "scan.close":             "Закрыть",
    "scan.starting":          "Запуск камеры…",
    "scanfind.button":        "Найти сканированием",
    "scanfind.found":         "Найдено: {n}",
    "scanfind.none":          "Товар ещё не добавлен",
    "scanfind.resultFound":   "{code} · найдено: {n}",
    "scanfind.resultNone":    "{code} · не найдено",
    "scanfind.clear":         "Сбросить",
    "scanfind.dupTitle":      "Уже в каталоге",
    "scanfind.dupHint":       "Обновите цену во вкладке «Цены», а не добавляйте заново.",
    "review.assignGender":    "Указать пол:",
    "review.mens":            "Мужское",
    "review.womens":          "Женское",
    "review.kids":            "Детское",
    "review.moved":           "Перемещено",
    "edit.title":             "Редактировать товар",
    "edit.channel":           "Канал",
    "edit.source":            "Описание Costco (англ.)",
    "edit.source.placeholder": "Вставьте описание товара с Costco для перевода",
    "edit.translate":         "Перевести",
    "edit.translating":       "Перевод…",
    "edit.save":              "Сохранить",
    "edit.saving":            "Сохранение…",
    "edit.saved":             "Сохранено",
    "edit.cancel":            "Отмена",
    "edit.descriptionEs":     "Описание (ES)",
    "edit.validThrough":      "Цена действует до",
    "edit.savePublish":       "Сохранить и опубликовать",
    "edit.published":         "Опубликовано",
    "edit.photos":            "Фотографии",
    "card.saleEnds":          "Действует до {date}",
    "scan.filled.item":       "Артикул заполнен: {code}",
    "scan.filled.barcode":    "Штрихкод заполнен: {code}",
    "scan.err.denied":        "Доступ к камере запрещён. Разрешите камеру в настройках.",
    "scan.err.nocamera":      "Камера не найдена на этом устройстве.",
    "scan.err.inuse":         "Камера занята другим приложением. Закройте его и повторите.",
    "scan.err.generic":       "Не удалось запустить камеру. Введите номер вручную.",
    "quickAdd.descriptionEn": "Описание (англ.) — источник",
    "quickAdd.descriptionEn.hint": "Английский сохраняется и автоматически переводится на RU + ES при сохранении.",
    "quickAdd.description":   "Описание (RU)",
    "quickAdd.description.placeholder": "Сгенерируйте через ИИ или напишите свой текст",
    "quickAdd.generate":      "Сгенерировать",
    "quickAdd.generating":    "Думаю…",
    "quickAdd.generate.needFields": "Введите бренд или модель",
    "quickAdd.generate.failed": "Не вышло сгенерировать: {err}",
    "quickAdd.notes":         "Заметка для себя",
    "quickAdd.notes.placeholder": "напр. tag .97, last-stock W234",
    "quickAdd.notes.hint":    "Не публикуется. Память для пересчёта или повторной поездки.",
    "quickAdd.cancel":        "Отмена",
    "quickAdd.publish":       "Опубликовать",
    "worklist.tab.add":       "Добавить",
    "worklist.tab.prices":    "Цены",
    "worklist.all":           "Все",
    "worklist.loading":       "Загрузка…",
    "worklist.empty":         "Пока нет складских товаров",
    "worklist.saving":        "Сохранение…",
    "worklist.saved":         "Сохранено: {n}",
    "worklist.savedPartial":  "Сохранено {ok}, ошибок {fail}",
    "worklist.saveN":         "Сохранить {n}",
    "worklist.noChanges":     "Нет изменений",
    "worklist.view.all":      "Все",
    "worklist.view.toFinish": "К доработке",
    "worklist.view.published":"Опубликованные",
    "worklist.draft":         "Черновик",
    "quickAdd.publishing":    "Публикую…",
    "quickAdd.published":     "Опубликовано",
    "quickAdd.publish.failed": "Ошибка публикации: {err}",
    "sidebar.quickAdd":       "Из магазина",
    "app.groups.available":   "Доступны для вступления",
    "app.groups.my":          "Мои группы",
    "app.groups.members":     "{n} участников",
    "app.groups.topics":      "{n} участников · {t} тем",
    "app.groups.join":        "Вступить",
    "app.groups.leave":       "Покинуть",
    "app.channels.posts.one":  "{n} пост",
    "app.channels.posts.few":  "{n} поста",
    "app.channels.posts.many": "{n} постов",
    "app.channels.cashback":   "{n}% кешбэк",
    "app.upload.failed":       "Загрузка не удалась: {err}",
    "app.tmpl.title":          "Шаблоны",
    "app.tmpl.short":          "Шабл.",
    "app.tmpl.heading":        "Шаблоны ответов",
    "app.tmpl.empty":          "Шаблонов пока нет — добавьте в админ‑панели.",
    "app.applicationBanner.title": "Заявка на проверке",
    "app.applicationBanner.body":  "Цены и пересылка станут доступны после одобрения.",
    "app.applicationBanner.open":  "Открыть",
    "app.applicationBanner.view":  "Посмотреть",
    "app.applicationBanner.openApp": "Открыть заявку",
    "app.applicationBanner.locked.title": "Подтвердите канал продаж",
    "app.applicationBanner.locked.body":  "Опубликуйте код в вашем основном канале — это единственный шаг, чтобы открыть доступ к ценам и пересылке.",
    "app.applicationBanner.locked.cta":   "Начать",
    "app.applicationBanner.submitted.title": "Заявка на проверке",
    "app.applicationBanner.submitted.body":  "Проверим в течение 12 часов. Цены и пересылка откроются автоматически.",
    "app.applicationBanner.rejected.title":  "Заявка отклонена — нужно исправить",
    "app.applicationBanner.rejected.body":   "Откройте заявку, посмотрите причину и отправьте новую.",
    "app.applicationBanner.pending.title":   "Подтвердите канал продаж",
    "app.applicationBanner.pending.body":    "Цены и пересылка скрыты до подтверждения. Это занимает несколько минут.",
  },

  en: {
    "welcome.title": "Welcome to BuyerGram",
    "welcome.customer": "Browse Channels for curated deals, or open Chats to message a manager about an order.",
    "welcome.staff": "Pick a chat or channel from the sidebar to get started.",
    "welcome.browseChannels": "Browse channels",

    "tab.chats": "Chats",
    "tab.channels": "Channels",
    "sidebar.group.brands":     "Brands",
    "sidebar.group.categories": "Categories",
    "channelfeed.gender.all":    "All",
    "channelfeed.gender.women":  "Women",
    "channelfeed.gender.men":    "Men",
    "channelfeed.gender.kids":   "Kids",
    "channelfeed.gender.unisex": "Unisex",
    "post.variants.title.one":  "{n} color available",
    "post.variants.title.few":  "{n} colors available",
    "post.variants.title.many": "{n} colors available",
    "post.variants.sizes":      "Sizes in stock:",
    "post.variants.soldOut":    "Sold out",
    "post.variants.lowStock.one":  "Only {n} left",
    "post.variants.lowStock.few":  "Only {n} left",
    "post.variants.lowStock.many": "Only {n} left",
    "sidebar.brand.empty":      "(empty)",
    "sidebar.brand.showEmpty":  "Show empty brands",
    "sidebar.brand.hideEmpty":  "Hide empty brands",
    "tab.groups": "Groups",
    "tab.admin": "Admin",
    "tab.search": "Search",
    "tab.search.noMatchDms": "No matching chats",

    "empty.channels.title": "No channels yet",
    "empty.channels.customer": "Channels are where we'll share curated product deals. New ones will appear here as they launch.",

    "empty.dms.title": "No conversations yet",
    "empty.dms.customer": "Message a manager to start an order, or tap “I want this” on any post in the Channels tab.",
    "empty.dms.cta.customer": "Message a manager",

    "empty.channelFeed.title": "No posts here yet",
    "empty.channelFeed.customer": "New deals will appear here as soon as they're posted. Subscribe in Settings to get notified.",

    "empty.dm.title": "Say hello",
    "empty.dm.body": "Send the first message to start the conversation.",
    "empty.topic.title": "No messages yet",
    "empty.topic.body": "Be the first to post in this topic.",

    "channel.subtitle.posts.one": "channel · {n} post",
    "channel.subtitle.posts.few": "channel · {n} posts",
    "channel.subtitle.posts.many": "channel · {n} posts",
    "post.iWantThis": "I want this →",
    "post.getCashbackLink": "Get cashback link",
    "post.directLink": "Direct link",

    "auth.signInTagline": "Sign in to continue",
    "auth.hero.login":      "Welcome back.",
    "auth.hero.register":   "Your personal US buyer.",
    "auth.hero.confirm":    "Confirm your email.",
    "auth.hero.forgot":     "Let's reset your password.",
    "auth.hero.reset":      "Choose a new password.",
    "auth.tagline.login":    "A curated catalog of US retail discounts for resellers — forward to your audience, we handle sourcing and fulfillment.",
    "auth.tagline.register": "BuyerGram isn't a store. It's a direct line to a person who buys items in the US and ships them to you.",
    "auth.tagline.confirm":  "We sent a code to your email. If you don't see it, check the spam folder.",
    "auth.tagline.forgot":   "Enter your email — we'll send a reset link.",
    "auth.tagline.reset":    "Password must be at least 12 characters.",
    "auth.field.fullName":          "Full name",
    "auth.field.fullName.ph":       "Ivan Ivanovich Ivanov",
    "auth.field.nickname":          "Nickname",
    "auth.field.nickname.ph":       "ivan_ivanov",
    "auth.field.phone":             "Phone",
    "auth.field.phone.ph":          "+1 555 1234567",
    "auth.field.confirmPassword":   "Confirm password",
    "auth.field.inviteCode":        "Invite code · optional",
    "auth.field.codeFromEmail":     "Code from email",
    "auth.field.newPassword":       "New password",
    "auth.codeSent":                "Code sent",
    "auth.error.passwordsDontMatch":     "Passwords don't match",
    "auth.error.passwordMismatch":       "Password and confirmation don't match.",
    "auth.error.phoneFormat":            "Enter the phone in international format, e.g. +15551234567.",
    "auth.error.pickNickname":           "Pick an available nickname.",
    "auth.error.acceptTerms":            "Please accept the Privacy Policy and Terms of Service.",
    "auth.info.forgotPlaceholder":       "This feature isn't wired up yet. Email r.dolgalev@gmail.com — I'll reset it manually within an hour.",
    "auth.info.resetPlaceholder":        "This feature isn't wired up yet. Use the link from your email or message a manager.",
    "auth.btn.justAMoment":      "Just a moment…",
    "auth.btn.confirm":          "Confirm",
    "auth.btn.sendLink":         "Send link",
    "auth.btn.sending":          "Sending…",
    "auth.btn.savePassword":     "Save password",
    "auth.btn.saving":           "Saving…",
    "auth.btn.forgotPassword":   "Forgot password?",
    "auth.back.toRegister":   "← Back to register",
    "auth.back.toLogin":      "← Back to sign in",
    "auth.terms.prefix":   "I have read and agree to the",
    "auth.terms.privacy": "Privacy Policy",
    "auth.terms.tos":     "Terms of Service",
    "auth.terms.and":     "and",
    "auth.registerTagline": "Create your account",
    "auth.confirmTagline": "Verify your email",
    "auth.displayName": "Display Name",
    "auth.displayName.placeholder": "Your name",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.inviteCode": "Invite Code (optional)",
    "auth.inviteCode.placeholder": "From a friend",
    "auth.confirmCode": "Confirmation Code",
    "auth.confirmCode.placeholder": "Enter 6-digit code",
    "auth.confirmCodeSent": "Confirmation code sent to",
    "auth.devCode": "Dev code:",
    "auth.confirmEmail": "Confirm Email",
    "auth.verifying": "Verifying...",
    "auth.back": "← Back",
    "auth.createAccount": "Create Account",
    "auth.signIn": "Sign In",
    "auth.pleaseWait": "Please wait...",
    "auth.alreadyHave": "Already have an account?",
    "auth.noAccount": "No account?",
    "auth.toggleSignIn": "Sign In",
    "auth.toggleRegister": "Register",
    "auth.captchaRequired": "Please complete the verification",

    "settings.title": "Settings",
    "settings.tab.profile": "Profile",
    "settings.tab.password": "Password",
    "settings.tab.subs": "Subscriptions",
    "settings.displayName": "Display name",
    "settings.email": "Email",
    "settings.language": "Language",
    "settings.save": "Save",
    "settings.avatar.hint":     "JPG / PNG / WebP — auto-cropped to a circle",
    "settings.avatar.upload":   "Upload photo",
    "settings.avatar.change":   "Change photo",
    "settings.avatar.remove":   "Remove",
    "settings.avatar.uploaded": "Photo updated",
    "settings.avatar.removed":  "Photo removed",
    "settings.saving": "Saving…",
    "settings.saved": "Saved",
    "settings.curPassword": "Current password",
    "settings.newPassword": "New password (12+ chars)",
    "settings.changePassword": "Change password",
    "settings.changing": "Changing…",
    "settings.passwordChanged": "Password changed",
    "settings.subs.help": "Control which channels push you Telegram notifications. The web app shows all channels regardless.",
    "settings.subs.empty": "No channels yet.",
    "settings.subs.tgPush": "TG push",

    "balance.title": "Cashback Balance",
    "balance.loading": "Loading…",
    "balance.available": "Available",
    "balance.minRedeem": "Min redemption: $5.00",
    "balance.redeem": "Redeem",
    "balance.cancel": "Cancel",
    "balance.requestRedemption": "Request redemption",
    "balance.amount": "Amount ($)",
    "balance.method": "Method",
    "balance.method.orderDiscount": "Apply to next managed order",
    "balance.method.amazonGift": "Amazon gift card (email)",
    "balance.method.walmartGift": "Walmart gift card (email)",
    "balance.sendGiftTo": "Send gift card to",
    "balance.submit": "Submit request",
    "balance.submitting": "Submitting…",
    "balance.referral.title": "Your referral link",
    "balance.referral.body": "Share your referral link. For every order your referee successfully receives, you earn 1% of the item subtotal (excluding BuyerGram's organizer fee) into your balance. It's your bonus — it doesn't reduce their cashback. Cashback is redeemable only for goods and services on BuyerGram.",
    "balance.usageHint": "Cashback can be spent only on goods and services within BuyerGram. External payouts and gift cards aren't supported.",

    // ─── Help / FAQ (EN mirror of the RU block) ──────────────────────
    "help.title":                "Help",
    "help.subtitle":             "Answers to common questions. Didn't find yours? Reach out.",
    "help.search":               "Search questions…",
    "help.empty":                "Nothing matched. Try different words or contact support.",
    "help.contact.title":        "Need a human?",
    "help.contact.body":         "If the answer isn't here — message us directly. It opens a chat with a BuyerGram team member.",
    "help.contact.cta":          "Contact support",
    "help.contact.opening":      "Opening chat…",
    "help.contact.fail":         "Couldn't open the chat. Try again later or email support@buyergram.app.",

    "help.cat.start":            "Getting started",
    "help.cat.catalog":          "Catalog & forwarding",
    "help.cat.orders":           "Orders & cashback",
    "help.cat.curated":          "Curated purchases",
    "help.cat.account":          "Account & access",
    "help.cat.trouble":          "Troubleshooting",

    "help.q.what":               "What is BuyerGram?",
    "help.a.what":                "BuyerGram is a closed B2B catalog for resellers. We aggregate items from major US retailers (Nordstrom Rack, Walmart, others), source them, and ship to Russia. You — the reseller — forward our product cards to your audience on Telegram/MAX/anywhere with your own markup; we handle the order and pay you cashback.",
    "help.q.start":              "Where do I start after approval?",
    "help.a.start":               "1) Open My Store → Forwarding settings and connect your Telegram or MAX channel. 2) Browse any catalog channel (e.g. Curated Shoes), pick a card and press Forward — it goes to your channel with your caption and markup. 3) When a customer wants to buy, place the order through My Store → Customers or have them contact you.",
    "help.q.commission":         "What does BuyerGram charge?",
    "help.a.commission":          "BuyerGram's organizer fee is added on top of the item price and shown as a separate line in the order. Your markup is whatever you set for your customer; we don't take a cut of it.",

    "help.q.forward":            "How do I forward a card?",
    "help.a.forward":             "Every card has a Forward button. The first time you use it you'll configure where it goes (a Telegram channel, MAX chat, or several targets). Before sending, you can edit the caption and check the preview — your customer sees your markup, no mention of BuyerGram.",
    "help.q.markup":              "How do I set my markup?",
    "help.a.markup":              "My Store → Forwarding settings: set your USD→RUB rate and a percentage markup. The base rate comes from the latest CBR update; you add your margin on top. The forward preview shows the final ruble price your customer will see.",
    "help.q.bgcode":              "What's the BG code on cards?",
    "help.a.bgcode":               "It's an internal BuyerGram product ID (e.g. BG-A4F2K9). Use it when a customer wants to clarify or reorder: the upstream retailer's URL is visible only on the Premium tier, but the BG code works everywhere.",
    "help.q.search":              "How do I find a product by code?",
    "help.a.search":               "Every channel has a search bar at the top. Enter the BG code or any part of the title — you'll find the card even if it's in another channel.",

    "help.q.order":               "How do I place an order for a customer?",
    "help.a.order":                "My Store → Customers → open the customer → New order, add items by BG code or link. We buy and ship; the status moves through paid → purchased → shipped → delivered.",
    "help.q.cashback":             "When do I get cashback?",
    "help.a.cashback":              "Cashback credits automatically when the order reaches Delivered. The rate is a percentage of the item subtotal (excluding BuyerGram's organizer fee). Visible in your Cashback balance.",
    "help.q.cashbackUse":          "What can I spend cashback on?",
    "help.a.cashbackUse":           "Only on future BuyerGram orders. There's no external payout or gift card route — it's a platform policy.",
    "help.q.referral":             "What's the referral link?",
    "help.a.referral":              "Share it with another reseller. When they register and get approved, every one of their orders that reaches Delivered credits you 1% of the item subtotal (excluding the organizer fee). It's your bonus — it doesn't reduce their cashback.",

    "help.q.curated":              "What are curated purchases?",
    "help.a.curated":               "It's a concierge service: we buy items on your behalf, even ones not in the catalog. You send links or give us account access at a retailer; we source it, invoice you in USDT/USDC/RUB, and deliver.",
    "help.q.curated.start":        "How do I open a curated request?",
    "help.a.curated.start":         "My Store → Curated → New request. Choose the type (product links or account access), give us the item count and estimated total, pick the invoice currency. We confirm the exact price, issue the invoice, source it, and ship.",
    "help.q.curated.status":       "What are the statuses?",
    "help.a.curated.status":        "Submitted → Priced (we confirmed the cost) → Invoiced → Paid → Purchased → Received → Shipped → Completed. You can cancel during the first two steps.",

    "help.q.application":          "Why are prices hidden?",
    "help.a.application":           "Pricing and the Forward button unlock after your application is approved. While the status is Submitted or Rejected, cards are visible but prices are masked. This protects already-approved resellers' margins.",
    "help.q.approve":               "How long does approval take?",
    "help.a.approve":                "Usually 1–2 business days. If your application is rejected you'll see the reason on the home banner and can resubmit with a new channel-ownership proof.",
    "help.q.lang":                  "How do I change the language?",
    "help.a.lang":                   "Settings → Profile → Language. Switching is instant; everything updates.",
    "help.q.nick":                  "How do I change my nickname?",
    "help.a.nick":                   "Settings → Profile → Nickname. Nicknames must be unique. Your legal name is visible only to you and the admin — other resellers and customers see only @nickname.",
    "help.q.logout":                 "How do I log out?",
    "help.a.logout":                  "The logout icon in the top-right of the sidebar. On iPhone the page reloads after logout — that's normal.",

    "help.q.trouble.notif":          "I'm not getting notifications.",
    "help.a.trouble.notif":           "Open Settings → Notifications and check that the push subscription is active. On iPhone, push works only if the PWA is added to the home screen. If the Telegram bot is silent, make sure you didn't send /stop.",
    "help.q.trouble.forward":        "I can't forward a card.",
    "help.a.trouble.forward":         "Make sure at least one channel is linked in Forwarding settings and that the BuyerGram bot has post permission. If the bot was removed, the button shows an empty state asking you to re-add it.",
    "help.q.trouble.pwa":            "Something is broken / white screen.",
    "help.a.trouble.pwa":             "Usually a stale cache on iPhone. Close the tab and reopen. If that doesn't work, iOS Settings → Safari → Clear history and website data. On macOS/Windows, Ctrl/Cmd+Shift+R for a hard reload.",
    "help.q.trouble.ru":             "The site is slow from Russia.",
    "help.a.trouble.ru":              "We host in Europe (OVH, France) and deliberately don't use Cloudflare — to avoid RKN-side throttling. If it's still slow it may be your local provider; alternatively install our PWA on the home screen, it runs faster.",

    "balance.copy": "Copy",
    "balance.copied": "Copied!",
    "balance.telegram.title": "Telegram delivery",
    "balance.telegram.linked": "Linked to",
    "balance.telegram.note": "You'll receive new posts via Telegram even when the web app is unreachable.",
    "balance.telegram.unlink": "Unlink",
    "balance.telegram.unlinkConfirm": "Unlink Telegram? You'll stop receiving messages from the bot.",
    "balance.telegram.openBot": "Open",
    "balance.telegram.thenSend": "in Telegram and send:",
    "balance.telegram.codeExpires": "Code expires in {n} minutes. Reopen this dialog to generate a new one.",
    "balance.telegram.unsetHelp": "Link your Telegram so you still receive new deals when the web app is throttled. Last-resort delivery channel for unreliable connections.",
    "balance.telegram.getCode": "Get linking code",
    "balance.pendingRedemptions": "Pending redemptions",
    "balance.requestedOn": "requested",
    "balance.recentLedger": "Recent ledger",
    "balance.ledger.empty": 'No activity yet. Click "Get cashback link" on any product post and complete a purchase — credit posts within 1-60 days of the sale being confirmed by the merchant.',
    "balance.recentTransactions": "Recent transactions",
    "balance.transactions.empty": "No transactions yet.",
    "balance.sale": "sale",

    "common.cancel": "Cancel",
    "common.ok": "OK",
    "common.loading": "Loading…",
    "common.confirm": "Confirm",

    // First-run intro — BuyerGram reseller flow. v2 rewrite.
    "intro.kicker": "From the editors",
    "intro.title": "Welcome to BuyerGram",
    "intro.lead": "You pick discounted US items and forward them to your channel — your customers see your ruble price with markup.",
    "intro.step1": "Set your own $→₽ rate. We auto-convert every card's price into rubles using it.",
    "intro.step2": "Add @Buyergrambot as an admin to your Telegram channel so it can post cards there.",
    "intro.step3": "Open any channel, pick the cards you like, and tap “Forward” — we'll send them to your Telegram automatically.",
    "intro.cta": "Finish setup",
    "intro.later": "Later",
    // Legacy key — kept so older clients that haven't reloaded the
    // bundle yet don't crash on a missing translation.
    "intro.gotIt": "Got it",

    // Setup checklist banner.
    "setup.banner.aria": "Finish BuyerGram setup",
    "setup.banner.both": "Finish setup to start forwarding cards.",
    "setup.banner.needFx": "One step left: set your $→₽ rate.",
    "setup.banner.needTarget": "One step left: add a Telegram channel to forward to.",
    "setup.banner.detail": "Without this, the Forward button can't deliver cards to your channel.",
    "setup.banner.cta": "Configure",

    "altcha.loading": "Preparing verification…",
    "altcha.solving": "Verifying browser…",
    "altcha.ready": "Verified",
    "altcha.error": "Verification failed",

    "post.order": "Order",
    "order.modal.title": "New order",
    "order.variant": "Variant (size/color/strength)",
    "order.variant.placeholder": "e.g. M, blue · 1000IU x60 · 256GB Black",
    "order.qty": "Quantity",
    "order.notes": "Notes",
    "order.notes.placeholder": "Special requests, shipping address, etc.",
    "order.total": "Total",
    "order.place": "Place order",
    "order.placing": "Placing…",
    "order.placed.ok": "Order placed. I'll be in touch shortly.",
    "cart.title": "Cart",
    "cart.add": "Add to cart",
    "cart.added": "Added to cart",
    "cart.remove": "Remove",
    "cart.each": "ea.",
    "cart.empty": "Cart is empty",
    "cart.emptyBody": "Your cart is empty. Open a channel and add items.",
    "cart.checkout": "Place order",
    "cart.subtotalN": "Items ({n})",
    "cart.fee": "Service fee",
    "cart.cashback": "Cashback",
    "cart.placed": "Order №{num} placed ({n} items)",
    "cart.placedSomeSkipped": "Order №{num}: {ok} placed, {skipped} skipped (unavailable)",
    "orders.group.items": "{n} items in order",
    "cart.rublePrice": "Price ₽",
    "cart.fxSuggested": "at {rate} ₽/$",
    "order.rublePrice.title": "Price for my customer (₽)",
    "order.rublePrice.label": "My price",
    "order.rublePrice.unset": "not set",
    "order.rublePrice.set": "set",
    "order.rublePrice.saved": "Price saved",
    "order.rublePrice.invalid": "Invalid price",
    "invoice.cta": "Invoice",
    "invoice.title": "Invoice",
    "invoice.from": "From",
    "invoice.to": "Customer",
    "invoice.item": "Item",
    "invoice.qty": "Qty",
    "invoice.price": "Price",
    "invoice.total": "Total",
    "invoice.copy": "Copy text",
    "invoice.pdf": "Download PDF",
    "invoice.copied": "Invoice copied",
    "invoice.copyFail": "Couldn't copy",
    "invoice.popupBlocked": "Allow pop-ups to save the PDF",
    "mystore.tab.batches": "Purchases",
    "admin.tab.batches": "Purchases",
    "batch.heading": "Purchase batches",
    "batch.new": "New batch",
    "batch.empty": "No batches yet. Create one to group orders from the same store.",
    "batch.unnamed": "Untitled",
    "batch.itemsN": "{n} items",
    "batch.retailer": "Retailer",
    "batch.retailer.ph": "e.g. Nordstrom Rack",
    "batch.supplierRef": "Supplier order #",
    "batch.supplierRef.ph": "e.g. sent for purchase",
    "batch.tariff": "Tariff ₽/kg",
    "batch.statusLabel": "Status",
    "batch.status.open": "Open",
    "batch.status.ordered": "Ordered",
    "batch.status.shipped": "Shipped",
    "batch.status.closed": "Closed",
    "batch.status.cancelled": "Cancelled",
    "batch.noMembers": "No items in this batch yet.",
    "batch.kg": "kg",
    "batch.goods": "Goods",
    "batch.delivery": "Delivery",
    "batch.grand": "Total",
    "batch.noCandidates": "No eligible orders to add.",
    "batch.addSelected": "Add ({n})",
    "batch.addOrders": "Add orders",
    "batch.delete": "Delete batch",
    "batch.delete.confirm": "Delete this batch? Orders stay, but leave the batch.",
    "orders.mine.title": "My orders",
    "orders.mine.empty": "No orders yet. Open a channel and tap \"Order\" under a product.",
    "orders.deletedPost": "(post deleted)",
    "order.status.placed":    "Placed",
    "order.status.confirmed": "Confirmed",
    "order.status.paid":      "Paid",
    "order.status.purchased": "Purchased",
    "order.status.received":  "At warehouse",
    "order.status.shipped":   "Shipped",
    "order.status.delivered": "Delivered",
    "order.status.cancelled": "Cancelled",
    "order.status.unavailable": "Out of stock",
    "order.fulfilled.of": "fulfilled {fulfilled} of {total}",
    "order.shipping.title": "Recipient & shipping address",
    "order.recipient.name": "Recipient name",
    "order.recipient.name.placeholder": "Who's receiving the package",
    "order.recipient.phone": "Recipient phone",
    "order.address": "Address",
    "order.address.placeholder": "Street, building, apt, city, postal code",
    "order.country": "Country",
    "order.recipient.required": "Recipient name is required",
    "order.address.required": "Shipping address is required",

    // Address book + picker (migration 0054)
    "order.address.source.label":    "Shipping address",
    "order.address.source.my":       "My address",
    "order.address.source.customer": "Customer's address",
    "order.address.source.manual":   "Enter manually",
    "order.address.empty.my":        "No saved addresses yet. Fill in the fields below — I'll offer to save it after the order is placed.",
    "order.address.empty.customer":  "This customer has no saved addresses yet.",
    "order.address.empty.noCustomer":"Pick a customer at the top of the form first.",
    "order.address.savePrompt.title":   "Save this address?",
    "order.address.savePrompt.bodyMy":  "I'll add it to your address book — next time you'll be able to pick it in one click.",
    "order.address.savePrompt.bodyCustomer": "I'll add it to customer “{name}” — next time it'll appear in the picker.",
    "order.address.savePrompt.label":    "Label (optional)",
    "order.address.savePrompt.labelPh":  "Home, Office, Mom…",
    "order.address.savePrompt.save":     "Save",
    "order.address.savePrompt.skip":     "Don't save",
    "order.address.savePrompt.saved":    "Address saved",
    "order.address.savePrompt.saveFail": "Couldn't save the address",
    "order.address.picker.default":      "default",
    "order.address.picker.primary":      "primary",

    // Settings → Address book (own)
    "settings.tab.addresses":      "Address book",
    "settings.addresses.title":    "My addresses",
    "settings.addresses.subtitle": "Save addresses you ship to often. They'll appear in the picker when placing an order.",
    "settings.addresses.empty":    "No addresses yet. Add your first one below.",
    "settings.addresses.add":      "Add address",
    "settings.addresses.label":    "Label",
    "settings.addresses.labelPh":  "Home, Office, Mom…",
    "settings.addresses.recipient":"Recipient name",
    "settings.addresses.phone":    "Phone",
    "settings.addresses.address":  "Address",
    "settings.addresses.country":  "Country",
    "settings.addresses.default":  "default",
    "settings.addresses.setDefault":"Set as default",
    "settings.addresses.edit":     "Edit",
    "settings.addresses.delete":   "Delete",
    "settings.addresses.save":     "Save",
    "settings.addresses.cancel":   "Cancel",
    "settings.addresses.deleteConfirm": "Remove this address from your book?",

    // Customer card → Addresses
    "customer.addresses.title":      "Addresses",
    "customer.addresses.empty":      "No addresses yet. Add the first one below.",
    "customer.addresses.add":        "Add address",
    "customer.addresses.primary":    "primary",
    "customer.addresses.setPrimary": "Set as primary",
    "customer.addresses.deleteConfirm": "Delete this address?",

    "orders.tab.orders": "Orders",
    "orders.tab.shipments": "Shipments",
    "orders.consolidate.hint": "Select items at the warehouse to bundle into one shipment",
    "orders.consolidate.selected": "Selected: {n}",
    "orders.consolidate.cta": "Consolidate",
    "shipment.label": "Shipment",
    "shipment.itemsCount": "items",
    "shipment.status.building":  "Building",
    "shipment.status.ready":     "Ready to ship",
    "shipment.status.shipped":   "Shipped",
    "shipment.status.delivered": "Delivered",
    "shipment.status.cancelled": "Cancelled",
    "shipment.ready.cta": "Ready, please ship",
    "shipment.ready.ok": "Marked ready",
    "shipment.ready.waiting": "Hang tight — your shopper will ship it and add the tracking number.",
    "shipment.cancel": "Cancel",
    "shipment.cancel.confirm": "Cancel this shipment? The orders go back to the list so you can rebuild a different bundle.",
    "shipment.cancelled.ok": "Shipment cancelled; orders released.",
    "shipment.created.ok": "Shipment created. Open the Shipments tab to view or edit.",
    "shipments.mine.empty": "No shipments yet. Once your orders reach the warehouse, bundle them in the Orders tab.",

    // Payment confirmation flow (Feature 1)
    "common.copied":             "Copied",
    "order.payment.cta":         "Pay for this order",
    "order.payment.requestQr":      "Request a QR code or link",
    "order.payment.requestQr.note": "Opens the chat with the seller — they'll send a QR or payment link",
    "order.payment.orPayDirect":    "or pay directly",
    "order.payment.requestSent":    "Request sent to chat",
    "order.payment.usdt":        "USDT (TRC20)",
    "order.payment.sbp":         "SBP (by phone)",
    "order.payment.card":        "Card transfer",
    "order.payment.amountDue":   "Amount due",
    "order.payment.noMethods":   "No payment methods configured — DM the manager.",
    "order.payment.pickMethodFirst": "Pick a payment method first.",
    "order.payment.attachScreenshot": "Attach payment screenshot",
    "order.payment.uploadHint":  "PNG/JPG, up to 5 MB",
    "order.payment.uploaded.ok": "Screenshot uploaded; manager will review shortly.",
    "order.payment.awaitingReview": "Payment received, awaiting manager confirmation.",
    "order.payment.viewScreenshot": "Screenshot",

    // СП group-buy (Feature 2)
    "post.locked":            "Group buy closed",
    "post.gb.locked":         "Group buy closed",
    "post.gb.open":           "Joint purchase open",
    "post.gb.expired":        "Time expired",
    "post.gb.committed":      "Committed: {n}",

    // Customs / declared value (Feature 3)
    "common.edit":                       "edit",
    "common.save":                       "Save",
    "shipment.customs.declared":         "Declared value",
    "shipment.customs.overThreshold":    "⚠ Over 200 € — customs duty applies. Split the shipment or set lower declared values.",
    "shipment.customs.declaredItem":     "Declared",
    "shipment.customs.usingPrice":       "= purchase price",
    "shipment.customs.saved":            "Declared value saved",
    "shipment.customs.invalidValue":     "Enter a number ≥ 0",

    // Organizer fee (Feature 7)
    "order.subtotal":      "Items",
    "order.facilitate.title":    "Purchase",
    "order.facilitate.byUs":     "We buy it",
    "order.facilitate.self":     "I order myself",
    "order.facilitate.selfNote": "You order it yourself — no fee from us. BuyerGram just tracks the order.",
    "order.organizerFee":  "Organizer fee ({pct}%)",

    // In-house BuyerGram cashback (migration 0028)
    "order.cashback.pending": "+${amt} cashback ({pct}%) on delivery",
    "order.cashback.earned":  "✓ +${amt} cashback credited",
    "order.cashback.preview": "{pct}% cashback on delivery",
    "channel.cashbackBadge":  "{pct}% cashback",

    // ─── Admin panel ────────────────────────────────────────────
    // Top tabs
    "admin.tab.overview":      "Overview",
    "admin.tab.applications":  "Applications",
    "admin.tab.curated":       "Curated buys",
    "admin.tab.subscriptions": "Subscriptions",
    "admin.tab.orders":        "Orders",
    "admin.tab.shipments":     "Shipments",
    "admin.tab.settings":      "Settings",
    // Sub-nav pills under Settings
    "admin.sub.payments":      "Payments / FX",
    "admin.sub.channels":      "Channels",
    "admin.sub.users":         "Users",
    "admin.sub.groups":        "Groups & Topics",
    "admin.sub.wa":            "WhatsApp",
    "admin.sub.tokens":        "Service tokens",
    "admin.sub.templates":     "DM templates",
    "admin.sub.redemptions":   "Redemptions",
    "admin.sub.audit":         "Audit log",
    // AdminHome
    "admin.home.kicker":           "ADMINISTRATION",
    "admin.home.greeting.night":   "Good night",
    "admin.home.greeting.morning": "Good morning",
    "admin.home.greeting.day":     "Good day",
    "admin.home.greeting.evening": "Good evening",
    "admin.home.subtitle":         "Queues that need your attention. Tap a card to open.",
    "admin.home.loading":          "Loading…",
    "admin.home.card.applications.label": "Applications to review",
    "admin.home.card.applications.empty": "Queue is empty",
    "admin.home.card.applications.hint":  "Approve or reject",
    "admin.home.card.curated.label":      "Active curated buys",
    "admin.home.card.curated.empty":      "All closed",
    "admin.home.card.curated.hint":       "{n0} new · {n1} awaiting payment",
    "admin.home.card.subscriptions.label":"Subscriptions",
    "admin.home.card.subscriptions.hint": "Go to queue",
    "admin.home.bucket.submitted": "New",
    "admin.home.bucket.priced":    "Priced",
    "admin.home.bucket.purchased": "Purchased",
    "admin.home.bucket.invoiced":  "Awaiting payment",
    "admin.home.bucket.paid":      "Paid",
    "admin.home.recent.apps":      "Recent applications",
    "admin.home.recent.curated":   "Recent curated buys",
    "admin.home.recent.openAll":   "Open all →",
    "admin.home.channelLink":      "Channel →",
    "admin.home.timeAgo.now":      "just now",
    "admin.home.timeAgo.min":      "{n} min ago",
    "admin.home.timeAgo.hour":     "{n} h ago",
    "admin.home.timeAgo.day":      "{n} d ago",
    "admin.home.items.one":  "item",
    "admin.home.items.few":  "items",
    "admin.home.items.many": "items",

    // Settings → Profile (nickname / full-name / claim widget)
    "settings.avatar.label":          "Profile photo",
    "settings.nickname.public":       "Nickname (public)",
    "settings.nickname.publicHint":   "This is what other BuyerGram users see. To change it, contact support.",
    "settings.nickname.claim.title":  "Pick a public nickname",
    "settings.nickname.claim.hint":   "Right now others see you as @{nick} — a placeholder. Lock in a real nickname; once set, only support can change it.",
    "settings.nickname.claim.placeholder": "my_handle",
    "settings.nickname.claim.cta":    "Lock in nickname",
    "settings.nickname.err.reserved": "This nickname is reserved.",
    "settings.nickname.err.taken":    "This nickname is already taken.",
    "settings.nickname.err.format":   "3-30 chars: a-z, 0-9, underscore.",
    "settings.nickname.claimed.ok":   "Nickname @{nick} locked in.",
    "settings.fullName.label":        "Full name (private)",
    "settings.language.ru":           "Russian",
    "settings.language.en":           "English",
    "settings.language.es":           "Spanish",
    "settings.subs.posts.one":  "post",
    "settings.subs.posts.few":  "posts",
    "settings.subs.posts.many": "posts",

    // Sidebar empty states (Channels / Groups)
    "sidebar.empty.channels.admin":  "Channels are curated product feeds. Create your first in Settings → Channels.",
    "sidebar.empty.groups.title":    "No groups yet",
    "sidebar.empty.groups.admin":    "Groups are private workspaces for your team. Create one in Settings → Groups & Topics.",
    "sidebar.empty.groups.customer": "You haven't been added to any groups yet. An admin can add you.",

    // ─── Admin: Orders tab ──────────────────────────────────────
    "admin.orders.filter.all":         "All statuses",
    "admin.orders.status.placed":      "placed",
    "admin.orders.status.confirmed":   "confirmed",
    "admin.orders.status.paid":        "paid",
    "admin.orders.status.purchased":   "purchased",
    "admin.orders.status.received":    "at warehouse",
    "admin.orders.status.shipped":     "shipped",
    "admin.orders.status.delivered":   "delivered",
    "admin.orders.status.cancelled":   "cancelled",
    "admin.orders.status.unavailable": "unavailable",
    "admin.ships.status.building":     "building",
    "admin.ships.status.ready":        "ready",
    "admin.ships.status.shipped":      "shipped",
    "admin.ships.status.delivered":    "delivered",
    "admin.ships.status.cancelled":    "cancelled",
    "admin.orders.refresh":            "Reload",
    "admin.orders.refreshing":         "Loading…",
    "admin.orders.empty":              "No orders match this filter.",
    "admin.orders.deletedPost":        "(deleted post)",
    "admin.orders.qtyShort":           "qty {n}",
    "admin.orders.source":             "Buy",
    "admin.orders.itemNo":             "Item #",
    "admin.orders.chat":               "Chat with customer",
    "admin.orders.chatOpened":         "Chat opened — see Messages",
    "admin.orders.fulfilledShort":     "(fulfilled {n})",
    "admin.orders.paymentBadge.needReview": "✓ Needs review",
    "admin.orders.paymentBadge.uploaded":   "✓ Receipt uploaded",
    "admin.orders.paymentBadge.open":       "Open →",
    "admin.orders.cancel.placeholder":      "Why cancel this order?",
    "admin.orders.cancel.reasonTitle":      "Cancel reason?",
    "admin.orders.unavailable.placeholder": "Which item / store / why?",
    "admin.orders.unavailable.reasonTitle": "Unavailable reason?",
    "admin.orders.received.ok":             "Marked received — please add a photo below for the customer.",
    "admin.orders.purchased.prompt":        "How many of {qty} did you buy? (0 = unavailable)",
    "admin.orders.purchased.error":         "Enter a number between 0 and {qty}",
    "admin.orders.reasonLabel":             "Reason:",
    "admin.orders.receipt.title":           "Receipt photos ({n})",
    "admin.orders.receipt.add":             "+ photo",
    "admin.orders.receipt.added":           "Added {n} photo(s)",
    "admin.orders.receipt.uploading":       "…",

    // ─── Admin: Applications tab ────────────────────────────────
    "admin.apps.filter.submitted": "Awaiting review",
    "admin.apps.filter.pending":   "Not yet submitted",
    "admin.apps.filter.approved":  "Approved",
    "admin.apps.filter.rejected":  "Rejected",
    "admin.apps.refresh":          "Refresh",
    "admin.apps.count.submitted":  "awaiting decision",
    "admin.apps.count.other":      "in this bucket",
    "admin.apps.empty":            "Queue is empty.",
    "admin.apps.submittedAt":      "Submitted {when}",
    "admin.apps.verificationCode": "verification code",
    "admin.apps.field.channel":    "Channel",
    "admin.apps.field.platform":   "Platform",
    "admin.apps.field.audience":   "Audience",
    "admin.apps.field.categories": "Categories",
    "admin.apps.field.contact":    "Contact",
    "admin.apps.field.verificationPost": "Verification post",
    "admin.apps.rejectReason":     "Reject reason:",
    "admin.apps.approve":          "Approve",
    "admin.apps.approved.ok":      "Approved",
    "admin.apps.reject":           "Reject…",
    "admin.apps.reject.confirm":   "Confirm reject",
    "admin.apps.reject.cancel":    "Cancel",
    "admin.apps.reject.placeholder": "Reject reason (required)",
    "admin.apps.reject.required":  "Reject reason required",
    "admin.apps.reject.ok":        "Rejected",

    // ─── Admin: Subscriptions tab ───────────────────────────────
    "admin.subs.hint":            "In-app subscription requests. Approve once payment has cleared out-of-band; rejection lets the user resubmit. Approving auto-extends the user's premium term and sets their tier.",
    "admin.subs.empty":           "No subscription orders yet.",
    "admin.subs.period.annual":   "annual",
    "admin.subs.period.monthly":  "monthly",
    "admin.subs.adminPrefix":     "Admin:",
    "admin.subs.filed":           "Filed {when}",
    "admin.subs.decided":         " · decided {when}",
    "admin.subs.approve":         "Approve",
    "admin.subs.reject":          "Reject",
    "admin.subs.approve.confirm": "Approve {tier} {period} for \"{user}\"?",
    "admin.subs.approve.placeholder": "Optional admin note (e.g. payment txn)",
    "admin.subs.approve.ok":      "Approved",
    "admin.subs.reject.confirm":  "Reject this order? Reason will be visible to the user.",
    "admin.subs.reject.placeholder": "Reason (e.g. payment not received)",
    "admin.subs.reject.ok":       "Rejected",

    // ─── Admin: Shipments tab ───────────────────────────────────
    "admin.ships.filter.all":     "All statuses",
    "admin.ships.refresh":        "Reload",
    "admin.ships.refreshing":     "Loading…",
    "admin.ships.empty":          "No shipments match.",
    "admin.ships.deletedItem":    "(deleted)",
    "admin.ships.itemsCount":     "{n} item(s):",
    "admin.ships.itemQty":        "qty",
    "admin.ships.customs.over":   "⚠ Customs:",
    "admin.ships.customs.normal": "Declared:",
    "admin.ships.printPickList":  "🖨 Print pick list / packing slip",
    "admin.ships.tracking.carrier":  "Carrier",
    "admin.ships.tracking.number":   "Tracking #",
    "admin.ships.tracking.weight":   "kg",
    "admin.ships.tracking.url":      "Tracking URL (optional)",
    "admin.ships.tracking.save":     "Save tracking",
    "admin.ships.tracking.saving":   "Saving…",
    "admin.ships.tracking.saved":    "Tracking saved",
    "admin.ships.tracking.required": "Tracking number required first",
    "admin.ships.markShipped":       "Mark shipped",
    "admin.ships.markDelivered":     "Mark delivered",
    "admin.ships.markShipped.confirm":   "Mark shipment #{id} as shipped? All {n} order(s) will move to shipped status.",
    "admin.ships.markShipped.ok":        "Shipped",
    "admin.ships.markDelivered.confirm": "Mark shipment #{id} as delivered?",
    "admin.ships.markDelivered.ok":      "Delivered",

    // ─── Admin: Settings (Payments / FX) ────────────────────────
    "admin.fx.hint":              "FX rate applied to USD prices for the RUB display on post cards and snapshotted into new orders. Past orders keep their original rate — only new orders use the updated number.",
    "admin.fx.title":             "USD → RUB rate",
    "admin.fx.base":              "Base rate (forex)",
    "admin.fx.markup":            "Markup %",
    "admin.fx.effective":         "Effective rate",
    "admin.fx.currentSaved":      "Currently saved: base {base} + {markup}% = ",
    "admin.fx.save":              "Save",
    "admin.fx.saving":            "Saving…",
    "admin.fx.saved":             "FX rate updated",
    "admin.fx.error.base":        "Base must be positive",
    "admin.fx.error.markup":      "Markup must be ≥ 0",
    "admin.fx.auto.title":        "CBR auto-update",
    "admin.fx.auto.on":           "On — hourly",
    "admin.fx.auto.off":          "Off — paused",
    "admin.fx.auto.enabled":      "Auto-update enabled",
    "admin.fx.auto.disabled":     "Auto-update disabled",
    "admin.fx.auto.lastFetch":    "Last fetch",
    "admin.fx.auto.status":       "Status",
    "admin.fx.auto.status.ok":    "OK",
    "admin.fx.auto.status.fail":  "Failed — {err}",
    "admin.fx.auto.status.unknown": "unknown",
    "admin.fx.auto.cbrRate":      "CBR rate",
    "admin.fx.auto.cbrRate.value": "1$ = {rate}₽ (pre-markup)",
    "admin.fx.auto.lastChange":   "Last change",
    "admin.fx.auto.noChange":     "no changes yet",
    "admin.fx.auto.source":       "Source: cbr-xml-daily.ru · publishes ~12:30 MSK",
    "admin.fx.auto.refreshNow":   "Refresh now",
    "admin.fx.auto.fetching":     "Fetching…",
    "admin.fx.auto.cbrToast":     "CBR: 1$ = {rate}₽",
    "admin.fx.auto.cbrFailed":    "CBR fetch failed: {err}",
    "admin.fx.auto.never":        "never",
    "admin.fx.auto.relSec":       "{n}s ago",
    "admin.fx.auto.relMin":       "{n} min ago",
    "admin.fx.auto.relHour":      "{n} h ago",
    "admin.fx.auto.relDay":       "{n} d ago",
    "admin.pm.title":             "Payment methods",
    "admin.pm.hint":              "Shown to customers when paying a confirmed order. Leave a field blank to hide that payment method. Numbers/wallets are NEVER written to the audit log — only field names + lengths are.",
    "admin.pm.loading":           "Loading…",
    "admin.pm.usdt.title":        "USDT (multi-network)",
    "admin.pm.usdc.title":        "USDC (multi-network)",
    "admin.pm.sbp.title":         "SBP (Russian fast payments)",
    "admin.pm.sbp.phone":         "Phone",
    "admin.pm.sbp.name":          "Recipient name",
    "admin.pm.card.title":        "Card-to-card",
    "admin.pm.card.number":       "Card number",
    "admin.pm.card.bank":         "Bank",
    "admin.pm.card.holder":       "Cardholder name",
    "admin.pm.instructions.title": "Instructions (shown beneath the methods)",
    "admin.pm.instructions.ru":   "Russian",
    "admin.pm.instructions.en":   "English",
    "admin.pm.instructions.placeholderRu": "Можно платить в любой валюте…",
    "admin.pm.instructions.placeholderEn": "USDT preferred…",
    "admin.pm.save":              "Save payment methods",
    "admin.pm.saving":            "Saving…",
    "admin.pm.saved":             "Payment methods updated",
    "admin.pm.networkHint":       "one row per chain · network labels normalized to UPPERCASE on save",
    "admin.pm.removeNetwork":     "Remove this entry",
    "admin.pm.addNetwork":        "+ Add network",
    "admin.pm.placeholder.networkAddress": "wallet address on that chain",

    // ─── Admin: Groups & Topics ────────────────────────────────
    "admin.groups.create":        "+ Create new group",
    "admin.groups.members":       "{n} members",
    "admin.groups.addTopic":      "+ Topic",
    "admin.groups.delete":        "Delete",
    "admin.groups.edit":          "Edit",
    "admin.groups.save":          "Save",
    "admin.groups.cancel":        "Cancel",
    "admin.groups.saved":         "Saved",
    "admin.groups.namePlaceholder": "Name",
    "admin.groups.descPlaceholder": "Description (optional)",
    "admin.groups.deleteConfirm":   "Delete group \"{name}\" with all its topics and messages?",
    "admin.groups.error.nameRequired": "Name is required",
    "admin.groups.error.topicNameRequired": "Topic name is required",
    "admin.groups.topic.edit":     "Rename topic",
    "admin.groups.topic.delete":   "Delete topic",
    "admin.groups.topic.saved":    "Topic renamed",
    "admin.groups.topic.deleteConfirm": "Delete topic \"{name}\"?",

    // ─── Admin: Users ──────────────────────────────────────────
    "admin.users.role.admin":    "ADMIN",
    "admin.users.role.manager":  "MANAGER",
    "admin.users.role.premium":  "PREMIUM",
    "admin.users.role.banned":   "BANNED",
    "admin.users.you":           "(you)",
    "admin.users.banReason":     "Reason:",
    "admin.users.role.customer.opt": "Customer",
    "admin.users.role.manager.opt":  "Manager",
    "admin.users.role.admin.opt":    "Admin",
    "admin.users.premium.label":   "Premium…",
    "admin.users.premium.1mo":     "+ 1 month",
    "admin.users.premium.3mo":     "+ 3 months",
    "admin.users.premium.12mo":    "+ 12 months",
    "admin.users.premium.revoke":  "Revoke",
    "admin.users.premium.grant":   "Grant {months} month(s) of premium to \"{name}\"?",
    "admin.users.premium.revoke.confirm": "Revoke premium from \"{name}\"?",
    "admin.users.unban":           "Unban",
    "admin.users.ban":             "Ban",
    "admin.users.ban.prompt":      "Ban \"{name}\"? Optional reason:",
    "admin.users.ban.placeholder": "Reason (optional)",
    "admin.users.premium.until":   "Premium until {date}",
    "admin.users.premium.free":    "Free tier",

    // ─── Admin: Channels tab ───────────────────────────────────
    "admin.channels.hint":       "Organizer fee is your margin on top of the FX-converted price. Customer cashback is paid out of that margin on order delivery — e.g. fee 20% + cashback 3% = net 17%. Changes apply to NEW orders only; historical orders keep their snapshot.",
    "admin.channels.empty":      "No channels yet.",
    "admin.channels.posts":      "{n} posts",
    "admin.channels.edit":       "Edit",
    "admin.channels.close":      "Close",
    "admin.channels.delete":     "Delete",
    "admin.channels.deleting":   "Deleting…",
    "admin.channels.editTitle.collapse": "Collapse",
    "admin.channels.editTitle.expand":   "Edit name / description / owner",
    "admin.channels.deleteTitle": "Delete channel (cascades to posts)",
    "admin.channels.name":       "Name",
    "admin.channels.name.placeholder": "Channel name",
    "admin.channels.description": "Description",
    "admin.channels.description.placeholder": "Shown to customers in the channel list",
    "admin.channels.owner":      "Owner (manager / admin)",
    "admin.channels.owner.current": "Current owner (uid: {uid}…)",
    "admin.channels.fee":        "Organizer fee %",
    "admin.storeFees.title":     "Commission by store",
    "admin.storeFees.hint":      "Set the commission for all channels of a store at once. Individual channels can still be fine-tuned below.",
    "admin.storeFees.count":     "{n} channels",
    "admin.storeFees.mixed":     "mixed",
    "admin.storeFees.apply":     "Apply",
    "admin.storeFees.applied":   "Commission applied to {n} channels ({store})",
    "admin.channels.cashback":   "Customer cashback %",
    "admin.channels.save":       "Save changes",
    "admin.channels.saving":     "Saving…",
    "admin.channels.saved":      "Saved",
    "admin.channels.savedShort": "Saved",
    "admin.channels.netMargin": "Net margin:",
    "admin.channels.netNegative": " — cashback exceeds fee, you'd lose money per order",
    "admin.channels.error.fee":    "Fee must be 0..100",
    "admin.channels.error.cashback": "Cashback must be 0..100",
    "admin.channels.error.name":   "Name must be at least 2 characters",
    "admin.channels.delete.confirm": "Delete channel \"{name}\"?\n\nThis cascades to ALL of its posts ({n} published).\nType the channel name to confirm.",
    "admin.channels.delete.mismatch": "Name didn't match — nothing deleted.",
    "admin.channels.delete.ok":     "Channel \"{name}\" deleted",

    // ─── Admin: WhatsApp tab ───────────────────────────────────
    "admin.wa.hint":              "WhatsApp ingest pipeline. Each store rep's phone number can be mapped to a channel — incoming product images + captions are auto-parsed (price, sizes) and become a draft post in that channel. Toggle \"auto-publish\" once you trust the parses for a given sender.",
    "admin.wa.configured":        "Configured senders",
    "admin.wa.configured.empty":  "No routes yet — pick a sender below or add one manually.",
    "admin.wa.recent":            "Recent senders (last 30 days)",
    "admin.wa.recent.empty":      "No messages received yet. Send a WhatsApp message to your business number to start.",
    "admin.wa.msgsLast":          "{n} msgs · last: \"{text}\"",
    "admin.wa.routed":            "✓ routed",
    "admin.wa.addRoute":          "+ Add route",
    "admin.wa.addRoute.prompt":   "Display name for {phone}?",
    "admin.wa.addRoute.placeholder": "e.g. Karl Lagerfeld store",
    "admin.wa.addRoute.ok":       "Route added — pick a channel below",
    "admin.wa.route.displayName": "Display name",
    "admin.wa.route.channelDefault": "(default channel)",
    "admin.wa.route.autopublish":  "Auto-publish (high-confidence parses only)",
    "admin.wa.route.delete":      "Delete",
    "admin.wa.route.delete.confirm": "Delete route for {phone}?",
    "admin.wa.route.deleted":     "Deleted",
    "admin.wa.route.save":        "Save",
    "admin.wa.route.saving":      "…",
    "admin.wa.route.saved":       "Saved",

    // ─── Admin: Tokens tab ─────────────────────────────────────
    "admin.tokens.newToken":      "New token (copy now — won't show again)",
    "admin.tokens.copy":          "Copy",
    "admin.tokens.dismiss":       "Dismiss",
    "admin.tokens.create":        "Create token",
    "admin.tokens.name":          "Name",
    "admin.tokens.name.placeholder": "shopper-bot-prod",
    "admin.tokens.scopes":        "Scopes (comma-separated)",
    "admin.tokens.scopes.placeholder": "posts.write",
    "admin.tokens.generate":      "Generate",
    "admin.tokens.active":        "Active tokens",
    "admin.tokens.active.empty":  "None.",
    "admin.tokens.createdAt":     "created {date}",
    "admin.tokens.lastUsedAt":    " · last used {date}",
    "admin.tokens.revoke":        "Revoke",
    "admin.tokens.revoke.confirm": "Revoke \"{name}\"? This cannot be undone.",
    "admin.tokens.revoked.section": "Revoked ({n})",
    "admin.tokens.revoked.row":     "{name} · revoked {date}",

    // ─── Admin: Redemptions tab ────────────────────────────────
    "admin.reds.empty":           "No pending redemptions.",
    "admin.reds.requested":       "requested {when}",
    "admin.reds.sendTo":          "Send to:",
    "admin.reds.fulfill":         "Fulfill",
    "admin.reds.fulfill.confirm": "Mark {amount} as fulfilled for {email}?",
    "admin.reds.fulfill.notePrompt": "Fulfillment note (optional)",
    "admin.reds.fulfill.placeholder": "gift card code, tracking number, etc.",
    "admin.reds.cancel":          "Cancel",
    "admin.reds.cancel.title":    "Cancel reason?",
    "admin.reds.cancel.placeholder": "Reason for cancellation",

    // ─── Admin: DM Templates tab ───────────────────────────────
    "admin.templates.hint.prefix":     "Reusable DM messages. Use ",
    "admin.templates.hint.suffix":     " as placeholders — they get substituted when the staff picks a template from the chat composer. Managers can use templates; only admins create or edit them.",
    "admin.templates.new":             "+ New template",
    "admin.templates.empty":           "No templates yet. Create your first.",
    "admin.templates.delete":          "Delete",
    "admin.templates.create.namePrompt": "Template name?",
    "admin.templates.create.namePlaceholder": "Прибыл на склад",
    "admin.templates.create.bodyPrompt":   "Body (Russian)?",
    "admin.templates.create.bodyPlaceholder": "Привет, {customer_name}! Заказ прибыл.",
    "admin.templates.create.ok":       "Template created",
    "admin.templates.delete.confirm":  "Delete template \"{name}\"?",
    "admin.templates.delete.ok":       "Deleted",

    // ─── Admin: Audit tab ──────────────────────────────────────
    "admin.audit.action.all":     "All actions",
    "admin.audit.target.all":     "All target types",
    "admin.audit.reload":         "Reload",
    "admin.audit.reloading":      "Loading…",
    "admin.audit.empty":          "No audit entries match the current filters.",
    "admin.audit.system":         "(system)",
    "admin.audit.req":            "req: {id}",

    // ─── Admin: Curated (staff) ────────────────────────────────
    "admin.curated.title":            "Curated purchases queue",
    "admin.curated.settings":         "Settings",
    "admin.curated.filter.open":      "Open (default)",
    "admin.curated.filter.submitted": "Submitted",
    "admin.curated.filter.priced":    "Priced",
    "admin.curated.filter.purchased": "Purchased",
    "admin.curated.filter.invoiced":  "Invoiced",
    "admin.curated.filter.paid":      "Paid",
    "admin.curated.filter.completed": "Completed",
    "admin.curated.filter.cancelled": "Cancelled",
    "admin.curated.filter.rejected":  "Rejected",
    "admin.curated.refresh":          "Refresh",
    "admin.curated.count":            "{n} requests",
    "admin.curated.loading":          "Loading…",
    "admin.curated.empty":            "Queue empty.",
    "admin.curated.row.items":        "{n} items · stated {stated}",
    "admin.curated.row.final":        " · final {final}",
    "admin.curated.row.account":      "🔐 acct",
    "admin.curated.row.links":        "🔗 links",
    "admin.curated.back":             "← Back to queue",
    "admin.curated.created":          "created {when}",
    "admin.curated.facts.type":       "Type",
    "admin.curated.facts.site":       "Site",
    "admin.curated.facts.recipient":  "Recipient",
    "admin.curated.facts.address":    "Address",
    "admin.curated.facts.items":      "Items",
    "admin.curated.facts.stated":     "Stated total",
    "admin.curated.facts.final":      "Final total",
    "admin.curated.facts.currency":   "Preferred currency",
    "admin.curated.facts.notes":      "Reseller notes",
    "admin.curated.facts.links":      "Links",
    "admin.curated.creds.title":      "🔐 Account credentials",
    "admin.curated.creds.purged":     "Purged.",
    "admin.curated.creds.none":       "Not provided.",
    "admin.curated.creds.login":      "Login:",
    "admin.curated.creds.password":   "Password:",
    "admin.curated.creds.audited":    "Reveal audited. Don't paste in chat.",
    "admin.curated.creds.reveal":     "Reveal (audit-logged)",
    "admin.curated.invoice.waiting":  "Awaiting payment — {amount} {currency}",
    "admin.curated.invoice.expires":  "Expires {when}",
    "admin.curated.invoice.markPaid": "Mark paid",
    "admin.curated.invoice.markPaid.ok": "Marked paid",
    "admin.curated.complete":         "Complete & purge credentials",
    "admin.curated.complete.confirm": "Complete & purge credentials?",
    "admin.curated.complete.ok":      "Completed",
    "admin.curated.reject.label":     "Reject reason (required, shown to reseller)",
    "admin.curated.reject.placeholder": "Reason…",
    "admin.curated.reject.confirm":   "Confirm reject",
    "admin.curated.reject.cancel":    "Cancel",
    "admin.curated.reject.start":     "Reject…",
    "admin.curated.reject.required":  "Reason required",
    "admin.curated.reject.ok":        "Rejected",
    "admin.curated.price.title":      "Set final total (USD)",
    "admin.curated.price.notes":      "Notes for the reseller (optional)",
    "admin.curated.price.notes.placeholder": "e.g. shipping turned out higher than expected",
    "admin.curated.price.save":       "Save price",
    "admin.curated.price.invalid":    "Invalid total",
    "admin.curated.price.ok":         "Priced",
    "admin.curated.purchased.title":  "Mark as purchased on upstream site",
    "admin.curated.purchased.receipt": "Receipt:",
    "admin.curated.purchased.uploaded": "Receipt uploaded",
    "admin.curated.purchased.mark":   "Mark purchased",
    "admin.curated.purchased.ok":     "Marked purchased",
    "admin.curated.invoice.title":    "Issue invoice",
    "admin.curated.invoice.reissue":  "Re-issue invoice (link expired)",
    "admin.curated.invoice.currency": "Currency",
    "admin.curated.invoice.wallet":   "Pick wallet (from admin settings)",
    "admin.curated.invoice.wallet.placeholder": "— pick wallet —",
    "admin.curated.invoice.target.rub": "Payment link (one-time, 20-min expiry)",
    "admin.curated.invoice.target.crypto": "Wallet address",
    "admin.curated.invoice.placeholder.rub": "https://…",
    "admin.curated.invoice.placeholder.crypto": "0x… or T…",
    "admin.curated.invoice.preview":  "Reseller will see: ",
    "admin.curated.invoice.previewExpires": " · expires in 20 min",
    "admin.curated.invoice.send":     "Send invoice",
    "admin.curated.invoice.resend":   "Re-issue invoice",
    "admin.curated.invoice.required": "Payment target required",
    "admin.curated.invoice.ok":       "Invoiced",
    "admin.curated.invoice.reissued": "Re-issued",
    "admin.curated.settings.back":    "← Back to queue",
    "admin.curated.settings.title":   "Curated purchase settings",
    "admin.curated.settings.fxTitle": "RUB / USD RATE FOR CURATED BUYS",
    "admin.curated.settings.cbrBase": "CBR rate (base)",
    "admin.curated.settings.markup": "Markup %",
    "admin.curated.settings.effective": "Effective rate",
    "admin.curated.settings.refreshCbr": "Refresh CBR rate",
    "admin.curated.settings.refreshing": "Refreshing…",
    "admin.curated.settings.refreshedToast": "CBR rate refreshed",
    "admin.curated.settings.updatedAt": "Updated {when}",
    "admin.curated.settings.fxHint": "Reseller sees the effective rate when issuing a RUB invoice. The base rate comes from the same CBR source as retail FX and refreshes hourly.",
    "admin.curated.settings.payInstructions": "Payment instructions shown to reseller (optional)",
    "admin.curated.settings.wallets": "Crypto wallets",
    "admin.curated.settings.addWallet": "+ Add wallet",
    "admin.curated.settings.save":    "Save",
    "admin.curated.settings.saved":   "Saved",
    "admin.curated.settings.adminOnly": "Admin only",
    "admin.curated.settings.wallet.address": "address",
    "admin.curated.settings.wallet.label": "label",

    // ─── Reseller-side CuratedPanel ────────────────────────────
    "admin.curated.reseller.title":       "Curated buys",
    "admin.curated.reseller.subtitle":    "Ask BuyerGram to make the purchase for you — pay the invoice in USDT, USDC or RUB.",
    "admin.curated.reseller.new":         "+ New request",
    "admin.curated.reseller.empty":       "No curated buys yet. Create your first — it takes 2 minutes.",
    "admin.curated.reseller.loading":     "Loading…",
    "admin.curated.reseller.row.account": "Account access",
    "admin.curated.reseller.row.links":   "Link list",
    "admin.curated.reseller.row.items.one":  "item",
    "admin.curated.reseller.row.items.few":  "items",
    "admin.curated.reseller.row.items.many": "items",
    "admin.curated.reseller.status.submitted": "Submitted",
    "admin.curated.reseller.status.priced":    "Priced",
    "admin.curated.reseller.status.purchased": "Purchased",
    "admin.curated.reseller.status.invoiced":  "Awaiting payment",
    "admin.curated.reseller.status.paid":      "Paid",
    "admin.curated.reseller.status.completed": "Completed",
    "admin.curated.reseller.status.cancelled": "Cancelled",
    "admin.curated.reseller.status.rejected":  "Rejected",
    "admin.curated.reseller.back":        "← Back to list",
    "admin.curated.reseller.new.title":   "New curated buy",
    "admin.curated.reseller.mode.label":  "How will you share details",
    "admin.curated.reseller.mode.links.title": "Link list",
    "admin.curated.reseller.mode.links.desc":  "I send product links — BuyerGram adds them to cart and buys.",
    "admin.curated.reseller.mode.account.title": "Account access",
    "admin.curated.reseller.mode.account.desc":  "Cart is ready — I share login/password, you just need to pay.",
    "admin.curated.reseller.account.site": "Store URL",
    "admin.curated.reseller.account.login": "Login",
    "admin.curated.reseller.account.password": "Password",
    "admin.curated.reseller.account.hint": "Login and password are stored encrypted and auto-purged once the request completes or is cancelled. If the store has 2FA enabled we'll DM you at purchase time.",
    "admin.curated.reseller.links.label": "Product links",
    "admin.curated.reseller.links.qty":   "qty",
    "admin.curated.reseller.links.add":   "+ Add link",
    "admin.curated.reseller.delivery.name": "Recipient name",
    "admin.curated.reseller.delivery.name.placeholder": "Full name",
    "admin.curated.reseller.delivery.address": "Shipping address",
    "admin.curated.reseller.delivery.address.placeholder": "City, street, building, apt, postcode, phone",
    "admin.curated.reseller.itemCount": "Item count",
    "admin.curated.reseller.statedTotal": "Expected total (USD)",
    "admin.curated.reseller.currency": "Invoice currency",
    "admin.curated.reseller.currency.rub": "RUB (at BuyerGram rate)",
    "admin.curated.reseller.notes": "Notes (optional)",
    "admin.curated.reseller.notes.placeholder": "Size, color, details…",
    "admin.curated.reseller.submit": "Submit request",
    "admin.curated.reseller.submitting": "Submitting…",
    "admin.curated.reseller.cancel": "Cancel",
    "admin.curated.reseller.error.amount": "Enter expected total in USD",
    "admin.curated.reseller.error.noLinks": "Add at least one link",
    "admin.curated.reseller.created.ok": "Request created",
    "admin.curated.reseller.detail.expired": "Payment link expired",
    "admin.curated.reseller.detail.expiredHint": "DM the manager — they'll issue a new link.",
    "admin.curated.reseller.detail.paid": "Payment received",
    "admin.curated.reseller.detail.paidHint": "Processing the purchase.",
    "admin.curated.reseller.field.type": "Request type",
    "admin.curated.reseller.field.site": "Store",
    "admin.curated.reseller.field.products": "Items",
    "admin.curated.reseller.field.recipient": "Recipient",
    "admin.curated.reseller.field.address": "Address",
    "admin.curated.reseller.field.itemCount": "Item count",
    "admin.curated.reseller.field.statedTotal": "Stated total",
    "admin.curated.reseller.field.finalTotal": "Final total",
    "admin.curated.reseller.field.currency": "Invoice currency",
    "admin.curated.reseller.field.notes": "Your notes",
    "admin.curated.reseller.field.managerNotes": "Manager notes",
    "admin.curated.reseller.field.rejectionReason": "Rejection reason",
    "admin.curated.reseller.field.receipt": "Purchase receipt",
    "admin.curated.reseller.receipt.open": "Open",
    "admin.curated.reseller.cancelBtn": "Cancel request",
    "admin.curated.reseller.cancel.confirm": "Cancel this request? Cannot be undone.",
    "admin.curated.reseller.cancel.ok": "Request cancelled",
    "admin.curated.reseller.invoice.title": "Invoice",
    "admin.curated.reseller.invoice.minutesLeft": "~{n} min left to pay",
    "admin.curated.reseller.invoice.cryptoAddress": "Wallet address ({currency}):",
    "admin.curated.reseller.invoice.payCta": "Pay now →",
    "admin.curated.reseller.invoice.refresh": "Refresh status",

    // ─── ApplicationModal (reseller) ───────────────────────────
    "admin.application.title":      "Channel ownership verification",
    "admin.application.loading":    "Loading…",
    "admin.application.copy":       "Copy",
    "admin.application.copied":     "Copied ✓",
    "admin.application.step1.label": "Step 1 · Verification code",
    "admin.application.step1.hint": "Post this code in your primary sales channel (Telegram, MAX, Instagram, VK or your own website) — it proves the channel is yours. You can delete the post once the application is approved.",
    "admin.application.step2.label": "Step 2 · Channel link",
    "admin.application.platform":   "Platform",
    "admin.application.platform.vk": "VK",
    "admin.application.platform.ok": "Odnoklassniki",
    "admin.application.platform.website": "Own website",
    "admin.application.platform.other": "Other",
    "admin.application.audience":   "Audience size",
    "admin.application.audience.lt100":  "Under 100",
    "admin.application.audience.100_500": "100–500",
    "admin.application.audience.500_2k": "500–2,000",
    "admin.application.audience.2k_10k": "2,000–10,000",
    "admin.application.audience.10kp":   "Over 10,000",
    "admin.application.categories": "Product categories",
    "admin.application.category.women.shoes": "Women's shoes",
    "admin.application.category.men.shoes":   "Men's shoes",
    "admin.application.category.women.clothing": "Women's clothing",
    "admin.application.category.men.clothing":   "Men's clothing",
    "admin.application.category.kids":        "Kids' clothing / shoes",
    "admin.application.category.beauty":      "Beauty & personal care",
    "admin.application.category.electronics": "Electronics",
    "admin.application.category.home":        "Home & kitchen",
    "admin.application.category.bags":        "Bags & accessories",
    "admin.application.category.other":       "Other",
    "admin.application.contact":    "Contact (Telegram handle or email)",
    "admin.application.step3.label": "Step 3 · Link to the post with the code",
    "admin.application.step3.hint": "The post must be public so the reviewer can open it and see the code. If your channel is private, DM the code to @buyergram_support with a screenshot.",
    "admin.application.submit":     "Submit application",
    "admin.application.submitting": "Submitting…",
    "admin.application.submitted.title": "Application under review",
    "admin.application.submitted.body":  "We usually review within 12 business hours. Once approved, prices and forwarding unlock automatically.",
    "admin.application.submitted.sent":  "What you submitted",
    "admin.application.rejected.title": "Application rejected",
    "admin.application.rejected.noReason": "No reason given. Contact support.",
    "admin.application.rejected.newCode": "We issued a new verification code. Post it in your channel and submit a new post link.",
    "admin.application.field.channel": "Channel",
    "admin.application.field.audience": "Audience",
    "admin.application.field.categories": "Categories",
    "admin.application.field.contact": "Contact",
    "admin.application.field.verificationPost": "Verification post",

    // ─── Dashboard (reseller home) ─────────────────────────────
    "dashboard.menu":              "Menu",
    "dashboard.kicker":            "Home",
    "dashboard.loading":           "Loading…",
    "dashboard.loadError":         "Couldn't load the dashboard: {err}",
    "dashboard.subtitle":          "What's happened in your store over the past 7 days.",
    "dashboard.greeting.night":    "Good night",
    "dashboard.greeting.morning":  "Good morning",
    "dashboard.greeting.day":      "Good day",
    "dashboard.greeting.evening":  "Good evening",
    "dashboard.attention.events": "unread events",
    "dashboard.attention.cta":     "Open →",
    "dashboard.kpi.forwards":      "Forwards",
    "dashboard.kpi.delivered":     "Delivered",
    "dashboard.kpi.orders":        "Orders",
    "dashboard.kpi.hint.noForwards":  "Nothing forwarded yet",
    "dashboard.kpi.hint.forwardsOk":  "{n} successful deliveries",
    "dashboard.kpi.hint.deliveriesOk":"Cards that reached subscribers",
    "dashboard.kpi.hint.noDeliveries":"No targets yet",
    "dashboard.kpi.hint.ordersOk":    "Placed by your store",
    "dashboard.kpi.hint.noOrders":    "No orders yet",
    "dashboard.section.pending":   "Orders in progress",
    "dashboard.section.pending.allOrders": "All orders",
    "dashboard.section.pending.empty":     "All orders delivered or none placed yet.",
    "dashboard.section.pending.browseCta": "Browse catalog",
    "dashboard.section.reengage":  "Worth reaching out to",
    "dashboard.section.reengage.allCustomers": "All customers",
    "dashboard.section.activity":  "Customer activity",
    "dashboard.section.activity.empty":     "Attach an order to a customer in the order card — recent activity per customer will appear here.",
    "dashboard.section.activity.openCustomers": "Open Customers",
    "dashboard.emptyState.title": "Launch your store in 5 minutes.",
    "dashboard.emptyState.body":  "Set your FX rate and a forward channel, then pick items from the catalog and send them to your subscribers.",
    "dashboard.emptyState.setup":  "Set up forwarding",
    "dashboard.emptyState.browse": "Browse catalog",
    "dashboard.row.untitled":     "(untitled)",
    "dashboard.row.openCta":      "Open →",
    "dashboard.row.ago":          "ago",
    "dashboard.row.now":          "just now",
    "dashboard.status.placed":    "placed",
    "dashboard.status.confirmed": "confirmed",
    "dashboard.status.paid":      "paid",
    "dashboard.status.purchased": "purchased",
    "dashboard.status.received":  "at warehouse",
    "dashboard.status.shipped":   "shipped",
    "dashboard.status.delivered": "delivered",
    "dashboard.status.cancelled": "cancelled",
    "dashboard.status.unavailable": "unavailable",
    "dashboard.unit.min":  "min",
    "dashboard.unit.hour": "h",
    "dashboard.unit.day":  "d",
    "dashboard.unit.mo":   "mo",

    // ─── MyStore (reseller business surface) ───────────────────
    "mystore.menu":              "Menu",
    "mystore.heading":           "My Store",
    "mystore.adminHeading":      "Admin",
    "mystore.tab.home":          "Home",
    "mystore.tab.customers":     "Customers",
    "mystore.tab.forward":       "Forwarding",
    "mystore.tab.curated":       "Curated buys",
    "mystore.tab.stats":         "Analytics",
    "mystore.section.customers": "Customers",
    "mystore.section.forward":   "Forwarding",
    "mystore.section.stats":     "Analytics",

    // ─── Forward modal ─────────────────────────────────────────
    "forward.title": "Forward {n}",
    "forward.cards.one":  "card",
    "forward.cards.few":  "cards",
    "forward.cards.many": "cards",
    "forward.allFilteredNote": "All cards in the current filter",
    "forward.loadingSettings": "Loading settings…",
    "forward.hint.noFx.title":   "Set your $ → ₽ rate first",
    "forward.hint.noFx.body":    "Without a rate we can't compute the ₽-price for your caption. Open forward settings and set the FX rate you want to show subscribers.",
    "forward.hint.openSettings": "Open settings",
    "forward.hint.noTarget.title": "Add a forward channel",
    "forward.hint.noTarget.body":  "Link a Telegram channel (or several) we'll push selected cards to. The bot must be an admin in that channel.",
    "forward.hint.allDisabled.title": "All channels disabled",
    "forward.hint.allDisabled.body":  "You have channels in settings but none are enabled. Enable at least one to continue.",
    "forward.hint.setup": "Configure",
    "forward.preview.label":   "What subscribers will see (first card)",
    "forward.preview.noImage": "no image",
    "forward.preview.emptyCaption": "(caption empty — configure the template)",
    "forward.preview.rate":      "Rate: ₽{rate} per $1",
    "forward.preview.batchSize": " · total cards: {n}",
    "forward.target.label":     "Send to",
    "forward.target.kind.tg":   "Telegram",
    "forward.target.kind.max":  "MAX",
    "forward.target.soon":      "soon",
    "forward.cancel":     "Cancel",
    "forward.send":       "Send {n}",
    "forward.sending":    "Sending…",
    "forward.result.failed":   "Nothing was sent",
    "forward.result.allOk":    "All {n} sends succeeded",
    "forward.result.partial":  "{ok} of {total} sends succeeded",
    "forward.result.done":     "Done",
    "forward.result.notSent":  "Not sent ({n})",
    "forward.result.andMore":  "…and {n} more",

    // ─── Forward history ───────────────────────────────────────
    "forward.history.title":   "Forward history",
    "forward.history.subtitle":"Tap an item to open its source page — that's where you place the order",
    "forward.history.tab.items":   "Items",
    "forward.history.tab.batches": "Sends",
    "forward.history.search.placeholder": "Search by brand, model, SKU",
    "forward.history.loading": "Loading…",
    "forward.history.empty":   "You haven't forwarded anything yet. Pick cards in a channel and tap \"Forward selected\" — they'll show up here.",
    "forward.history.foundOf": "{n} of {total} items",
    "forward.history.count.one":  "item",
    "forward.history.count.few":  "items",
    "forward.history.count.many": "items",
    "forward.history.noMatch":  "Nothing found for \"{q}\".",
    "forward.history.toSource": "Open source ↗",
    "forward.history.sendsCount": "{n} sends",
    "forward.history.dash":       "—",
    "forward.history.unitMin":  "min ago",
    "forward.history.unitHour": "h ago",
    "forward.history.unitDay":  "d ago",
    "forward.history.justNow":  "just now",
    "forward.history.batchAllSent": "All {n} sent",
    "forward.history.batchPartial": "{ok} of {total} sent",
    "forward.history.postMissing":  "post removed or unavailable",

    // ─── Notifications ─────────────────────────────────────────
    "notifications.title": "Events",
    "notifications.push.title": "Enable push?",
    "notifications.push.body":  "Your browser will show notifications even when BG is closed.",
    "notifications.push.enable":"Enable",
    "notifications.feed.label": "Notifications feed",
    "notifications.markAll":    "Mark all",
    "notifications.loading":    "Loading…",
    "notifications.empty.title": "All quiet.",
    "notifications.empty.body":  "When an order updates, cashback posts, or a customer connects to the bot — it'll show up here.",
    "notifications.loadMore":    "Load more",
    "notifications.loadingMore": "Loading…",
    "notifications.bucket.today": "Today",
    "notifications.bucket.week":  "This week",
    "notifications.bucket.older": "Earlier",
    "notifications.row.unit.min":  "min",
    "notifications.row.unit.hour": "h",
    "notifications.type.order": "Order {status}",
    "notifications.type.credit": "+${amt} credited",
    "notifications.type.credit.referral":  "Referral bonus",
    "notifications.type.credit.cashback":  "Order cashback",
    "notifications.type.customerOrdered":  "New order from {name}",
    "notifications.type.paymentRequested": "{name} asks for a payment QR/link",
    "notifications.type.paymentUploaded":  "{name} uploaded payment — review it",
    "notifications.type.customerOrdered.unknown": "customer",
    "notifications.type.forward":  "Forward · {n} {noun}",
    "notifications.type.forward.cards.one":  "card",
    "notifications.type.forward.cards.few":  "cards",
    "notifications.type.forward.cards.many": "cards",
    "notifications.type.forward.ok":   "{n} delivered",
    "notifications.type.forward.none": "none delivered",
    "notifications.type.forward.failed": " · {n} failed",
    "notifications.type.reminder":  "Reminder for {count} customer{suffix}",
    "notifications.type.reminder.suffix.one":  "",
    "notifications.type.reminder.suffix.many": "s",
    "notifications.type.reminder.andMore":  " and more…",
    "notifications.type.botLinked":  "{name} connected to the bot",
    "notifications.type.botLinked.unknown": "A customer",
    "notifications.type.botLinked.body":   "Messages go straight through now",
    "notifications.type.reengage":  "{n} customers are waiting for your attention",
    "notifications.status.confirmed":   "confirmed",
    "notifications.status.received":    "received at warehouse",
    "notifications.status.shipped":     "shipped",
    "notifications.status.delivered":   "delivered",
    "notifications.status.cancelled":   "cancelled",
    "notifications.status.unavailable": "out of stock",

    // ─── Onboarding wizard ─────────────────────────────────────
    "onboarding.step.welcome": "Welcome",
    "onboarding.step.fx":      "Rate",
    "onboarding.step.target":  "Channel",
    "onboarding.step.done":    "Done",
    "onboarding.stepIndicator": "Step {idx} of {total} · {label}",
    "onboarding.loading":    "Loading…",
    "onboarding.skip":       "Skip",
    "onboarding.back":       "Back",
    "onboarding.saving":     "Saving…",
    "onboarding.finish":     "Browse catalog",
    "onboarding.next":       "Next",
    "onboarding.welcome.title": "BuyerGram — your store, 100% yours.",
    "onboarding.welcome.body":  "We curate product cards from US retailers. Pick what you like and forward to your Telegram subscribers under your brand — no BuyerGram logos or watermarks.",
    "onboarding.welcome.foot":  "Two minutes: set your ruble rate, your channel, and you're ready.",
    "onboarding.fx.title":     "What rate do we show rubles at?",
    "onboarding.fx.body":      "All catalog cards are priced in dollars. We'll convert to rubles in the caption you send subscribers. Pick a rate that fits your margin.",
    "onboarding.fx.unit":      "₽ / $1",
    "onboarding.fx.example":   "Example: a $49.99 item shows as ₽{rub}",
    "onboarding.fx.unusualLow":  "Rate is below the usual range (60–200 ₽/$). Double-check.",
    "onboarding.fx.unusualHigh": "Rate is above the usual range (60–200 ₽/$). Double-check.",
    "onboarding.fx.err.positive":  "Rate must be greater than zero",
    "onboarding.target.title": "Where do we send the cards?",
    "onboarding.target.body":  "Point us at your channel — add @Buyergrambot as an admin with posting rights, then paste the chat_id or @handle. The bot can't read your subscribers' messages.",
    "onboarding.target.platform": "Platform",
    "onboarding.target.kind.tg":  "Telegram",
    "onboarding.target.kind.max": "MAX",
    "onboarding.target.chatId":   "chat_id or @channel handle",
    "onboarding.target.chatId.placeholder.tg":  "@my_shop  or  -1001234567890",
    "onboarding.target.chatId.placeholder.max": "MAX channel chat-id",
    "onboarding.target.label":    "Label (optional)",
    "onboarding.target.label.placeholder": "e.g. My main channel",
    "onboarding.target.err.empty":    "Enter chat_id or @channel handle",
    "onboarding.target.err.format":   "That doesn't look like an @handle or chat_id. Try @my_shop or -1001234567890.",
    "onboarding.done.title":  "Ready.",
    "onboarding.done.body":   "Your store's set up. Open the catalog from the sidebar, tick a few cards and tap \"Send\" — we'll push them to your channel with your prices already converted.",
    "onboarding.done.foot":   "Need to tweak something? All settings live under the \"Forwarding\" tab of your store.",

    // ─── Code lookup ───────────────────────────────────────────
    "code.lookup.title":    "Find by BG code",
    "code.lookup.hint":     "Enter the code a customer sent you. We'll find the card in any channel.",
    "code.lookup.placeholder": "e.g. 7F3K9PXY",
    "code.lookup.find":     "Find",
    "code.lookup.finding":  "…",
    "code.lookup.err.format":   "Code must be 4–12 characters (no 0, 1, I, or O).",
    "code.lookup.err.notFound": "Not found. Double-check the code and try again.",
    "code.lookup.err.generic":  "Error",
    "code.lookup.err.noChannel":"This card has no channel",
    "code.lookup.source":   "Source ↗",
    "code.lookup.sourcePremium": "Source in Premium ↗",
    "code.lookup.jump":     "Go to channel",

    // ─── modals.jsx (Group / Channel / Topic creators) ─────────
    "modals.group.title":     "New group",
    "modals.group.name":      "Name",
    "modals.group.name.placeholder": "e.g. Engineering team",
    "modals.group.desc":      "Description",
    "modals.group.desc.placeholder": "What's this group about?",
    "modals.cancel":          "Cancel",
    "modals.group.create":    "Create",
    "modals.topic.title":     "New topic",
    "modals.topic.name":      "Topic name",
    "modals.topic.name.placeholder": "e.g. Announcements",
    "modals.topic.add":       "Add",
    "modals.channel.title":   "New channel",
    "modals.channel.name":    "Name",
    "modals.channel.name.placeholder": "Walmart Deals",
    "modals.channel.slug":    "Slug (URL)",
    "modals.channel.slug.placeholder": "walmart-deals",
    "modals.channel.desc":    "Description",
    "modals.channel.desc.placeholder": "Optional — visible to customers",
    "modals.channel.fee":     "Organizer fee, %",
    "modals.channel.fee.placeholder": "e.g. 15",
    "modals.channel.cashback":         "Customer cashback, % (from organizer-fee margin)",
    "modals.channel.cashback.placeholder": "e.g. 3",
    "modals.channel.create":  "Create",
    "modals.channel.creating":"Creating…",

    // ─── PostModal (admin/manager) ─────────────────────────────
    "post.modal.title":     "New post",
    "post.modal.images":    "Images",
    "post.modal.hint.touch": "plus — add from camera",
    "post.modal.hint.desktop": "⌘V to paste · drag from WhatsApp",
    "post.modal.uploading": "Uploading…",
    "post.modal.add":       "Add",
    "post.modal.photo":     "Photo",
    "post.modal.channel":   "Channel",
    "post.modal.title.field":  "Title",
    "post.modal.title.placeholder": "e.g. DKNY satin dress",
    "post.modal.desc":      "Description (RU)",
    "post.modal.desc.placeholder": "Russian description of the post",
    "post.modal.price":     "Price ($)",
    "post.modal.origPrice": "Original price ($)",
    "post.modal.brand":     "Brand",
    "post.modal.brand.placeholder": "DKNY",
    "post.modal.category":  "Category",
    "post.modal.category.placeholder": "apparel",
    "post.modal.sourceUrl": "Source URL",
    "post.modal.gb.tag":    "Group buy",
    "post.modal.gb.label":  " — collect orders to a goal or deadline, then close",
    "post.modal.gb.target": "Goal (opt.)",
    "post.modal.gb.target.placeholder": "e.g. 30",
    "post.modal.gb.deadline":           "Deadline (opt.)",
    "post.modal.publish":   "Publish now",
    "post.modal.publishHint": " (admins only — managers create drafts)",
    "post.modal.cancel":    "Cancel",
    "post.modal.create":    "Create post",
    "post.modal.creating":  "Creating…",

    // ─── Customer detail (CustomerDetailModal) ─────────────────
    "customer.detail.tab.contact": "Contacts",
    "customer.detail.tab.history": "History",
    "customer.detail.tab.notes":   "Notes",
    "customer.detail.contacts":    "Contacts",
    "customer.detail.tg.label":    "Telegram:",
    "customer.detail.tg.none":     "No Telegram on file",
    "customer.detail.phone.label": "Phone:",
    "customer.detail.phone.none":  "No phone on file",
    "customer.detail.inBaseSince": "In your CRM since {date}",
    "customer.detail.stats.orders":  "Orders",
    "customer.detail.stats.spent":   "Total spent",
    "customer.detail.stats.last":    "Last activity",
    "customer.detail.notes":         "Notes",
    "customer.detail.notes.edit":    "Edit",
    "customer.detail.notes.placeholder": "Clothing size, favorite brands, birthdays — anything that helps you serve this customer better.",
    "customer.detail.notes.help":    "{n}/2000 · Esc to cancel · ⌘↵ to save",
    "customer.detail.notes.cancel":  "Cancel",
    "customer.detail.notes.save":    "Save",
    "customer.detail.notes.saving":  "Saving…",
    "customer.detail.notes.clickToEdit": "Click to edit",
    "customer.detail.notes.add":     "+ Add a note",
    "customer.detail.notes.savedToast": "Note saved",
    "customer.detail.reminders":     "Reminders",
    "customer.detail.reminders.edit":"Edit",
    "customer.detail.reminders.add": "+ Remind me",
    "customer.detail.reminder.in":   "Remind in",
    "customer.detail.reminder.days": "days",
    "customer.detail.reminder.note.placeholder": "Note (optional)",
    "customer.detail.reminder.cancel": "Cancel",
    "customer.detail.reminder.create": "Create",
    "customer.detail.reminder.saving": "Saving…",
    "customer.detail.reminders.empty": "No active reminders.",
    "customer.detail.reminder.overdue":"Overdue ",
    "customer.detail.reminder.done":   "Done",
    "customer.detail.reminder.delete": "Delete",
    "customer.detail.reminder.createdToast": "Reminder created",
    "customer.detail.history":       "Order history",
    "customer.detail.history.loading": "Loading…",
    "customer.detail.history.empty":   "No orders yet. When you place an order for this customer, it shows here.",
    "customer.detail.delete":     "Delete",
    "customer.detail.close":      "Close",
    "customer.detail.edit":       "Edit",
    "customer.detail.reorderUnavailable": "This item is no longer available",
    "customer.detail.bot.connected":  "Connected to the bot · since {date}",
    "customer.detail.bot.invite":     "Invite to the bot",
    "customer.detail.bot.loading":    "…",
    "customer.detail.bot.inviteHint": "Link is valid for 7 days. Each click creates a fresh one.",
    "customer.detail.bot.notConfigured": "Customer-bot isn't configured on this platform yet.",
    "customer.detail.bot.linkCopied":    "Link copied — share it with the customer.",
    "customer.detail.bot.linkError":     "Couldn't create the link",
    "customer.detail.order.untitled":    "(untitled)",
    "customer.detail.order.dash":        "—",
    "customer.detail.reorder":           "Reorder",
    "customer.detail.reorder.loading":   "…",

    // ─── Bulk message modal (in SettingsModal) ─────────────────
    "bulk.title":   "Message customers",
    "bulk.template": "Message template",
    "bulk.template.placeholder": "Hi {имя}! ...",
    "bulk.template.default":     "Hi {имя}! New arrivals in my channel — take a look.",
    "bulk.template.vars":        "Substitutes: {var1} — customer name · {var2} — Telegram handle without @",
    "bulk.summary.bot":          "via bot · one click",
    "bulk.summary.bot.sentOf":   "({sent}/{total} sent)",
    "bulk.summary.manual":       "via t.me links · manually from the list below",
    "bulk.summary.skip":         "without Telegram — skipped",
    "bulk.sending":              "Sending…",
    "bulk.sent":                 "Sent · {n}",
    "bulk.sendViaBot":           "Send via bot · {n}",
    "bulk.openFirst5":           "Open first 5 t.me",
    "bulk.close":                "Close",
    "bulk.list":                 "Recipients via t.me links",
    "bulk.open":                 "Open",
    "bulk.copy":                 "Copy",
    "bulk.copy.title":           "Copy text",
    "bulk.copied":               "Copied",
    "bulk.sentToast":            "Sent via bot: {n}",
    "bulk.failedToast":          "Not delivered: {n}",
    "bulk.errorToast":           "Couldn't send",
    "bulk.openSomeToast":        "Opened the first 5 of {n}. Use the per-row buttons below for the rest.",

    // ─── OrderModal extras ─────────────────────────────────────
    "order.modal.forCustomer":  "For which customer",
    "order.modal.reorderTag":   " · reorder",
    "order.modal.forMe":        "— For me —",
    "order.modal.priceWas":     "was ${amt}",
    "order.modal.priceNow":     " · now ",
    "order.modal.phonePlaceholder": "+7 ...",
    "order.modal.countryDefault":   "Russia",

    // ─── OrdersModal ───────────────────────────────────────────
    "orders.modal.filter.all":   "All",
    "orders.modal.empty":        "No orders yet.",
    "orders.modal.loading":      "Loading…",

    // ─── ChannelFeed extras ────────────────────────────────────
    "channelfeed.menu":          "Menu",
    "channelfeed.search.placeholder": "Search by brand, model, SKU",
    "channelfeed.search.clear":  "Clear search",
    "channelfeed.filter.all":    "All",
    "channelfeed.sort.newest":   "Newest",
    "channelfeed.sort.priceAsc": "Price ↑",
    "channelfeed.sort.priceDesc":"Price ↓",
    "channelfeed.sort.discount": "Discount",
    "channelfeed.view.list":     "List",
    "channelfeed.view.grid":     "Grid",
    "channelfeed.select":        "Select",
    "channelfeed.selectAll":     "Select all",
    "channelfeed.deselectAll":   "Deselect",
    "channelfeed.forwardSelected":  "Forward selected",
    "channelfeed.forwardFiltered":  "Forward all",
    "channelfeed.forwardFilteredN": "Forward all {n} in filter",
    "channelfeed.cancelSelect":   "Cancel",
    "channelfeed.selectedCount":  "Selected: {n}",
    "channelfeed.selectedShort":  "{n} selected",
    "channelfeed.empty.staff":    "No posts in this channel yet. Create the first one.",
    "channelfeed.loadMore":       "Load more",
    "channelfeed.close":          "Close",
    "channelfeed.newPost":        "Post",
    "channelfeed.newPost.title":  "New post",
    "channelfeed.history":        "History",
    "channelfeed.history.title":  "Forward history and source links",
    "channelfeed.findByCode":     "Find by code",
    "channelfeed.findByCode.title": "Find a card by the code your customer mentioned",
    "channelfeed.forward.toggle.title": "Turn on card selection for forwarding",
    "channelfeed.forward.locked.title": "Available after channel approval",
    "channelfeed.forward.locked.label": "🔒 Forwarding",
    "channelfeed.forward.toggle.on":  "Cancel selection",
    "channelfeed.forward.toggle.off": "Select to forward",
    "channelfeed.heroTitle":      "Pick\nof the day",
    "channelfeed.closingSoon.one":  "{n} group buy closes in the next 24h — jump in",
    "channelfeed.closingSoon.many": "{n} group buys close in the next 24h — jump in",
    "channelfeed.filterBar.brand":     "Brand",
    "channelfeed.filterBar.brand.count": "Brand · {n}",
    "channelfeed.filterBar.brand.empty":  "No brands",
    "channelfeed.filterBar.priceMin":  "min",
    "channelfeed.filterBar.priceMax":  "max",
    "channelfeed.filterBar.anyDiscount":  "Any discount",
    "channelfeed.filterBar.fromPct":   "{n}%+",
    "channelfeed.filterBar.sort":      "Sort",
    "channelfeed.filterBar.sort.newest":   "Newest first",
    "channelfeed.filterBar.sort.discountDesc": "Discount ↓",
    "channelfeed.filterBar.sort.priceAsc":  "Price ↑",
    "channelfeed.filterBar.sort.priceDesc": "Price ↓",
    "channelfeed.filterBar.sort.brand":  "Brand A-Z",
    "channelfeed.filterBar.resultOf":  "{n} of {total}",
    "channelfeed.filterBar.clear":     "Clear",
    "channelfeed.noMatch.title":      "Nothing matches the current filters",
    "channelfeed.noMatch.clear":      "Clear filters",
    "channelfeed.priceLocked":        "Price hidden",
    "channelfeed.priceLocked.full":   "🔒 Price hidden",
    "channelfeed.priceLocked.body":   "Finish channel verification to see prices and forward cards to your customers.",
    "channelfeed.code.copyTitle":     "Click to copy",
    "channelfeed.code.copied":        "Code copied",
    "channelfeed.premiumSource":      "Source in Premium ↗",
    "channelfeed.premiumSource.title": "Open Premium",
    "channelfeed.toast.forwardSent":  "Forward sent",
    "channelfeed.gb.outOf":           " of {n}",
    "channelfeed.gb.remainDays":      "{d}d {h}h",
    "channelfeed.gb.remainHours":     "{h}h {m}m",
    "channelfeed.gb.remainMin":       "{m}m",
    "channelfeed.gb.gbLabel":         "GB",
    "channelfeed.gb.targetLabel":     "GB · TARGET",
    "channelfeed.age.justNow":        "just now",
    "channelfeed.age.min":            "{n} min ago",
    "channelfeed.age.hour":           "{n} h ago",
    "channelfeed.age.day":            "{n} d ago",

    // ─── App.jsx misc ──────────────────────────────────────────
    "app.loading":           "Loading…",
    "app.sidebar.home":      "Home",
    "app.sidebar.store":     "Store",
    "app.sidebar.store.title": "My store",
    "app.fab.code":          "Find by code",
    "app.fab.forwards":      "Forwards",
    "app.fab.history":       "History",
    "app.fab.newChat":       "Start a chat",
    "app.fab.newGroup":      "Create group",
    "app.fab.newChannel":    "Create channel",
    "app.fab.newTopic":      "Create topic",
    "app.notif.bell":        "Notifications",
    "app.notif.bell.title":  "Events",
    "app.balance.title":     "Cashback balance",
    "app.help.title":        "Help",
    "app.settings.title":    "Settings",
    "app.admin.title":       "Admin",
    "app.logout.title":      "Log out",
    "app.attach.title":      "Attach image",
    "app.attach.uploading":  "Uploading…",
    "app.attach.imageName":  "Image",
    "app.send.title":        "Send",
    "app.message.placeholder": "Message",
    "app.message.captionPlaceholder": "Caption for the image (optional)",
    "app.message.edited":    "(edited)",
    "app.message.editingCancel": "Cancel",
    "app.message.editingSave": "Save",
    "app.message.menu.edit":   "Edit",
    "app.message.menu.delete": "Delete",
    "app.message.menu.reply":  "Reply",
    "app.message.menu.copy":   "Copy Text",
    "app.message.menu.pin":    "Pin",
    "app.message.menu.unpin":  "Unpin",
    "app.message.copied":      "Text copied",
    "app.message.attachment":  "Attachment",
    "app.message.pinned":      "Pinned message",
    "app.message.replyingTo":  "Reply to {name}",
    "app.reaction.more":       "More emoji",
    "app.compose.emoji":       "Emoji",
    "app.chat.scrollDown":     "Scroll to bottom",
    "app.voice.record":        "Record voice message",
    "app.voice.recording":     "Recording…",
    "app.voice.send":          "Send",
    "app.voice.micDenied":     "Microphone access denied",
    "app.message.deleteConfirm": "Delete this message? This cannot be undone.",
    "app.chatSearch.title":   "Search this chat",
    "app.chatSearch.placeholder": "Search messages…",
    "app.chatSearch.searching": "Searching…",
    "app.chatSearch.empty":   "No matches",
    "app.chatSub.dm":         "direct message",
    "app.chatTitle.unknown":  "Unknown",
    "app.empty.dm.title":     "Say hello",
    "app.empty.dm.body":      "Send the first message to start the conversation.",
    "app.empty.topic.title":  "Quiet here",
    "app.empty.topic.body":   "Be the first to post in this topic.",
    "app.empty.staff.dmsCustomer": "Customers will reach out via “I want this” on a post. You can also message someone directly.",
    "app.empty.staff.dmsCta":  "Start a chat",
    "app.newDm.title":        "New message",
    "app.newDm.empty.customer": "No managers available right now. Try again later.",
    "app.newDm.empty.staff":  "No other users yet. Customers will appear here as they sign up.",
    "app.adminPanel.title":   "Admin Panel",
    "app.quickAdd.title":     "Quick add from warehouse",
    "quickAdd.store":         "Store",
    "quickAdd.register.spec": "Spec voice: \"60 tablets, 1000 mcg each\"",
    "quickAdd.register.editorial": "Editorial voice: materials, silhouette, signature detail",
    "quickAdd.photos":        "Photos",
    "quickAdd.camera":        "Camera",
    "quickAdd.library":       "Library",
    "quickAdd.photos.max":    "Maximum 4 photos",
    "quickAdd.uploading":     "Uploading…",
    "quickAdd.upload.failed": "Upload failed: {err}",
    "quickAdd.brand":         "Brand",
    "quickAdd.title":         "Title / model",
    "quickAdd.priceUsd":      "Price $",
    "quickAdd.msrpUsd":       "MSRP $",
    "quickAdd.itemNumber":    "Costco item #",
    "quickAdd.itemNumber.hint": "From the shelf tag. Lets the coupon book auto-update this price.",
    "quickAdd.upc":           "Barcode (UPC)",
    "quickAdd.upc.hint":      "The product barcode. Backs scan-to-find and dedup.",
    "quickAdd.scan":          "Scan barcode",
    "scan.hint":              "Point the camera at the barcode",
    "scan.cancel":            "Cancel",
    "scan.close":             "Close",
    "scan.starting":          "Starting camera…",
    "scanfind.button":        "Scan to find",
    "scanfind.found":         "{n} found",
    "scanfind.none":          "Not listed yet",
    "scanfind.resultFound":   "{code} · {n} found",
    "scanfind.resultNone":    "{code} · not listed yet",
    "scanfind.clear":         "Clear",
    "scanfind.dupTitle":      "Already listed",
    "scanfind.dupHint":       "Update its price in the Prices tab instead of re-adding.",
    "review.assignGender":    "Assign gender:",
    "review.mens":            "Men's",
    "review.womens":          "Women's",
    "review.kids":            "Kids'",
    "review.moved":           "Moved",
    "edit.title":             "Edit item",
    "edit.channel":           "Channel",
    "edit.source":            "Costco description (English)",
    "edit.source.placeholder": "Paste the product description from Costco to translate",
    "edit.translate":         "Translate",
    "edit.translating":       "Translating…",
    "edit.save":              "Save",
    "edit.saving":            "Saving…",
    "edit.saved":             "Saved",
    "edit.cancel":            "Cancel",
    "edit.descriptionEs":     "Description (ES)",
    "edit.validThrough":      "Sale valid through",
    "edit.savePublish":       "Save & publish",
    "edit.published":         "Published",
    "edit.photos":            "Photos",
    "card.saleEnds":          "Valid through {date}",
    "scan.filled.item":       "Item # filled: {code}",
    "scan.filled.barcode":    "Barcode filled: {code}",
    "scan.err.denied":        "Camera access was denied. Allow the camera in Settings to scan.",
    "scan.err.nocamera":      "No camera found on this device.",
    "scan.err.inuse":         "The camera is in use by another app. Close it and try again.",
    "scan.err.generic":       "Couldn't start the camera. Type the number instead.",
    "quickAdd.descriptionEn": "Description (English) — source",
    "quickAdd.descriptionEn.hint": "English is saved and auto-translated to RU + ES when you save.",
    "quickAdd.description":   "Description (RU)",
    "quickAdd.description.placeholder": "Generate via AI or write your own",
    "quickAdd.generate":      "Generate",
    "quickAdd.generating":    "Thinking…",
    "quickAdd.generate.needFields": "Enter brand or title first",
    "quickAdd.generate.failed": "Generation failed: {err}",
    "quickAdd.notes":         "Personal note",
    "quickAdd.notes.placeholder": "e.g. tag .97, last-stock W234",
    "quickAdd.notes.hint":    "Not published. For your own re-pricing or return-trip memory.",
    "quickAdd.cancel":        "Cancel",
    "quickAdd.publish":       "Publish",
    "worklist.tab.add":       "Add item",
    "worklist.tab.prices":    "Update prices",
    "worklist.all":           "All",
    "worklist.loading":       "Loading…",
    "worklist.empty":         "No warehouse items yet",
    "worklist.saving":        "Saving…",
    "worklist.saved":         "Saved {n}",
    "worklist.savedPartial":  "Saved {ok}, {fail} failed",
    "worklist.saveN":         "Save {n}",
    "worklist.noChanges":     "No changes",
    "worklist.view.all":      "All",
    "worklist.view.toFinish": "To finish",
    "worklist.view.published":"Published",
    "worklist.draft":         "Draft",
    "quickAdd.publishing":    "Publishing…",
    "quickAdd.published":     "Published",
    "quickAdd.publish.failed": "Publish failed: {err}",
    "sidebar.quickAdd":       "From store",
    "app.groups.available":   "Available to Join",
    "app.groups.my":          "My Groups",
    "app.groups.members":     "{n} members",
    "app.groups.topics":      "{n} members · {t} topics",
    "app.groups.join":        "Join",
    "app.groups.leave":       "Leave",
    "app.channels.posts.one":  "{n} post",
    "app.channels.posts.few":  "{n} posts",
    "app.channels.posts.many": "{n} posts",
    "app.channels.cashback":   "{n}% cashback",
    "app.upload.failed":       "Upload failed: {err}",
    "app.tmpl.title":          "Templates",
    "app.tmpl.short":          "Tmpl.",
    "app.tmpl.heading":        "Reply templates",
    "app.tmpl.empty":          "No templates yet — add them in the admin panel.",
    "app.applicationBanner.title": "Application under review",
    "app.applicationBanner.body":  "Prices and forwarding unlock after approval.",
    "app.applicationBanner.open":  "Open",
    "app.applicationBanner.view":  "View",
    "app.applicationBanner.openApp": "Open application",
    "app.applicationBanner.locked.title": "Verify your sales channel",
    "app.applicationBanner.locked.body":  "Post the verification code in your main channel — that's the one step to unlock prices and forwarding.",
    "app.applicationBanner.locked.cta":   "Start",
    "app.applicationBanner.submitted.title": "Application under review",
    "app.applicationBanner.submitted.body":  "We'll review within 12 hours. Prices and forwarding will unlock automatically.",
    "app.applicationBanner.rejected.title":  "Application rejected — needs fixing",
    "app.applicationBanner.rejected.body":   "Open the application, see the reason, and submit a new one.",
    "app.applicationBanner.pending.title":   "Verify your sales channel",
    "app.applicationBanner.pending.body":    "Prices and forwarding are hidden until verified. Takes a few minutes.",
  },
  es: {
    "welcome.title": "Bienvenido a BuyerGram",
    "welcome.customer": "Explora los canales para ver ofertas seleccionadas, o abre los chats para escribirle a un gerente sobre un pedido.",
    "welcome.staff": "Elige un chat o canal en la barra lateral para empezar.",
    "welcome.browseChannels": "Explorar canales",
    "tab.chats": "Chats",
    "tab.channels": "Canales",
    "sidebar.group.brands":     "Marcas",
    "sidebar.group.categories": "Categorías",
    "channelfeed.gender.all":    "Todo",
    "channelfeed.gender.women":  "Mujer",
    "channelfeed.gender.men":    "Hombre",
    "channelfeed.gender.kids":   "Niños",
    "channelfeed.gender.unisex": "Unisex",
    "post.variants.title.one":  "{n} color disponible",
    "post.variants.title.few":  "{n} colores disponibles",
    "post.variants.title.many": "{n} colores disponibles",
    "post.variants.sizes":      "Tallas disponibles:",
    "post.variants.soldOut":    "Agotado",
    "post.variants.lowStock.one":  "Solo queda {n}",
    "post.variants.lowStock.few":  "Solo quedan {n}",
    "post.variants.lowStock.many": "Solo quedan {n}",
    "sidebar.brand.empty":      "(vacío)",
    "sidebar.brand.showEmpty":  "Mostrar marcas vacías",
    "sidebar.brand.hideEmpty":  "Ocultar marcas vacías",
    "tab.groups": "Grupos",
    "tab.admin": "Admin",
    "tab.search": "Buscar",
    "tab.search.noMatchDms": "Sin chats coincidentes",
    "empty.channels.title": "Aún no hay canales",
    "empty.channels.customer": "En los canales compartimos ofertas de productos seleccionadas. Los nuevos aparecerán aquí a medida que se lancen.",
    "empty.dms.title": "Aún no hay conversaciones",
    "empty.dms.customer": "Escríbele a un gerente para iniciar un pedido, o toca «Lo quiero» en cualquier publicación de la pestaña Canales.",
    "empty.dms.cta.customer": "Escribir a un gerente",
    "empty.channelFeed.title": "Aún no hay publicaciones aquí",
    "empty.channelFeed.customer": "Las nuevas ofertas aparecerán aquí en cuanto se publiquen. Suscríbete en Ajustes para recibir avisos.",
    "empty.dm.title": "Saluda",
    "empty.dm.body": "Envía el primer mensaje para iniciar la conversación.",
    "empty.topic.title": "Aún no hay mensajes",
    "empty.topic.body": "Sé el primero en publicar en este tema.",
    "channel.subtitle.posts.one": "canal · {n} publicación",
    "channel.subtitle.posts.few": "canal · {n} publicaciones",
    "channel.subtitle.posts.many": "canal · {n} publicaciones",
    "post.iWantThis": "Lo quiero →",
    "post.getCashbackLink": "Obtener enlace con cashback",
    "post.directLink": "Enlace directo",
    "auth.signInTagline": "Inicia sesión para continuar",
    "auth.hero.login":      "Bienvenido de nuevo.",
    "auth.hero.register":   "Tu comprador personal en EE. UU.",
    "auth.hero.confirm":    "Confirma tu email.",
    "auth.hero.forgot":     "Vamos a recuperar tu contraseña.",
    "auth.hero.reset":      "Elige una nueva contraseña.",
    "auth.tagline.login":    "Un catálogo curado de descuentos de retailers de EE. UU. para revendedores — reenvía a tu audiencia, nosotros nos encargamos del abastecimiento y la entrega.",
    "auth.tagline.register": "BuyerGram no es una tienda. Es una línea directa con la persona que compra productos en EE. UU. y te los envía.",
    "auth.tagline.confirm":  "Te enviamos un código por email. Si no lo ves, revisa la carpeta de spam.",
    "auth.tagline.forgot":   "Ingresa tu email — te enviaremos un enlace para restablecerla.",
    "auth.tagline.reset":    "La contraseña debe tener al menos 12 caracteres.",
    "auth.field.fullName":          "Nombre completo",
    "auth.field.fullName.ph":       "Juan Carlos Pérez",
    "auth.field.nickname":          "Alias",
    "auth.field.nickname.ph":       "juan_perez",
    "auth.field.phone":             "Teléfono",
    "auth.field.phone.ph":          "+52 555 1234567",
    "auth.field.confirmPassword":   "Confirmar contraseña",
    "auth.field.inviteCode":        "Código de invitación · opcional",
    "auth.field.codeFromEmail":     "Código del email",
    "auth.field.newPassword":       "Nueva contraseña",
    "auth.codeSent":                "Código enviado",
    "auth.error.passwordsDontMatch":     "Las contraseñas no coinciden",
    "auth.error.passwordMismatch":       "La contraseña y la confirmación no coinciden.",
    "auth.error.phoneFormat":            "Ingresa el teléfono en formato internacional, p. ej. +525551234567.",
    "auth.error.pickNickname":           "Elige un alias disponible.",
    "auth.error.acceptTerms":            "Por favor acepta la Política de Privacidad y los Términos de Servicio.",
    "auth.info.forgotPlaceholder":       "Esta función todavía no está activa. Escribe a r.dolgalev@gmail.com — la restablezco manualmente en una hora.",
    "auth.info.resetPlaceholder":        "Esta función todavía no está activa. Usa el enlace del email o escribe a un gerente.",
    "auth.btn.justAMoment":      "Un momento…",
    "auth.btn.confirm":          "Confirmar",
    "auth.btn.sendLink":         "Enviar enlace",
    "auth.btn.sending":          "Enviando…",
    "auth.btn.savePassword":     "Guardar contraseña",
    "auth.btn.saving":           "Guardando…",
    "auth.btn.forgotPassword":   "¿Olvidaste tu contraseña?",
    "auth.back.toRegister":   "← Volver al registro",
    "auth.back.toLogin":      "← Volver a iniciar sesión",
    "auth.terms.prefix":   "He leído y acepto la",
    "auth.terms.privacy": "Política de Privacidad",
    "auth.terms.tos":     "Términos de Servicio",
    "auth.terms.and":     "y",
    "auth.registerTagline": "Crea tu cuenta",
    "auth.confirmTagline": "Verifica tu correo",
    "auth.displayName": "Nombre para mostrar",
    "auth.displayName.placeholder": "Tu nombre",
    "auth.email": "Correo",
    "auth.password": "Contraseña",
    "auth.inviteCode": "Código de invitación (opcional)",
    "auth.inviteCode.placeholder": "De un amigo",
    "auth.confirmCode": "Código de confirmación",
    "auth.confirmCode.placeholder": "Ingresa el código de 6 dígitos",
    "auth.confirmCodeSent": "Código de confirmación enviado a",
    "auth.devCode": "Código dev:",
    "auth.confirmEmail": "Confirmar correo",
    "auth.verifying": "Verificando...",
    "auth.back": "← Atrás",
    "auth.createAccount": "Crear cuenta",
    "auth.signIn": "Iniciar sesión",
    "auth.pleaseWait": "Espera un momento...",
    "auth.alreadyHave": "¿Ya tienes una cuenta?",
    "auth.noAccount": "¿No tienes cuenta?",
    "auth.toggleSignIn": "Iniciar sesión",
    "auth.toggleRegister": "Registrarse",
    "auth.captchaRequired": "Por favor completa la verificación",
    "settings.title": "Ajustes",
    "settings.tab.profile": "Perfil",
    "settings.tab.password": "Contraseña",
    "settings.tab.subs": "Suscripciones",
    "settings.displayName": "Nombre para mostrar",
    "settings.email": "Correo",
    "settings.language": "Idioma",
    "settings.save": "Guardar",
    "settings.avatar.hint": "JPG / PNG / WebP — recortado automáticamente en círculo",
    "settings.avatar.upload": "Subir foto",
    "settings.avatar.change": "Cambiar foto",
    "settings.avatar.remove": "Quitar",
    "settings.avatar.uploaded": "Foto actualizada",
    "settings.avatar.removed": "Foto eliminada",
    "settings.saving": "Guardando…",
    "settings.saved": "Guardado",
    "settings.curPassword": "Contraseña actual",
    "settings.newPassword": "Nueva contraseña (12+ caracteres)",
    "settings.changePassword": "Cambiar contraseña",
    "settings.changing": "Cambiando…",
    "settings.passwordChanged": "Contraseña cambiada",
    "settings.subs.help": "Controla qué canales te envían notificaciones por Telegram. La app web muestra todos los canales de todos modos.",
    "settings.subs.empty": "Aún no hay canales.",
    "settings.subs.tgPush": "Push de TG",
    "balance.title": "Saldo de cashback",
    "balance.loading": "Cargando…",
    "balance.available": "Disponible",
    "balance.minRedeem": "Canje mínimo: $5.00",
    "balance.redeem": "Canjear",
    "balance.cancel": "Cancelar",
    "balance.requestRedemption": "Solicitar canje",
    "balance.amount": "Monto ($)",
    "balance.method": "Método",
    "balance.method.orderDiscount": "Aplicar al próximo pedido gestionado",
    "balance.method.amazonGift": "Tarjeta de regalo de Amazon (correo)",
    "balance.method.walmartGift": "Tarjeta de regalo de Walmart (correo)",
    "balance.sendGiftTo": "Enviar tarjeta de regalo a",
    "balance.submit": "Enviar solicitud",
    "balance.submitting": "Enviando…",
    "balance.referral.title": "Tu enlace de referidos",
    "balance.referral.body": "Comparte tu enlace de referidos. Por cada pedido que tu referido reciba correctamente, ganas el 1% del subtotal del artículo (sin contar la comisión de organizador de BuyerGram) en tu saldo. Es tu bono — no reduce su cashback. El cashback solo se puede canjear por bienes y servicios en BuyerGram.",
    "balance.usageHint": "El cashback solo se puede gastar en bienes y servicios dentro de BuyerGram. No se admiten pagos externos ni tarjetas de regalo.",
    "help.title": "Ayuda",
    "help.subtitle": "Respuestas a preguntas frecuentes. ¿No encuentras la tuya? Escríbenos.",
    "help.search": "Buscar preguntas…",
    "help.empty": "Nada coincide. Prueba con otras palabras o contacta a soporte.",
    "help.contact.title": "¿Necesitas hablar con una persona?",
    "help.contact.body": "Si la respuesta no está aquí — escríbenos directamente. Se abre un chat con un miembro del equipo de BuyerGram.",
    "help.contact.cta": "Contactar a soporte",
    "help.contact.opening": "Abriendo el chat…",
    "help.contact.fail": "No se pudo abrir el chat. Inténtalo más tarde o escribe a support@buyergram.app.",
    "help.cat.start": "Primeros pasos",
    "help.cat.catalog": "Catálogo y reenvío",
    "help.cat.orders": "Pedidos y cashback",
    "help.cat.curated": "Compras seleccionadas",
    "help.cat.account": "Cuenta y acceso",
    "help.cat.trouble": "Solución de problemas",
    "help.q.what": "¿Qué es BuyerGram?",
    "help.a.what": "BuyerGram es un catálogo B2B cerrado para revendedores. Agregamos artículos de grandes retailers de EE. UU. (Nordstrom Rack, Walmart, otros), los compramos y los enviamos a Rusia. Tú — el revendedor — reenvías nuestras tarjetas de producto a tu audiencia en Telegram/MAX/donde sea con tu propio margen; nosotros nos encargamos del pedido y te pagamos cashback.",
    "help.q.start": "¿Por dónde empiezo después de la aprobación?",
    "help.a.start": "1) Abre Mi tienda → Ajustes de reenvío y conecta tu canal de Telegram o MAX. 2) Explora cualquier canal del catálogo (p. ej. Curated Shoes), elige una tarjeta y pulsa Reenviar — va a tu canal con tu pie de foto y tu margen. 3) Cuando un cliente quiera comprar, realiza el pedido desde Mi tienda → Clientes o pídele que te contacte.",
    "help.q.commission": "¿Qué cobra BuyerGram?",
    "help.a.commission": "La comisión de organizador de BuyerGram se suma al precio del artículo y se muestra como una línea aparte en el pedido. Tu margen es el que tú le pongas a tu cliente; no nos quedamos con nada de eso.",
    "help.q.forward": "¿Cómo reenvío una tarjeta?",
    "help.a.forward": "Cada tarjeta tiene un botón de Reenviar. La primera vez que lo uses configurarás a dónde va (un canal de Telegram, un chat de MAX o varios destinos). Antes de enviarla, puedes editar el pie de foto y revisar la vista previa — tu cliente ve tu margen, sin ninguna mención de BuyerGram.",
    "help.q.markup": "¿Cómo configuro mi margen?",
    "help.a.markup": "Mi tienda → Ajustes de reenvío: define tu tasa USD→RUB y un porcentaje de margen. La tasa base sale de la última actualización del CBR; tú le sumas tu margen encima. La vista previa del reenvío muestra el precio final en rublos que verá tu cliente.",
    "help.q.bgcode": "¿Qué es el código BG en las tarjetas?",
    "help.a.bgcode": "Es un ID interno de producto de BuyerGram (p. ej. BG-A4F2K9). Úsalo cuando un cliente quiera aclarar o volver a pedir algo: la URL del retailer original solo se ve en el plan Premium, pero el código BG funciona en todas partes.",
    "help.q.search": "¿Cómo encuentro un producto por código?",
    "help.a.search": "Cada canal tiene una barra de búsqueda arriba. Ingresa el código BG o cualquier parte del título — encontrarás la tarjeta aunque esté en otro canal.",
    "help.q.order": "¿Cómo hago un pedido para un cliente?",
    "help.a.order": "Mi tienda → Clientes → abre el cliente → Nuevo pedido, agrega artículos por código BG o por enlace. Nosotros compramos y enviamos; el estado pasa por pagado → comprado → enviado → entregado.",
    "help.q.cashback": "¿Cuándo recibo el cashback?",
    "help.a.cashback": "El cashback se acredita automáticamente cuando el pedido llega a Entregado. La tasa es un porcentaje del subtotal del artículo (sin contar la comisión de organizador de BuyerGram). Visible en tu saldo de cashback.",
    "help.q.cashbackUse": "¿En qué puedo gastar el cashback?",
    "help.a.cashbackUse": "Solo en futuros pedidos de BuyerGram. No hay pagos externos ni canje por tarjetas de regalo — es una política de la plataforma.",
    "help.q.referral": "¿Qué es el enlace de referidos?",
    "help.a.referral": "Compártelo con otro revendedor. Cuando se registre y sea aprobado, cada uno de sus pedidos que llegue a Entregado te acredita el 1% del subtotal del artículo (sin contar la comisión de organizador). Es tu bono — no reduce su cashback.",
    "help.q.curated": "¿Qué son las compras seleccionadas?",
    "help.a.curated": "Es un servicio de concierge: compramos artículos en tu nombre, incluso los que no están en el catálogo. Nos envías enlaces o nos das acceso a tu cuenta en un retailer; nosotros lo conseguimos, te facturamos en USDT/USDC/RUB y lo entregamos.",
    "help.q.curated.start": "¿Cómo abro una solicitud de compra seleccionada?",
    "help.a.curated.start": "Mi tienda → Seleccionadas → Nueva solicitud. Elige el tipo (enlaces de productos o acceso a cuenta), indícanos la cantidad de artículos y el total estimado, elige la moneda de facturación. Confirmamos el precio exacto, emitimos la factura, lo conseguimos y lo enviamos.",
    "help.q.curated.status": "¿Cuáles son los estados?",
    "help.a.curated.status": "Enviada → Cotizada (confirmamos el costo) → Facturada → Pagada → Comprada → Recibida → Enviada → Completada. Puedes cancelar durante los dos primeros pasos.",
    "help.q.application": "¿Por qué los precios están ocultos?",
    "help.a.application": "El precio y el botón de Reenviar se desbloquean después de que tu solicitud sea aprobada. Mientras el estado sea Enviada o Rechazada, las tarjetas son visibles pero los precios están enmascarados. Esto protege los márgenes de los revendedores ya aprobados.",
    "help.q.approve": "¿Cuánto tarda la aprobación?",
    "help.a.approve": "Normalmente 1–2 días hábiles. Si tu solicitud es rechazada verás el motivo en el banner de inicio y podrás volver a enviarla con una nueva prueba de propiedad del canal.",
    "help.q.lang": "¿Cómo cambio el idioma?",
    "help.a.lang": "Ajustes → Perfil → Idioma. El cambio es instantáneo; todo se actualiza.",
    "help.q.nick": "¿Cómo cambio mi alias?",
    "help.a.nick": "Ajustes → Perfil → Alias. Los alias deben ser únicos. Tu nombre completo solo lo ven tú y el administrador — el resto de revendedores y clientes solo ven @alias.",
    "help.q.logout": "¿Cómo cierro sesión?",
    "help.a.logout": "El icono de cerrar sesión en la esquina superior derecha de la barra lateral. En iPhone la página se recarga al cerrar sesión — es normal.",
    "help.q.trouble.notif": "No recibo notificaciones.",
    "help.a.trouble.notif": "Abre Ajustes → Notificaciones y revisa que la suscripción push esté activa. En iPhone, las push funcionan solo si la PWA está añadida a la pantalla de inicio. Si el bot de Telegram está en silencio, asegúrate de no haberle enviado /stop.",
    "help.q.trouble.forward": "No puedo reenviar una tarjeta.",
    "help.a.trouble.forward": "Asegúrate de tener al menos un canal vinculado en Ajustes de reenvío y de que el bot de BuyerGram tenga permiso para publicar. Si quitaste el bot, el botón muestra un estado vacío pidiéndote que lo vuelvas a agregar.",
    "help.q.trouble.pwa": "Algo no funciona / pantalla en blanco.",
    "help.a.trouble.pwa": "Suele ser caché obsoleta en iPhone. Cierra la pestaña y vuelve a abrirla. Si no funciona, Ajustes de iOS → Safari → Borrar historial y datos de sitios web. En macOS/Windows, Ctrl/Cmd+Shift+R para forzar la recarga.",
    "help.q.trouble.ru": "El sitio va lento desde Rusia.",
    "help.a.trouble.ru": "Alojamos en Europa (OVH, Francia) y deliberadamente no usamos Cloudflare — para evitar la limitación por parte de RKN. Si sigue lento puede ser tu proveedor local; alternativamente instala nuestra PWA en la pantalla de inicio, va más rápido.",
    "balance.copy": "Copiar",
    "balance.copied": "¡Copiado!",
    "balance.telegram.title": "Entrega por Telegram",
    "balance.telegram.linked": "Vinculado a",
    "balance.telegram.note": "Recibirás las nuevas publicaciones por Telegram incluso cuando la app web no esté accesible.",
    "balance.telegram.unlink": "Desvincular",
    "balance.telegram.unlinkConfirm": "¿Desvincular Telegram? Dejarás de recibir mensajes del bot.",
    "balance.telegram.openBot": "Abrir",
    "balance.telegram.thenSend": "en Telegram y envía:",
    "balance.telegram.codeExpires": "El código vence en {n} minutos. Vuelve a abrir este diálogo para generar uno nuevo.",
    "balance.telegram.unsetHelp": "Vincula tu Telegram para seguir recibiendo las nuevas ofertas cuando la app web esté limitada. Canal de entrega de último recurso para conexiones poco fiables.",
    "balance.telegram.getCode": "Obtener código de vinculación",
    "balance.pendingRedemptions": "Canjes pendientes",
    "balance.requestedOn": "solicitado",
    "balance.recentLedger": "Movimientos recientes",
    "balance.ledger.empty": "Aún sin actividad. Haz clic en «Obtener enlace con cashback» en cualquier publicación de producto y completa una compra — el crédito se acredita entre 1 y 60 días después de que el comercio confirme la venta.",
    "balance.recentTransactions": "Transacciones recientes",
    "balance.transactions.empty": "Aún no hay transacciones.",
    "balance.sale": "venta",
    "common.cancel": "Cancelar",
    "common.ok": "OK",
    "common.loading": "Cargando…",
    "common.confirm": "Confirmar",
    "intro.kicker": "De los editores",
    "intro.title": "Bienvenido a BuyerGram",
    "intro.lead": "Eliges artículos de EE. UU. con descuento y los reenvías a tu canal — tus clientes ven tu precio en rublos con margen.",
    "intro.step1": "Define tu propia tasa $→₽. Convertimos automáticamente el precio de cada tarjeta a rublos usándola.",
    "intro.step2": "Agrega a @Buyergrambot como administrador en tu canal de Telegram para que pueda publicar tarjetas allí.",
    "intro.step3": "Abre cualquier canal, elige las tarjetas que te gusten y toca «Reenviar» — las enviaremos a tu Telegram automáticamente.",
    "intro.cta": "Terminar configuración",
    "intro.later": "Más tarde",
    "intro.gotIt": "Entendido",
    "setup.banner.aria": "Terminar la configuración de BuyerGram",
    "setup.banner.both": "Termina la configuración para empezar a reenviar tarjetas.",
    "setup.banner.needFx": "Queda un paso: define tu tasa $→₽.",
    "setup.banner.needTarget": "Queda un paso: agrega un canal de Telegram al que reenviar.",
    "setup.banner.detail": "Sin esto, el botón Reenviar no puede entregar tarjetas a tu canal.",
    "setup.banner.cta": "Configurar",
    "altcha.loading": "Preparando verificación…",
    "altcha.solving": "Verificando navegador…",
    "altcha.ready": "Verificado",
    "altcha.error": "Falló la verificación",
    "post.order": "Pedido",
    "order.modal.title": "Nuevo pedido",
    "order.variant": "Variante (talla/color/concentración)",
    "order.variant.placeholder": "p. ej. M, azul · 1000IU x60 · 256GB Negro",
    "order.qty": "Cantidad",
    "order.notes": "Notas",
    "order.notes.placeholder": "Pedidos especiales, dirección de envío, etc.",
    "order.total": "Total",
    "order.place": "Realizar pedido",
    "order.placing": "Realizando…",
    "order.placed.ok": "Pedido realizado. Te contacto en breve.",
    "cart.title": "Carrito",
    "cart.add": "Añadir al carrito",
    "cart.added": "Añadido al carrito",
    "cart.remove": "Quitar",
    "cart.each": "c/u",
    "cart.empty": "El carrito está vacío",
    "cart.emptyBody": "Tu carrito está vacío. Abre un canal y añade artículos.",
    "cart.checkout": "Realizar pedido",
    "cart.subtotalN": "Artículos ({n})",
    "cart.fee": "Comisión de servicio",
    "cart.cashback": "Reembolso",
    "cart.placed": "Pedido №{num} realizado ({n} art.)",
    "cart.placedSomeSkipped": "Pedido №{num}: {ok} realizados, {skipped} omitidos (no disponibles)",
    "orders.group.items": "{n} art. en el pedido",
    "cart.rublePrice": "Precio ₽",
    "cart.fxSuggested": "a {rate} ₽/$",
    "order.rublePrice.title": "Precio para mi cliente (₽)",
    "order.rublePrice.label": "Mi precio",
    "order.rublePrice.unset": "sin definir",
    "order.rublePrice.set": "definir",
    "order.rublePrice.saved": "Precio guardado",
    "order.rublePrice.invalid": "Precio inválido",
    "invoice.cta": "Factura",
    "invoice.title": "Factura",
    "invoice.from": "De",
    "invoice.to": "Cliente",
    "invoice.item": "Artículo",
    "invoice.qty": "Cant.",
    "invoice.price": "Precio",
    "invoice.total": "Total",
    "invoice.copy": "Copiar texto",
    "invoice.pdf": "Descargar PDF",
    "invoice.copied": "Factura copiada",
    "invoice.copyFail": "No se pudo copiar",
    "invoice.popupBlocked": "Permite ventanas emergentes para el PDF",
    "mystore.tab.batches": "Compras",
    "admin.tab.batches": "Compras",
    "batch.heading": "Lotes de compra",
    "batch.new": "Nuevo lote",
    "batch.empty": "Aún no hay lotes. Crea uno para agrupar pedidos de la misma tienda.",
    "batch.unnamed": "Sin título",
    "batch.itemsN": "{n} art.",
    "batch.retailer": "Tienda",
    "batch.retailer.ph": "p. ej. Nordstrom Rack",
    "batch.supplierRef": "N.º de pedido al proveedor",
    "batch.supplierRef.ph": "p. ej. enviado para compra",
    "batch.tariff": "Tarifa ₽/kg",
    "batch.statusLabel": "Estado",
    "batch.status.open": "Abierto",
    "batch.status.ordered": "Pedido",
    "batch.status.shipped": "Enviado",
    "batch.status.closed": "Cerrado",
    "batch.status.cancelled": "Cancelado",
    "batch.noMembers": "Aún no hay artículos en este lote.",
    "batch.kg": "kg",
    "batch.goods": "Productos",
    "batch.delivery": "Envío",
    "batch.grand": "Total",
    "batch.noCandidates": "No hay pedidos elegibles para añadir.",
    "batch.addSelected": "Añadir ({n})",
    "batch.addOrders": "Añadir pedidos",
    "batch.delete": "Eliminar lote",
    "batch.delete.confirm": "¿Eliminar este lote? Los pedidos se conservan, pero salen del lote.",
    "orders.mine.title": "Mis pedidos",
    "orders.mine.empty": "Aún no hay pedidos. Abre un canal y toca \"Pedido\" debajo de un producto.",
    "orders.deletedPost": "(publicación eliminada)",
    "order.status.placed": "Realizado",
    "order.status.confirmed": "Confirmado",
    "order.status.paid": "Pagado",
    "order.status.purchased": "Comprado",
    "order.status.received": "En almacén",
    "order.status.shipped": "Enviado",
    "order.status.delivered": "Entregado",
    "order.status.cancelled": "Cancelado",
    "order.status.unavailable": "Agotado",
    "order.fulfilled.of": "completados {fulfilled} de {total}",
    "order.shipping.title": "Destinatario y dirección de envío",
    "order.recipient.name": "Nombre del destinatario",
    "order.recipient.name.placeholder": "Quién recibe el paquete",
    "order.recipient.phone": "Teléfono del destinatario",
    "order.address": "Dirección",
    "order.address.placeholder": "Calle, edificio, depto, ciudad, código postal",
    "order.country": "País",
    "order.recipient.required": "El nombre del destinatario es obligatorio",
    "order.address.required": "La dirección de envío es obligatoria",
    "order.address.source.label": "Dirección de envío",
    "order.address.source.my": "Mi dirección",
    "order.address.source.customer": "Dirección del cliente",
    "order.address.source.manual": "Ingresar manualmente",
    "order.address.empty.my": "Aún no hay direcciones guardadas. Completa los campos abajo — te ofrezco guardarla después de realizar el pedido.",
    "order.address.empty.customer": "Este cliente aún no tiene direcciones guardadas.",
    "order.address.empty.noCustomer": "Primero elige un cliente arriba del formulario.",
    "order.address.savePrompt.title": "¿Guardar esta dirección?",
    "order.address.savePrompt.bodyMy": "La agrego a tu libreta de direcciones — la próxima vez la eliges con un clic.",
    "order.address.savePrompt.bodyCustomer": "La agrego al cliente “{name}” — la próxima vez aparecerá en el selector.",
    "order.address.savePrompt.label": "Etiqueta (opcional)",
    "order.address.savePrompt.labelPh": "Casa, Oficina, Mamá…",
    "order.address.savePrompt.save": "Guardar",
    "order.address.savePrompt.skip": "No guardar",
    "order.address.savePrompt.saved": "Dirección guardada",
    "order.address.savePrompt.saveFail": "No se pudo guardar la dirección",
    "order.address.picker.default": "predeterminada",
    "order.address.picker.primary": "principal",
    "settings.tab.addresses": "Libreta de direcciones",
    "settings.addresses.title": "Mis direcciones",
    "settings.addresses.subtitle": "Guarda las direcciones a las que envías seguido. Aparecerán en el selector al realizar un pedido.",
    "settings.addresses.empty": "Aún no hay direcciones. Agrega la primera abajo.",
    "settings.addresses.add": "Agregar dirección",
    "settings.addresses.label": "Etiqueta",
    "settings.addresses.labelPh": "Casa, Oficina, Mamá…",
    "settings.addresses.recipient": "Nombre del destinatario",
    "settings.addresses.phone": "Teléfono",
    "settings.addresses.address": "Dirección",
    "settings.addresses.country": "País",
    "settings.addresses.default": "predeterminada",
    "settings.addresses.setDefault": "Marcar como predeterminada",
    "settings.addresses.edit": "Editar",
    "settings.addresses.delete": "Eliminar",
    "settings.addresses.save": "Guardar",
    "settings.addresses.cancel": "Cancelar",
    "settings.addresses.deleteConfirm": "¿Eliminar esta dirección de tu libreta?",
    "customer.addresses.title": "Direcciones",
    "customer.addresses.empty": "Aún no hay direcciones. Agrega la primera abajo.",
    "customer.addresses.add": "Agregar dirección",
    "customer.addresses.primary": "principal",
    "customer.addresses.setPrimary": "Marcar como principal",
    "customer.addresses.deleteConfirm": "¿Eliminar esta dirección?",
    "orders.tab.orders": "Pedidos",
    "orders.tab.shipments": "Envíos",
    "orders.consolidate.hint": "Selecciona artículos en el almacén para agruparlos en un solo envío",
    "orders.consolidate.selected": "Seleccionados: {n}",
    "orders.consolidate.cta": "Consolidar",
    "shipment.label": "Envío",
    "shipment.itemsCount": "artículos",
    "shipment.status.building": "Armando",
    "shipment.status.ready": "Listo para enviar",
    "shipment.status.shipped": "Enviado",
    "shipment.status.delivered": "Entregado",
    "shipment.status.cancelled": "Cancelado",
    "shipment.ready.cta": "Listo, por favor envíalo",
    "shipment.ready.ok": "Marcado como listo",
    "shipment.ready.waiting": "Tranqui — tu comprador lo envía y agrega el número de seguimiento.",
    "shipment.cancel": "Cancelar",
    "shipment.cancel.confirm": "¿Cancelar este envío? Los pedidos vuelven a la lista para que armes otro paquete.",
    "shipment.cancelled.ok": "Envío cancelado; pedidos liberados.",
    "shipment.created.ok": "Envío creado. Abre la pestaña Envíos para ver o editar.",
    "shipments.mine.empty": "Aún no hay envíos. Cuando tus pedidos lleguen al almacén, agrúpalos en la pestaña Pedidos.",
    "common.copied": "Copiado",
    "order.payment.cta": "Pagar este pedido",
    "order.payment.requestQr":      "Solicitar un código QR o enlace",
    "order.payment.requestQr.note": "Abre el chat con el vendedor — te enviará un QR o enlace de pago",
    "order.payment.orPayDirect":    "o paga directamente",
    "order.payment.requestSent":    "Solicitud enviada al chat",
    "order.payment.usdt": "USDT (TRC20)",
    "order.payment.sbp": "SBP (por teléfono)",
    "order.payment.card": "Transferencia con tarjeta",
    "order.payment.amountDue": "Monto a pagar",
    "order.payment.noMethods": "No hay métodos de pago configurados — escríbele al gerente por DM.",
    "order.payment.pickMethodFirst": "Primero elige un método de pago.",
    "order.payment.attachScreenshot": "Adjuntar captura del pago",
    "order.payment.uploadHint": "PNG/JPG, hasta 5 MB",
    "order.payment.uploaded.ok": "Captura subida; el gerente la revisará en breve.",
    "order.payment.awaitingReview": "Pago recibido, esperando confirmación del gerente.",
    "order.payment.viewScreenshot": "Captura",
    "post.locked": "Compra grupal cerrada",
    "post.gb.locked": "Compra grupal cerrada",
    "post.gb.open": "Compra conjunta abierta",
    "post.gb.expired": "Tiempo agotado",
    "post.gb.committed": "Comprometidos: {n}",
    "common.edit": "editar",
    "common.save": "Guardar",
    "shipment.customs.declared": "Valor declarado",
    "shipment.customs.overThreshold": "⚠ Supera los 200 € — aplica arancel aduanero. Divide el envío o baja los valores declarados.",
    "shipment.customs.declaredItem": "Declarado",
    "shipment.customs.usingPrice": "= precio de compra",
    "shipment.customs.saved": "Valor declarado guardado",
    "shipment.customs.invalidValue": "Ingresa un número ≥ 0",
    "order.subtotal": "Artículos",
    "order.facilitate.title":    "Compra",
    "order.facilitate.byUs":     "La compramos",
    "order.facilitate.self":     "La pido yo",
    "order.facilitate.selfNote": "Tú haces el pedido — sin nuestra comisión. BuyerGram solo registra el pedido.",
    "order.organizerFee": "Comisión del organizador ({pct}%)",
    "order.cashback.pending": "+${amt} de cashback ({pct}%) al entregar",
    "order.cashback.earned": "✓ +${amt} de cashback acreditado",
    "order.cashback.preview": "{pct}% de cashback al entregar",
    "channel.cashbackBadge": "{pct}% cashback",
    "admin.tab.overview": "Resumen",
    "admin.tab.applications": "Solicitudes",
    "admin.tab.curated": "Compras curadas",
    "admin.tab.subscriptions": "Suscripciones",
    "admin.tab.orders": "Pedidos",
    "admin.tab.shipments": "Envíos",
    "admin.tab.settings": "Ajustes",
    "admin.sub.payments": "Pagos / FX",
    "admin.sub.channels": "Canales",
    "admin.sub.users": "Usuarios",
    "admin.sub.groups": "Grupos y Temas",
    "admin.sub.wa": "WhatsApp",
    "admin.sub.tokens": "Tokens de servicio",
    "admin.sub.templates": "Plantillas de DM",
    "admin.sub.redemptions": "Canjes",
    "admin.sub.audit": "Registro de auditoría",
    "admin.home.kicker": "ADMINISTRACIÓN",
    "admin.home.greeting.night": "Buenas noches",
    "admin.home.greeting.morning": "Buenos días",
    "admin.home.greeting.day": "Buen día",
    "admin.home.greeting.evening": "Buenas tardes",
    "admin.home.subtitle": "Colas que necesitan tu atención. Toca una tarjeta para abrir.",
    "admin.home.loading": "Cargando…",
    "admin.home.card.applications.label": "Solicitudes por revisar",
    "admin.home.card.applications.empty": "Cola vacía",
    "admin.home.card.applications.hint": "Aprobar o rechazar",
    "admin.home.card.curated.label": "Compras curadas activas",
    "admin.home.card.curated.empty": "Todas cerradas",
    "admin.home.card.curated.hint": "{n0} nuevas · {n1} esperando pago",
    "admin.home.card.subscriptions.label": "Suscripciones",
    "admin.home.card.subscriptions.hint": "Ir a la cola",
    "admin.home.bucket.submitted": "Nuevas",
    "admin.home.bucket.priced": "Cotizadas",
    "admin.home.bucket.purchased": "Compradas",
    "admin.home.bucket.invoiced": "Esperando pago",
    "admin.home.bucket.paid": "Pagadas",
    "admin.home.recent.apps": "Solicitudes recientes",
    "admin.home.recent.curated": "Compras curadas recientes",
    "admin.home.recent.openAll": "Abrir todo →",
    "admin.home.channelLink": "Canal →",
    "admin.home.timeAgo.now": "ahora mismo",
    "admin.home.timeAgo.min": "hace {n} min",
    "admin.home.timeAgo.hour": "hace {n} h",
    "admin.home.timeAgo.day": "hace {n} d",
    "admin.home.items.one": "artículo",
    "admin.home.items.few": "artículos",
    "admin.home.items.many": "artículos",
    "settings.avatar.label": "Foto de perfil",
    "settings.nickname.public": "Alias (público)",
    "settings.nickname.publicHint": "Esto es lo que ven otros usuarios de BuyerGram. Para cambiarlo, contacta a soporte.",
    "settings.nickname.claim.title": "Elige un alias público",
    "settings.nickname.claim.hint": "Ahora mismo los demás te ven como @{nick} — un marcador. Asegura un alias real; una vez fijado, solo soporte puede cambiarlo.",
    "settings.nickname.claim.placeholder": "mi_alias",
    "settings.nickname.claim.cta": "Asegurar alias",
    "settings.nickname.err.reserved": "Este alias está reservado.",
    "settings.nickname.err.taken": "Este alias ya está en uso.",
    "settings.nickname.err.format": "3-30 caracteres: a-z, 0-9, guion bajo.",
    "settings.nickname.claimed.ok": "Alias @{nick} asegurado.",
    "settings.fullName.label": "Nombre completo (privado)",
    "settings.language.ru": "Ruso",
    "settings.language.en": "Inglés",
    "settings.language.es": "Español",
    "settings.subs.posts.one": "publicación",
    "settings.subs.posts.few": "publicaciones",
    "settings.subs.posts.many": "publicaciones",
    "sidebar.empty.channels.admin": "Los canales son feeds curados de productos. Crea el primero en Ajustes → Canales.",
    "sidebar.empty.groups.title": "Aún no hay grupos",
    "sidebar.empty.groups.admin": "Los grupos son espacios privados para tu equipo. Crea uno en Ajustes → Grupos y Temas.",
    "sidebar.empty.groups.customer": "Aún no te han agregado a ningún grupo. Un administrador puede agregarte.",
    "admin.orders.filter.all": "Todos los estados",
    "admin.orders.status.placed": "realizado",
    "admin.orders.status.confirmed": "confirmado",
    "admin.orders.status.paid": "pagado",
    "admin.orders.status.purchased": "comprado",
    "admin.orders.status.received": "en almacén",
    "admin.orders.status.shipped": "enviado",
    "admin.orders.status.delivered": "entregado",
    "admin.orders.status.cancelled": "cancelado",
    "admin.orders.status.unavailable": "no disponible",
    "admin.ships.status.building": "armando",
    "admin.ships.status.ready": "listo",
    "admin.ships.status.shipped": "enviado",
    "admin.ships.status.delivered": "entregado",
    "admin.ships.status.cancelled": "cancelado",
    "admin.orders.refresh": "Recargar",
    "admin.orders.refreshing": "Cargando…",
    "admin.orders.empty": "Ningún pedido coincide con este filtro.",
    "admin.orders.deletedPost": "(publicación eliminada)",
    "admin.orders.qtyShort": "cant. {n}",
    "admin.orders.source":   "Comprar",
    "admin.orders.itemNo":   "Art. #",
    "admin.orders.chat":     "Chatear con cliente",
    "admin.orders.chatOpened": "Chat abierto — ver «Mensajes»",
    "admin.orders.fulfilledShort": "(cumplidos {n})",
    "admin.orders.paymentBadge.needReview": "✓ Requiere revisión",
    "admin.orders.paymentBadge.uploaded": "✓ Comprobante subido",
    "admin.orders.paymentBadge.open": "Abrir →",
    "admin.orders.cancel.placeholder": "¿Por qué cancelar este pedido?",
    "admin.orders.cancel.reasonTitle": "¿Motivo de cancelación?",
    "admin.orders.unavailable.placeholder": "¿Qué artículo / tienda / por qué?",
    "admin.orders.unavailable.reasonTitle": "¿Motivo de no disponible?",
    "admin.orders.received.ok": "Marcado como recibido — agrega una foto abajo para el cliente.",
    "admin.orders.purchased.prompt": "¿Cuántos de {qty} compraste? (0 = no disponible)",
    "admin.orders.purchased.error": "Ingresa un número entre 0 y {qty}",
    "admin.orders.reasonLabel": "Motivo:",
    "admin.orders.receipt.title": "Fotos del comprobante ({n})",
    "admin.orders.receipt.add": "+ foto",
    "admin.orders.receipt.added": "Se agregaron {n} foto(s)",
    "admin.orders.receipt.uploading": "…",
    "admin.apps.filter.submitted": "Esperando revisión",
    "admin.apps.filter.pending": "Aún no enviada",
    "admin.apps.filter.approved": "Aprobada",
    "admin.apps.filter.rejected": "Rechazada",
    "admin.apps.refresh": "Actualizar",
    "admin.apps.count.submitted": "esperando decisión",
    "admin.apps.count.other": "en este grupo",
    "admin.apps.empty": "La cola está vacía.",
    "admin.apps.submittedAt": "Enviada {when}",
    "admin.apps.verificationCode": "código de verificación",
    "admin.apps.field.channel": "Canal",
    "admin.apps.field.platform": "Plataforma",
    "admin.apps.field.audience": "Audiencia",
    "admin.apps.field.categories": "Categorías",
    "admin.apps.field.contact": "Contacto",
    "admin.apps.field.verificationPost": "Publicación de verificación",
    "admin.apps.rejectReason": "Motivo de rechazo:",
    "admin.apps.approve": "Aprobar",
    "admin.apps.approved.ok": "Aprobada",
    "admin.apps.reject": "Rechazar…",
    "admin.apps.reject.confirm": "Confirmar rechazo",
    "admin.apps.reject.cancel": "Cancelar",
    "admin.apps.reject.placeholder": "Motivo de rechazo (obligatorio)",
    "admin.apps.reject.required": "Motivo de rechazo obligatorio",
    "admin.apps.reject.ok": "Rechazada",
    "admin.subs.hint": "Solicitudes de suscripción dentro de la app. Aprueba una vez que el pago haya sido confirmado por fuera; el rechazo permite al usuario reenviar la solicitud. Al aprobar se extiende automáticamente el plazo premium del usuario y se fija su tier.",
    "admin.subs.empty": "Aún no hay pedidos de suscripción.",
    "admin.subs.period.annual": "anual",
    "admin.subs.period.monthly": "mensual",
    "admin.subs.adminPrefix": "Admin:",
    "admin.subs.filed": "Registrado {when}",
    "admin.subs.decided": " · decidido {when}",
    "admin.subs.approve": "Aprobar",
    "admin.subs.reject": "Rechazar",
    "admin.subs.approve.confirm": "¿Aprobar {tier} {period} para \"{user}\"?",
    "admin.subs.approve.placeholder": "Nota opcional de admin (p. ej. txn de pago)",
    "admin.subs.approve.ok": "Aprobado",
    "admin.subs.reject.confirm": "¿Rechazar este pedido? El motivo será visible para el usuario.",
    "admin.subs.reject.placeholder": "Motivo (p. ej. pago no recibido)",
    "admin.subs.reject.ok": "Rechazado",
    "admin.ships.filter.all": "Todos los estados",
    "admin.ships.refresh": "Recargar",
    "admin.ships.refreshing": "Cargando…",
    "admin.ships.empty": "Ningún envío coincide.",
    "admin.ships.deletedItem": "(eliminado)",
    "admin.ships.itemsCount": "{n} artículo(s):",
    "admin.ships.itemQty": "cant.",
    "admin.ships.customs.over": "⚠ Aduana:",
    "admin.ships.customs.normal": "Declarado:",
    "admin.ships.printPickList": "🖨 Imprimir lista de picking / albarán",
    "admin.ships.tracking.carrier": "Transportista",
    "admin.ships.tracking.number": "N.º de seguimiento",
    "admin.ships.tracking.weight": "kg",
    "admin.ships.tracking.url": "URL de seguimiento (opcional)",
    "admin.ships.tracking.save": "Guardar seguimiento",
    "admin.ships.tracking.saving": "Guardando…",
    "admin.ships.tracking.saved": "Seguimiento guardado",
    "admin.ships.tracking.required": "Primero hace falta el número de seguimiento",
    "admin.ships.markShipped": "Marcar como enviado",
    "admin.ships.markDelivered": "Marcar como entregado",
    "admin.ships.markShipped.confirm": "¿Marcar el envío #{id} como enviado? Los {n} pedido(s) pasarán al estado enviado.",
    "admin.ships.markShipped.ok": "Enviado",
    "admin.ships.markDelivered.confirm": "¿Marcar el envío #{id} como entregado?",
    "admin.ships.markDelivered.ok": "Entregado",
    "admin.fx.hint": "Tasa de cambio aplicada a los precios en USD para mostrar en RUB en las tarjetas de publicaciones y guardada como snapshot en los pedidos nuevos. Los pedidos pasados conservan su tasa original — solo los pedidos nuevos usan el valor actualizado.",
    "admin.fx.title": "Tasa USD → RUB",
    "admin.fx.base": "Tasa base (forex)",
    "admin.fx.markup": "Margen %",
    "admin.fx.effective": "Tasa efectiva",
    "admin.fx.currentSaved": "Guardado actualmente: base {base} + {markup}% = ",
    "admin.fx.save": "Guardar",
    "admin.fx.saving": "Guardando…",
    "admin.fx.saved": "Tasa de cambio actualizada",
    "admin.fx.error.base": "La base debe ser positiva",
    "admin.fx.error.markup": "El margen debe ser ≥ 0",
    "admin.fx.auto.title": "Auto-actualización CBR",
    "admin.fx.auto.on": "Activado — cada hora",
    "admin.fx.auto.off": "Desactivado — pausado",
    "admin.fx.auto.enabled": "Auto-actualización activada",
    "admin.fx.auto.disabled": "Auto-actualización desactivada",
    "admin.fx.auto.lastFetch": "Última consulta",
    "admin.fx.auto.status": "Estado",
    "admin.fx.auto.status.ok": "OK",
    "admin.fx.auto.status.fail": "Falló — {err}",
    "admin.fx.auto.status.unknown": "desconocido",
    "admin.fx.auto.cbrRate": "Tasa CBR",
    "admin.fx.auto.cbrRate.value": "1$ = {rate}₽ (antes del margen)",
    "admin.fx.auto.lastChange": "Último cambio",
    "admin.fx.auto.noChange": "aún sin cambios",
    "admin.fx.auto.source": "Fuente: cbr-xml-daily.ru · publica ~12:30 MSK",
    "admin.fx.auto.refreshNow": "Actualizar ahora",
    "admin.fx.auto.fetching": "Consultando…",
    "admin.fx.auto.cbrToast": "CBR: 1$ = {rate}₽",
    "admin.fx.auto.cbrFailed": "Falló la consulta a CBR: {err}",
    "admin.fx.auto.never": "nunca",
    "admin.fx.auto.relSec": "hace {n}s",
    "admin.fx.auto.relMin": "hace {n} min",
    "admin.fx.auto.relHour": "hace {n} h",
    "admin.fx.auto.relDay": "hace {n} d",
    "admin.pm.title": "Métodos de pago",
    "admin.pm.hint": "Se muestran a los clientes al pagar un pedido confirmado. Deja un campo vacío para ocultar ese método de pago. Los números/wallets NUNCA se escriben en el registro de auditoría — solo los nombres y longitudes de los campos.",
    "admin.pm.loading": "Cargando…",
    "admin.pm.usdt.title": "USDT (multired)",
    "admin.pm.usdc.title": "USDC (multired)",
    "admin.pm.sbp.title": "SBP (pagos rápidos rusos)",
    "admin.pm.sbp.phone": "Teléfono",
    "admin.pm.sbp.name": "Nombre del destinatario",
    "admin.pm.card.title": "Tarjeta a tarjeta",
    "admin.pm.card.number": "Número de tarjeta",
    "admin.pm.card.bank": "Banco",
    "admin.pm.card.holder": "Nombre del titular",
    "admin.pm.instructions.title": "Instrucciones (se muestran debajo de los métodos)",
    "admin.pm.instructions.ru": "Ruso",
    "admin.pm.instructions.en": "Inglés",
    "admin.pm.instructions.placeholderRu": "Можно платить в любой валюте…",
    "admin.pm.instructions.placeholderEn": "USDT preferred…",
    "admin.pm.save": "Guardar métodos de pago",
    "admin.pm.saving": "Guardando…",
    "admin.pm.saved": "Métodos de pago actualizados",
    "admin.pm.networkHint": "una fila por cadena · las etiquetas de red se normalizan a MAYÚSCULAS al guardar",
    "admin.pm.removeNetwork": "Quitar esta entrada",
    "admin.pm.addNetwork": "+ Agregar red",
    "admin.pm.placeholder.networkAddress": "dirección de wallet en esa cadena",
    "admin.groups.create": "+ Crear nuevo grupo",
    "admin.groups.members": "{n} miembros",
    "admin.groups.addTopic": "+ Tema",
    "admin.groups.delete": "Eliminar",
    "admin.groups.edit": "Editar",
    "admin.groups.save": "Guardar",
    "admin.groups.cancel": "Cancelar",
    "admin.groups.saved": "Guardado",
    "admin.groups.namePlaceholder": "Nombre",
    "admin.groups.descPlaceholder": "Descripción (opcional)",
    "admin.groups.deleteConfirm": "¿Eliminar el grupo \"{name}\" con todos sus temas y mensajes?",
    "admin.groups.error.nameRequired": "El nombre es obligatorio",
    "admin.groups.error.topicNameRequired": "El nombre del tema es obligatorio",
    "admin.groups.topic.edit": "Renombrar tema",
    "admin.groups.topic.delete": "Eliminar tema",
    "admin.groups.topic.saved": "Tema renombrado",
    "admin.groups.topic.deleteConfirm": "¿Eliminar el tema \"{name}\"?",
    "admin.users.role.admin": "ADMINISTRADOR",
    "admin.users.role.manager": "GERENTE",
    "admin.users.role.premium": "PREMIUM",
    "admin.users.role.banned": "BLOQUEADO",
    "admin.users.you": "(tú)",
    "admin.users.banReason": "Motivo:",
    "admin.users.role.customer.opt": "Cliente",
    "admin.users.role.manager.opt": "Gerente",
    "admin.users.role.admin.opt": "Administrador",
    "admin.users.premium.label": "Premium…",
    "admin.users.premium.1mo": "+ 1 mes",
    "admin.users.premium.3mo": "+ 3 meses",
    "admin.users.premium.12mo": "+ 12 meses",
    "admin.users.premium.revoke": "Revocar",
    "admin.users.premium.grant": "¿Otorgar {months} mes(es) de premium a \"{name}\"?",
    "admin.users.premium.revoke.confirm": "¿Revocar premium a \"{name}\"?",
    "admin.users.unban": "Desbloquear",
    "admin.users.ban": "Bloquear",
    "admin.users.ban.prompt": "¿Bloquear a \"{name}\"? Motivo opcional:",
    "admin.users.ban.placeholder": "Motivo (opcional)",
    "admin.users.premium.until": "Premium hasta {date}",
    "admin.users.premium.free": "Plan Free",
    "admin.channels.hint": "La comisión del organizador es tu margen sobre el precio convertido por FX. El cashback del cliente sale de ese margen al entregarse el pedido — p. ej. comisión 20% + cashback 3% = neto 17%. Los cambios aplican solo a pedidos NUEVOS; los pedidos históricos conservan su snapshot.",
    "admin.channels.empty": "Aún no hay canales.",
    "admin.channels.posts": "{n} publicaciones",
    "admin.channels.edit": "Editar",
    "admin.channels.close": "Cerrar",
    "admin.channels.delete": "Eliminar",
    "admin.channels.deleting": "Eliminando…",
    "admin.channels.editTitle.collapse": "Contraer",
    "admin.channels.editTitle.expand": "Editar nombre / descripción / propietario",
    "admin.channels.deleteTitle": "Eliminar canal (cascada a publicaciones)",
    "admin.channels.name": "Nombre",
    "admin.channels.name.placeholder": "Nombre del canal",
    "admin.channels.description": "Descripción",
    "admin.channels.description.placeholder": "Se muestra a los clientes en la lista de canales",
    "admin.channels.owner": "Propietario (gerente / administrador)",
    "admin.channels.owner.current": "Propietario actual (uid: {uid}…)",
    "admin.channels.fee": "% de comisión del organizador",
    "admin.storeFees.title":     "Comisión por tienda",
    "admin.storeFees.hint":      "Define la comisión para todos los canales de una tienda a la vez. Los canales individuales se pueden ajustar abajo.",
    "admin.storeFees.count":     "{n} canales",
    "admin.storeFees.mixed":     "valores variados",
    "admin.storeFees.apply":     "Aplicar",
    "admin.storeFees.applied":   "Comisión aplicada a {n} canales ({store})",
    "admin.channels.cashback": "% de cashback del cliente",
    "admin.channels.save": "Guardar cambios",
    "admin.channels.saving": "Guardando…",
    "admin.channels.saved": "Guardado",
    "admin.channels.savedShort": "Guardado",
    "admin.channels.netMargin": "Margen neto:",
    "admin.channels.netNegative": " — el cashback supera la comisión, perderías dinero por pedido",
    "admin.channels.error.fee": "La comisión debe ser 0..100",
    "admin.channels.error.cashback": "El cashback debe ser 0..100",
    "admin.channels.error.name": "El nombre debe tener al menos 2 caracteres",
    "admin.channels.delete.confirm": "¿Eliminar el canal \"{name}\"?\n\nEsto se propaga a TODAS sus publicaciones ({n} publicadas).\nEscribe el nombre del canal para confirmar.",
    "admin.channels.delete.mismatch": "El nombre no coincide — no se eliminó nada.",
    "admin.channels.delete.ok": "Canal \"{name}\" eliminado",
    "admin.wa.hint": "Pipeline de ingesta de WhatsApp. El número de teléfono de cada representante de tienda puede asociarse a un canal — las imágenes de productos y los pies de foto entrantes se parsean automáticamente (precio, tallas) y se convierten en una publicación borrador en ese canal. Activa «auto-publicar» cuando confíes en los parseos de un remitente concreto.",
    "admin.wa.configured": "Remitentes configurados",
    "admin.wa.configured.empty": "Aún no hay rutas — elige un remitente abajo o agrega uno manualmente.",
    "admin.wa.recent": "Remitentes recientes (últimos 30 días)",
    "admin.wa.recent.empty": "Aún no se han recibido mensajes. Envía un mensaje de WhatsApp a tu número de negocio para empezar.",
    "admin.wa.msgsLast": "{n} msjs · último: \"{text}\"",
    "admin.wa.routed": "✓ enrutado",
    "admin.wa.addRoute": "+ Agregar ruta",
    "admin.wa.addRoute.prompt": "¿Nombre para mostrar de {phone}?",
    "admin.wa.addRoute.placeholder": "p. ej. tienda Karl Lagerfeld",
    "admin.wa.addRoute.ok": "Ruta agregada — elige un canal abajo",
    "admin.wa.route.displayName": "Nombre para mostrar",
    "admin.wa.route.channelDefault": "(canal por defecto)",
    "admin.wa.route.autopublish": "Auto-publicar (solo parseos de alta confianza)",
    "admin.wa.route.delete": "Eliminar",
    "admin.wa.route.delete.confirm": "¿Eliminar ruta de {phone}?",
    "admin.wa.route.deleted": "Eliminada",
    "admin.wa.route.save": "Guardar",
    "admin.wa.route.saving": "…",
    "admin.wa.route.saved": "Guardado",
    "admin.tokens.newToken": "Nuevo token (cópialo ahora — no se mostrará de nuevo)",
    "admin.tokens.copy": "Copiar",
    "admin.tokens.dismiss": "Descartar",
    "admin.tokens.create": "Crear token",
    "admin.tokens.name": "Nombre",
    "admin.tokens.name.placeholder": "shopper-bot-prod",
    "admin.tokens.scopes": "Scopes (separados por coma)",
    "admin.tokens.scopes.placeholder": "posts.write",
    "admin.tokens.generate": "Generar",
    "admin.tokens.active": "Tokens activos",
    "admin.tokens.active.empty": "Ninguno.",
    "admin.tokens.createdAt": "creado {date}",
    "admin.tokens.lastUsedAt": " · último uso {date}",
    "admin.tokens.revoke": "Revocar",
    "admin.tokens.revoke.confirm": "¿Revocar \"{name}\"? Esto no se puede deshacer.",
    "admin.tokens.revoked.section": "Revocados ({n})",
    "admin.tokens.revoked.row": "{name} · revocado {date}",
    "admin.reds.empty": "No hay canjes pendientes.",
    "admin.reds.requested": "solicitado {when}",
    "admin.reds.sendTo": "Enviar a:",
    "admin.reds.fulfill": "Procesar",
    "admin.reds.fulfill.confirm": "¿Marcar {amount} como procesado para {email}?",
    "admin.reds.fulfill.notePrompt": "Nota de procesamiento (opcional)",
    "admin.reds.fulfill.placeholder": "código de tarjeta regalo, número de seguimiento, etc.",
    "admin.reds.cancel": "Cancelar",
    "admin.reds.cancel.title": "¿Motivo de la cancelación?",
    "admin.reds.cancel.placeholder": "Motivo de la cancelación",
    "admin.templates.hint.prefix": "Mensajes de DM reutilizables. Usa ",
    "admin.templates.hint.suffix": " como placeholders — se sustituyen cuando el equipo elige una plantilla desde el compositor del chat. Los gerentes pueden usar plantillas; solo los administradores las crean o editan.",
    "admin.templates.new": "+ Nueva plantilla",
    "admin.templates.empty": "Aún no hay plantillas. Crea la primera.",
    "admin.templates.delete": "Eliminar",
    "admin.templates.create.namePrompt": "¿Nombre de la plantilla?",
    "admin.templates.create.namePlaceholder": "Прибыл на склад",
    "admin.templates.create.bodyPrompt": "¿Cuerpo (ruso)?",
    "admin.templates.create.bodyPlaceholder": "Привет, {customer_name}! Заказ прибыл.",
    "admin.templates.create.ok": "Plantilla creada",
    "admin.templates.delete.confirm": "¿Eliminar la plantilla \"{name}\"?",
    "admin.templates.delete.ok": "Eliminada",
    "admin.audit.action.all": "Todas las acciones",
    "admin.audit.target.all": "Todos los tipos de objetivo",
    "admin.audit.reload": "Recargar",
    "admin.audit.reloading": "Cargando…",
    "admin.audit.empty": "Ninguna entrada de auditoría coincide con los filtros actuales.",
    "admin.audit.system": "(sistema)",
    "admin.audit.req": "req: {id}",
    "admin.curated.title": "Cola de compras curadas",
    "admin.curated.settings": "Ajustes",
    "admin.curated.filter.open": "Abiertas (por defecto)",
    "admin.curated.filter.submitted": "Enviadas",
    "admin.curated.filter.priced": "Cotizadas",
    "admin.curated.filter.purchased": "Compradas",
    "admin.curated.filter.invoiced": "Facturadas",
    "admin.curated.filter.paid": "Pagadas",
    "admin.curated.filter.completed": "Completadas",
    "admin.curated.filter.cancelled": "Canceladas",
    "admin.curated.filter.rejected": "Rechazadas",
    "admin.curated.refresh": "Actualizar",
    "admin.curated.count": "{n} solicitudes",
    "admin.curated.loading": "Cargando…",
    "admin.curated.empty": "Cola vacía.",
    "admin.curated.row.items": "{n} artículos · estimado {stated}",
    "admin.curated.row.final": " · final {final}",
    "admin.curated.row.account": "🔐 cuenta",
    "admin.curated.row.links": "🔗 enlaces",
    "admin.curated.back": "← Volver a la cola",
    "admin.curated.created": "creado {when}",
    "admin.curated.facts.type": "Tipo",
    "admin.curated.facts.site": "Sitio",
    "admin.curated.facts.recipient": "Destinatario",
    "admin.curated.facts.address": "Dirección",
    "admin.curated.facts.items": "Artículos",
    "admin.curated.facts.stated": "Total estimado",
    "admin.curated.facts.final": "Total final",
    "admin.curated.facts.currency": "Moneda preferida",
    "admin.curated.facts.notes": "Notas del revendedor",
    "admin.curated.facts.links": "Enlaces",
    "admin.curated.creds.title": "🔐 Credenciales de cuenta",
    "admin.curated.creds.purged": "Purgadas.",
    "admin.curated.creds.none": "No proporcionadas.",
    "admin.curated.creds.login": "Login:",
    "admin.curated.creds.password": "Contraseña:",
    "admin.curated.creds.audited": "Revelación auditada. No las pegues en el chat.",
    "admin.curated.creds.reveal": "Revelar (registrado en auditoría)",
    "admin.curated.invoice.waiting": "Esperando pago — {amount} {currency}",
    "admin.curated.invoice.expires": "Expira {when}",
    "admin.curated.invoice.markPaid": "Marcar como pagado",
    "admin.curated.invoice.markPaid.ok": "Marcado como pagado",
    "admin.curated.complete": "Completar y purgar credenciales",
    "admin.curated.complete.confirm": "¿Completar y purgar credenciales?",
    "admin.curated.complete.ok": "Completado",
    "admin.curated.reject.label": "Motivo de rechazo (obligatorio, se muestra al revendedor)",
    "admin.curated.reject.placeholder": "Motivo…",
    "admin.curated.reject.confirm": "Confirmar rechazo",
    "admin.curated.reject.cancel": "Cancelar",
    "admin.curated.reject.start": "Rechazar…",
    "admin.curated.reject.required": "El motivo es obligatorio",
    "admin.curated.reject.ok": "Rechazada",
    "admin.curated.price.title": "Definir total final (USD)",
    "admin.curated.price.notes": "Notas para el revendedor (opcional)",
    "admin.curated.price.notes.placeholder": "p. ej. el envío salió más caro de lo previsto",
    "admin.curated.price.save": "Guardar precio",
    "admin.curated.price.invalid": "Total inválido",
    "admin.curated.price.ok": "Cotizada",
    "admin.curated.purchased.title": "Marcar como comprado en el sitio externo",
    "admin.curated.purchased.receipt": "Recibo:",
    "admin.curated.purchased.uploaded": "Recibo subido",
    "admin.curated.purchased.mark": "Marcar como comprado",
    "admin.curated.purchased.ok": "Marcada como comprada",
    "admin.curated.invoice.title": "Emitir factura",
    "admin.curated.invoice.reissue": "Reemitir factura (enlace caducado)",
    "admin.curated.invoice.currency": "Moneda",
    "admin.curated.invoice.wallet": "Elegir wallet (desde los ajustes de administrador)",
    "admin.curated.invoice.wallet.placeholder": "— elegir wallet —",
    "admin.curated.invoice.target.rub": "Enlace de pago (de un solo uso, expira en 20 min)",
    "admin.curated.invoice.target.crypto": "Dirección de wallet",
    "admin.curated.invoice.placeholder.rub": "https://…",
    "admin.curated.invoice.placeholder.crypto": "0x… o T…",
    "admin.curated.invoice.preview": "El revendedor verá: ",
    "admin.curated.invoice.previewExpires": " · expira en 20 min",
    "admin.curated.invoice.send": "Enviar factura",
    "admin.curated.invoice.resend": "Reemitir factura",
    "admin.curated.invoice.required": "Se requiere destino de pago",
    "admin.curated.invoice.ok": "Facturada",
    "admin.curated.invoice.reissued": "Reemitida",
    "admin.curated.settings.back": "← Volver a la cola",
    "admin.curated.settings.title": "Ajustes de compras curadas",
    "admin.curated.settings.fxTitle": "TIPO DE CAMBIO RUB / USD PARA COMPRAS CURADAS",
    "admin.curated.settings.cbrBase": "Tipo CBR (base)",
    "admin.curated.settings.markup": "Margen %",
    "admin.curated.settings.effective": "Tipo efectivo",
    "admin.curated.settings.refreshCbr": "Actualizar tipo CBR",
    "admin.curated.settings.refreshing": "Actualizando…",
    "admin.curated.settings.refreshedToast": "Tipo CBR actualizado",
    "admin.curated.settings.updatedAt": "Actualizado {when}",
    "admin.curated.settings.fxHint": "El revendedor ve el tipo efectivo al emitir una factura en RUB. El tipo base viene de la misma fuente CBR que el FX de retail y se actualiza cada hora.",
    "admin.curated.settings.payInstructions": "Instrucciones de pago mostradas al revendedor (opcional)",
    "admin.curated.settings.wallets": "Wallets de cripto",
    "admin.curated.settings.addWallet": "+ Agregar wallet",
    "admin.curated.settings.save": "Guardar",
    "admin.curated.settings.saved": "Guardado",
    "admin.curated.settings.adminOnly": "Solo administrador",
    "admin.curated.settings.wallet.address": "dirección",
    "admin.curated.settings.wallet.label": "etiqueta",
    "admin.curated.reseller.title": "Compras curadas",
    "admin.curated.reseller.subtitle": "Pide a BuyerGram que haga la compra por ti — paga la factura en USDT, USDC o RUB.",
    "admin.curated.reseller.new": "+ Nueva solicitud",
    "admin.curated.reseller.empty": "Aún no hay compras curadas. Crea la primera — toma 2 minutos.",
    "admin.curated.reseller.loading": "Cargando…",
    "admin.curated.reseller.row.account": "Acceso a la cuenta",
    "admin.curated.reseller.row.links": "Lista de enlaces",
    "admin.curated.reseller.row.items.one": "artículo",
    "admin.curated.reseller.row.items.few": "artículos",
    "admin.curated.reseller.row.items.many": "artículos",
    "admin.curated.reseller.status.submitted": "Enviada",
    "admin.curated.reseller.status.priced": "Cotizada",
    "admin.curated.reseller.status.purchased": "Comprada",
    "admin.curated.reseller.status.invoiced": "Esperando pago",
    "admin.curated.reseller.status.paid": "Pagada",
    "admin.curated.reseller.status.completed": "Completada",
    "admin.curated.reseller.status.cancelled": "Cancelada",
    "admin.curated.reseller.status.rejected": "Rechazado",
    "admin.curated.reseller.back": "← Volver a la lista",
    "admin.curated.reseller.new.title": "Nueva compra curada",
    "admin.curated.reseller.mode.label": "¿Cómo compartirás los detalles?",
    "admin.curated.reseller.mode.links.title": "Lista de enlaces",
    "admin.curated.reseller.mode.links.desc": "Yo envío los enlaces de productos — BuyerGram los agrega al carrito y los compra.",
    "admin.curated.reseller.mode.account.title": "Acceso a la cuenta",
    "admin.curated.reseller.mode.account.desc": "El carrito está listo — yo comparto el login y la contraseña, tú solo tienes que pagar.",
    "admin.curated.reseller.account.site": "URL de la tienda",
    "admin.curated.reseller.account.login": "Login",
    "admin.curated.reseller.account.password": "Contraseña",
    "admin.curated.reseller.account.hint": "El login y la contraseña se guardan cifrados y se eliminan automáticamente cuando la solicitud se completa o se cancela. Si la tienda tiene 2FA activado, te escribiremos por DM al momento de la compra.",
    "admin.curated.reseller.links.label": "Enlaces de productos",
    "admin.curated.reseller.links.qty": "cant.",
    "admin.curated.reseller.links.add": "+ Agregar enlace",
    "admin.curated.reseller.delivery.name": "Nombre del destinatario",
    "admin.curated.reseller.delivery.name.placeholder": "Nombre completo",
    "admin.curated.reseller.delivery.address": "Dirección de envío",
    "admin.curated.reseller.delivery.address.placeholder": "Ciudad, calle, edificio, depto., código postal, teléfono",
    "admin.curated.reseller.itemCount": "Cantidad de artículos",
    "admin.curated.reseller.statedTotal": "Total esperado (USD)",
    "admin.curated.reseller.currency": "Moneda de la factura",
    "admin.curated.reseller.currency.rub": "RUB (al tipo de cambio de BuyerGram)",
    "admin.curated.reseller.notes": "Notas (opcional)",
    "admin.curated.reseller.notes.placeholder": "Talla, color, detalles…",
    "admin.curated.reseller.submit": "Enviar solicitud",
    "admin.curated.reseller.submitting": "Enviando…",
    "admin.curated.reseller.cancel": "Cancelar",
    "admin.curated.reseller.error.amount": "Ingresa el total esperado en USD",
    "admin.curated.reseller.error.noLinks": "Agrega al menos un enlace",
    "admin.curated.reseller.created.ok": "Solicitud creada",
    "admin.curated.reseller.detail.expired": "Enlace de pago expirado",
    "admin.curated.reseller.detail.expiredHint": "Escríbele por DM al gerente — te enviará un nuevo enlace.",
    "admin.curated.reseller.detail.paid": "Pago recibido",
    "admin.curated.reseller.detail.paidHint": "Procesando la compra.",
    "admin.curated.reseller.field.type": "Tipo de solicitud",
    "admin.curated.reseller.field.site": "Tienda",
    "admin.curated.reseller.field.products": "Artículos",
    "admin.curated.reseller.field.recipient": "Destinatario",
    "admin.curated.reseller.field.address": "Dirección",
    "admin.curated.reseller.field.itemCount": "Cantidad de artículos",
    "admin.curated.reseller.field.statedTotal": "Total declarado",
    "admin.curated.reseller.field.finalTotal": "Total final",
    "admin.curated.reseller.field.currency": "Moneda de la factura",
    "admin.curated.reseller.field.notes": "Tus notas",
    "admin.curated.reseller.field.managerNotes": "Notas del gerente",
    "admin.curated.reseller.field.rejectionReason": "Motivo del rechazo",
    "admin.curated.reseller.field.receipt": "Recibo de compra",
    "admin.curated.reseller.receipt.open": "Abrir",
    "admin.curated.reseller.cancelBtn": "Cancelar solicitud",
    "admin.curated.reseller.cancel.confirm": "¿Cancelar esta solicitud? No se puede deshacer.",
    "admin.curated.reseller.cancel.ok": "Solicitud cancelada",
    "admin.curated.reseller.invoice.title": "Factura",
    "admin.curated.reseller.invoice.minutesLeft": "~{n} min restantes para pagar",
    "admin.curated.reseller.invoice.cryptoAddress": "Dirección del monedero ({currency}):",
    "admin.curated.reseller.invoice.payCta": "Pagar ahora →",
    "admin.curated.reseller.invoice.refresh": "Actualizar estado",
    "admin.application.title": "Verificación de propiedad del canal",
    "admin.application.loading": "Cargando…",
    "admin.application.copy": "Copiar",
    "admin.application.copied": "Copiado ✓",
    "admin.application.step1.label": "Paso 1 · Código de verificación",
    "admin.application.step1.hint": "Publica este código en tu canal principal de ventas (Telegram, MAX, Instagram, VK o tu propio sitio web) — esto prueba que el canal es tuyo. Puedes eliminar la publicación una vez que se apruebe la solicitud.",
    "admin.application.step2.label": "Paso 2 · Enlace del canal",
    "admin.application.platform": "Plataforma",
    "admin.application.platform.vk": "VK",
    "admin.application.platform.ok": "Odnoklassniki",
    "admin.application.platform.website": "Sitio web propio",
    "admin.application.platform.other": "Otro",
    "admin.application.audience": "Tamaño de la audiencia",
    "admin.application.audience.lt100": "Menos de 100",
    "admin.application.audience.100_500": "100–500",
    "admin.application.audience.500_2k": "500–2.000",
    "admin.application.audience.2k_10k": "2.000–10.000",
    "admin.application.audience.10kp": "Más de 10.000",
    "admin.application.categories": "Categorías de productos",
    "admin.application.category.women.shoes": "Calzado de mujer",
    "admin.application.category.men.shoes": "Calzado de hombre",
    "admin.application.category.women.clothing": "Ropa de mujer",
    "admin.application.category.men.clothing": "Ropa de hombre",
    "admin.application.category.kids": "Ropa / calzado infantil",
    "admin.application.category.beauty": "Belleza y cuidado personal",
    "admin.application.category.electronics": "Electrónica",
    "admin.application.category.home": "Hogar y cocina",
    "admin.application.category.bags": "Bolsos y accesorios",
    "admin.application.category.other": "Otro",
    "admin.application.contact": "Contacto (usuario de Telegram o correo electrónico)",
    "admin.application.step3.label": "Paso 3 · Enlace a la publicación con el código",
    "admin.application.step3.hint": "La publicación debe ser pública para que el revisor pueda abrirla y ver el código. Si tu canal es privado, envía el código por DM a @buyergram_support con una captura de pantalla.",
    "admin.application.submit": "Enviar solicitud",
    "admin.application.submitting": "Enviando…",
    "admin.application.submitted.title": "Solicitud en revisión",
    "admin.application.submitted.body": "Normalmente revisamos en un plazo de 12 horas hábiles. Una vez aprobada, los precios y el reenvío se desbloquean automáticamente.",
    "admin.application.submitted.sent": "Lo que enviaste",
    "admin.application.rejected.title": "Solicitud rechazada",
    "admin.application.rejected.noReason": "No se dio motivo. Contacta con soporte.",
    "admin.application.rejected.newCode": "Emitimos un nuevo código de verificación. Publícalo en tu canal y envía un nuevo enlace de la publicación.",
    "admin.application.field.channel": "Canal",
    "admin.application.field.audience": "Audiencia",
    "admin.application.field.categories": "Categorías",
    "admin.application.field.contact": "Contacto",
    "admin.application.field.verificationPost": "Publicación de verificación",
    "dashboard.menu": "Menú",
    "dashboard.kicker": "Inicio",
    "dashboard.loading": "Cargando…",
    "dashboard.loadError": "No se pudo cargar el panel: {err}",
    "dashboard.subtitle": "Lo que ha pasado en tu tienda durante los últimos 7 días.",
    "dashboard.greeting.night": "Buenas noches",
    "dashboard.greeting.morning": "Buenos días",
    "dashboard.greeting.day": "Buen día",
    "dashboard.greeting.evening": "Buenas tardes",
    "dashboard.attention.events": "eventos sin leer",
    "dashboard.attention.cta": "Abrir →",
    "dashboard.kpi.forwards": "Reenvíos",
    "dashboard.kpi.delivered": "Entregados",
    "dashboard.kpi.orders": "Pedidos",
    "dashboard.kpi.hint.noForwards": "Aún no se ha reenviado nada",
    "dashboard.kpi.hint.forwardsOk": "{n} entregas exitosas",
    "dashboard.kpi.hint.deliveriesOk": "Tarjetas que llegaron a los suscriptores",
    "dashboard.kpi.hint.noDeliveries": "Aún no hay destinatarios",
    "dashboard.kpi.hint.ordersOk": "Realizados por tu tienda",
    "dashboard.kpi.hint.noOrders": "Aún no hay pedidos",
    "dashboard.section.pending": "Pedidos en curso",
    "dashboard.section.pending.allOrders": "Todos los pedidos",
    "dashboard.section.pending.empty": "Todos los pedidos entregados o aún no se han hecho.",
    "dashboard.section.pending.browseCta": "Explorar catálogo",
    "dashboard.section.reengage": "Vale la pena contactar",
    "dashboard.section.reengage.allCustomers": "Todos los clientes",
    "dashboard.section.activity": "Actividad de clientes",
    "dashboard.section.activity.empty": "Vincula un pedido a un cliente en la tarjeta del pedido — la actividad reciente por cliente aparecerá aquí.",
    "dashboard.section.activity.openCustomers": "Abrir Clientes",
    "dashboard.emptyState.title": "Lanza tu tienda en 5 minutos.",
    "dashboard.emptyState.body": "Configura tu tipo de cambio y un canal de reenvío, luego elige artículos del catálogo y envíalos a tus suscriptores.",
    "dashboard.emptyState.setup": "Configurar el reenvío",
    "dashboard.emptyState.browse": "Explorar catálogo",
    "dashboard.row.untitled": "(sin título)",
    "dashboard.row.openCta": "Abrir →",
    "dashboard.row.ago": "hace",
    "dashboard.row.now": "ahora mismo",
    "dashboard.status.placed": "realizado",
    "dashboard.status.confirmed": "confirmado",
    "dashboard.status.paid": "pagado",
    "dashboard.status.purchased": "comprado",
    "dashboard.status.received": "en almacén",
    "dashboard.status.shipped": "enviado",
    "dashboard.status.delivered": "entregado",
    "dashboard.status.cancelled": "cancelado",
    "dashboard.status.unavailable": "no disponible",
    "dashboard.unit.min": "min",
    "dashboard.unit.hour": "h",
    "dashboard.unit.day": "d",
    "dashboard.unit.mo": "mes",
    "mystore.menu": "Menú",
    "mystore.heading": "Mi tienda",
    "mystore.adminHeading": "Admin",
    "mystore.tab.home": "Inicio",
    "mystore.tab.customers": "Clientes",
    "mystore.tab.forward": "Reenvío",
    "mystore.tab.curated": "Compras curadas",
    "mystore.tab.stats": "Analítica",
    "mystore.section.customers": "Clientes",
    "mystore.section.forward": "Reenvío",
    "mystore.section.stats": "Analítica",
    "forward.title": "Reenviar {n}",
    "forward.cards.one": "tarjeta",
    "forward.cards.few": "tarjetas",
    "forward.cards.many": "tarjetas",
    "forward.allFilteredNote": "Todas las tarjetas del filtro actual",
    "forward.loadingSettings": "Cargando ajustes…",
    "forward.hint.noFx.title": "Configura primero tu tipo de cambio $ → ₽",
    "forward.hint.noFx.body": "Sin un tipo de cambio no podemos calcular el precio en ₽ para tu descripción. Abre los ajustes de reenvío y configura el tipo de cambio que quieres mostrarles a los suscriptores.",
    "forward.hint.openSettings": "Abrir ajustes",
    "forward.hint.noTarget.title": "Agrega un canal de reenvío",
    "forward.hint.noTarget.body": "Vincula un canal de Telegram (o varios) al que enviaremos las tarjetas seleccionadas. El bot debe ser administrador en ese canal.",
    "forward.hint.allDisabled.title": "Todos los canales desactivados",
    "forward.hint.allDisabled.body": "Tienes canales en los ajustes, pero ninguno está activado. Activa al menos uno para continuar.",
    "forward.hint.setup": "Configurar",
    "forward.preview.label": "Lo que verán los suscriptores (primera tarjeta)",
    "forward.preview.noImage": "sin imagen",
    "forward.preview.emptyCaption": "(descripción vacía — configura la plantilla)",
    "forward.preview.rate": "Tipo de cambio: ₽{rate} por $1",
    "forward.preview.batchSize": " · total de tarjetas: {n}",
    "forward.target.label": "Enviar a",
    "forward.target.kind.tg": "Telegram",
    "forward.target.kind.max": "MAX",
    "forward.target.soon": "pronto",
    "forward.cancel": "Cancelar",
    "forward.send": "Enviar {n}",
    "forward.sending": "Enviando…",
    "forward.result.failed": "No se envió nada",
    "forward.result.allOk": "Los {n} envíos se realizaron con éxito",
    "forward.result.partial": "{ok} de {total} envíos se realizaron con éxito",
    "forward.result.done": "Listo",
    "forward.result.notSent": "No enviados ({n})",
    "forward.result.andMore": "…y {n} más",
    "forward.history.title": "Historial de reenvíos",
    "forward.history.subtitle": "Toca un artículo para abrir su página de origen — ahí es donde realizas el pedido",
    "forward.history.tab.items": "Artículos",
    "forward.history.tab.batches": "Envíos",
    "forward.history.search.placeholder": "Buscar por marca, modelo, SKU",
    "forward.history.loading": "Cargando…",
    "forward.history.empty": "Todavía no has reenviado nada. Elige tarjetas en un canal y toca \"Reenviar seleccionadas\" — aparecerán aquí.",
    "forward.history.foundOf": "{n} de {total} elementos",
    "forward.history.count.one": "elemento",
    "forward.history.count.few": "elementos",
    "forward.history.count.many": "elementos",
    "forward.history.noMatch": "No se encontró nada para \"{q}\".",
    "forward.history.toSource": "Abrir fuente ↗",
    "forward.history.sendsCount": "{n} envíos",
    "forward.history.dash": "—",
    "forward.history.unitMin": "min",
    "forward.history.unitHour": "h",
    "forward.history.unitDay": "d",
    "forward.history.justNow": "ahora mismo",
    "forward.history.batchAllSent": "Los {n} enviados",
    "forward.history.batchPartial": "{ok} de {total} enviados",
    "forward.history.postMissing": "publicación eliminada o no disponible",
    "notifications.title": "Eventos",
    "notifications.push.title": "¿Activar push?",
    "notifications.push.body": "Tu navegador mostrará notificaciones incluso cuando BG esté cerrado.",
    "notifications.push.enable": "Activar",
    "notifications.feed.label": "Feed de notificaciones",
    "notifications.markAll": "Marcar todo",
    "notifications.loading": "Cargando…",
    "notifications.empty.title": "Todo tranquilo.",
    "notifications.empty.body": "Cuando se actualice un pedido, se acredite cashback o un cliente se conecte al bot — aparecerá aquí.",
    "notifications.loadMore": "Cargar más",
    "notifications.loadingMore": "Cargando…",
    "notifications.bucket.today": "Hoy",
    "notifications.bucket.week": "Esta semana",
    "notifications.bucket.older": "Antes",
    "notifications.row.unit.min": "min",
    "notifications.row.unit.hour": "h",
    "notifications.type.order": "Pedido {status}",
    "notifications.type.credit": "+${amt} acreditados",
    "notifications.type.credit.referral": "Bono de referido",
    "notifications.type.credit.cashback": "Cashback del pedido",
    "notifications.type.customerOrdered": "Nuevo pedido de {name}",
    "notifications.type.paymentRequested": "{name} pide un QR/enlace de pago",
    "notifications.type.paymentUploaded":  "{name} subió el pago — revísalo",
    "notifications.type.customerOrdered.unknown": "cliente",
    "notifications.type.forward": "Reenvío · {n} {noun}",
    "notifications.type.forward.cards.one": "tarjeta",
    "notifications.type.forward.cards.few": "tarjetas",
    "notifications.type.forward.cards.many": "tarjetas",
    "notifications.type.forward.ok": "{n} entregadas",
    "notifications.type.forward.none": "ninguna entregada",
    "notifications.type.forward.failed": " · {n} fallaron",
    "notifications.type.reminder": "Recordatorio para {count} cliente{suffix}",
    "notifications.type.reminder.suffix.one": "",
    "notifications.type.reminder.suffix.many": "s",
    "notifications.type.reminder.andMore": " y más…",
    "notifications.type.botLinked": "{name} se conectó al bot",
    "notifications.type.botLinked.unknown": "Un cliente",
    "notifications.type.botLinked.body": "Los mensajes ahora pasan directo",
    "notifications.type.reengage": "{n} clientes están esperando tu atención",
    "notifications.status.confirmed": "confirmado",
    "notifications.status.received": "recibido en el almacén",
    "notifications.status.shipped": "enviado",
    "notifications.status.delivered": "entregado",
    "notifications.status.cancelled": "cancelado",
    "notifications.status.unavailable": "agotado",
    "onboarding.step.welcome": "Bienvenida",
    "onboarding.step.fx": "Tasa",
    "onboarding.step.target": "Canal",
    "onboarding.step.done": "Listo",
    "onboarding.stepIndicator": "Paso {idx} de {total} · {label}",
    "onboarding.loading": "Cargando…",
    "onboarding.skip": "Omitir",
    "onboarding.back": "Atrás",
    "onboarding.saving": "Guardando…",
    "onboarding.finish": "Ver catálogo",
    "onboarding.next": "Siguiente",
    "onboarding.welcome.title": "BuyerGram — tu tienda, 100% tuya.",
    "onboarding.welcome.body": "Curamos tarjetas de productos de retailers de EE. UU. Elige las que te gusten y reenvíalas a tus suscriptores de Telegram bajo tu marca — sin logos ni marcas de agua de BuyerGram.",
    "onboarding.welcome.foot": "Dos minutos: define tu tasa en rublos, tu canal, y listo.",
    "onboarding.fx.title": "¿A qué tasa mostramos los rublos?",
    "onboarding.fx.body": "Todas las tarjetas del catálogo tienen precio en dólares. Convertiremos a rublos en el texto que envíes a tus suscriptores. Elige una tasa que se ajuste a tu margen.",
    "onboarding.fx.unit": "₽ / $1",
    "onboarding.fx.example": "Ejemplo: un artículo de $49.99 se muestra como ₽{rub}",
    "onboarding.fx.unusualLow": "La tasa está por debajo del rango habitual (60–200 ₽/$). Revísala.",
    "onboarding.fx.unusualHigh": "La tasa está por encima del rango habitual (60–200 ₽/$). Revísala.",
    "onboarding.fx.err.positive": "La tasa debe ser mayor que cero",
    "onboarding.target.title": "¿A dónde enviamos las tarjetas?",
    "onboarding.target.body": "Indícanos tu canal — añade @Buyergrambot como administrador con permisos de publicación, luego pega el chat_id o el @handle. El bot no puede leer los mensajes de tus suscriptores.",
    "onboarding.target.platform": "Plataforma",
    "onboarding.target.kind.tg": "Telegram",
    "onboarding.target.kind.max": "MAX",
    "onboarding.target.chatId": "chat_id o @handle del canal",
    "onboarding.target.chatId.placeholder.tg": "@mi_tienda  o  -1001234567890",
    "onboarding.target.chatId.placeholder.max": "chat-id del canal de MAX",
    "onboarding.target.label": "Etiqueta (opcional)",
    "onboarding.target.label.placeholder": "ej. Mi canal principal",
    "onboarding.target.err.empty": "Ingresa chat_id o @handle del canal",
    "onboarding.target.err.format": "Eso no parece un @handle ni un chat_id. Prueba @mi_tienda o -1001234567890.",
    "onboarding.done.title": "Listo.",
    "onboarding.done.body": "Tu tienda está configurada. Abre el catálogo desde la barra lateral, marca algunas tarjetas y toca \"Enviar\" — las publicaremos en tu canal con tus precios ya convertidos.",
    "onboarding.done.foot": "¿Necesitas ajustar algo? Todos los ajustes están en la pestaña \"Reenvío\" de tu tienda.",
    "code.lookup.title": "Buscar por código BG",
    "code.lookup.hint": "Ingresa el código que te envió un cliente. Encontraremos la tarjeta en cualquier canal.",
    "code.lookup.placeholder": "ej. 7F3K9PXY",
    "code.lookup.find": "Buscar",
    "code.lookup.finding": "…",
    "code.lookup.err.format": "El código debe tener 4–12 caracteres (sin 0, 1, I, ni O).",
    "code.lookup.err.notFound": "No se encontró. Revisa el código e inténtalo otra vez.",
    "code.lookup.err.generic": "Error",
    "code.lookup.err.noChannel": "Esta tarjeta no tiene canal",
    "code.lookup.source": "Fuente ↗",
    "code.lookup.sourcePremium": "Fuente en Premium ↗",
    "code.lookup.jump": "Ir al canal",
    "modals.group.title": "Nuevo grupo",
    "modals.group.name": "Nombre",
    "modals.group.name.placeholder": "ej. Equipo de ingeniería",
    "modals.group.desc": "Descripción",
    "modals.group.desc.placeholder": "¿De qué trata este grupo?",
    "modals.cancel": "Cancelar",
    "modals.group.create": "Crear",
    "modals.topic.title": "Nuevo tema",
    "modals.topic.name": "Nombre del tema",
    "modals.topic.name.placeholder": "ej. Anuncios",
    "modals.topic.add": "Añadir",
    "modals.channel.title": "Nuevo canal",
    "modals.channel.name": "Nombre",
    "modals.channel.name.placeholder": "Walmart Deals",
    "modals.channel.slug": "Slug (URL)",
    "modals.channel.slug.placeholder": "walmart-deals",
    "modals.channel.desc": "Descripción",
    "modals.channel.desc.placeholder": "Opcional — visible para los clientes",
    "modals.channel.fee": "Comisión de organizador, %",
    "modals.channel.fee.placeholder": "ej. 15",
    "modals.channel.cashback": "Cashback del cliente, % (del margen de la comisión de organizador)",
    "modals.channel.cashback.placeholder": "ej. 3",
    "modals.channel.create": "Crear",
    "modals.channel.creating": "Creando…",
    "post.modal.title": "Nueva publicación",
    "post.modal.images": "Imágenes",
    "post.modal.hint.touch": "más — añadir desde la cámara",
    "post.modal.hint.desktop": "⌘V para pegar · arrastra desde WhatsApp",
    "post.modal.uploading": "Subiendo…",
    "post.modal.add": "Añadir",
    "post.modal.photo": "Foto",
    "post.modal.channel": "Canal",
    "post.modal.title.field": "Título",
    "post.modal.title.placeholder": "ej. Vestido de satén DKNY",
    "post.modal.desc": "Descripción (RU)",
    "post.modal.desc.placeholder": "Descripción en ruso de la publicación",
    "post.modal.price": "Precio ($)",
    "post.modal.origPrice": "Precio original ($)",
    "post.modal.brand": "Marca",
    "post.modal.brand.placeholder": "DKNY",
    "post.modal.category": "Categoría",
    "post.modal.category.placeholder": "ropa",
    "post.modal.sourceUrl": "URL de la fuente",
    "post.modal.gb.tag": "Compra grupal",
    "post.modal.gb.label": " — recolecta pedidos hasta una meta o fecha límite, luego cierra",
    "post.modal.gb.target": "Meta (opc.)",
    "post.modal.gb.target.placeholder": "ej. 30",
    "post.modal.gb.deadline": "Fecha límite (opc.)",
    "post.modal.publish": "Publicar ahora",
    "post.modal.publishHint": " (solo administradores — los gerentes crean borradores)",
    "post.modal.cancel": "Cancelar",
    "post.modal.create": "Crear publicación",
    "post.modal.creating": "Creando…",
    "customer.detail.tab.contact": "Contactos",
    "customer.detail.tab.history": "Historial",
    "customer.detail.tab.notes": "Notas",
    "customer.detail.contacts": "Contactos",
    "customer.detail.tg.label": "Telegram:",
    "customer.detail.tg.none": "Sin Telegram registrado",
    "customer.detail.phone.label": "Teléfono:",
    "customer.detail.phone.none": "Sin teléfono registrado",
    "customer.detail.inBaseSince": "En tu CRM desde {date}",
    "customer.detail.stats.orders": "Pedidos",
    "customer.detail.stats.spent": "Total gastado",
    "customer.detail.stats.last": "Última actividad",
    "customer.detail.notes": "Notas",
    "customer.detail.notes.edit": "Editar",
    "customer.detail.notes.placeholder": "Talla de ropa, marcas favoritas, cumpleaños — cualquier cosa que te ayude a atender mejor a este cliente.",
    "customer.detail.notes.help": "{n}/2000 · Esc para cancelar · ⌘↵ para guardar",
    "customer.detail.notes.cancel": "Cancelar",
    "customer.detail.notes.save": "Guardar",
    "customer.detail.notes.saving": "Guardando…",
    "customer.detail.notes.clickToEdit": "Haz clic para editar",
    "customer.detail.notes.add": "+ Añadir una nota",
    "customer.detail.notes.savedToast": "Nota guardada",
    "customer.detail.reminders": "Recordatorios",
    "customer.detail.reminders.edit": "Editar",
    "customer.detail.reminders.add": "+ Recuérdame",
    "customer.detail.reminder.in": "Recordar en",
    "customer.detail.reminder.days": "días",
    "customer.detail.reminder.note.placeholder": "Nota (opcional)",
    "customer.detail.reminder.cancel": "Cancelar",
    "customer.detail.reminder.create": "Crear",
    "customer.detail.reminder.saving": "Guardando…",
    "customer.detail.reminders.empty": "Sin recordatorios activos.",
    "customer.detail.reminder.overdue": "Atrasado ",
    "customer.detail.reminder.done": "Listo",
    "customer.detail.reminder.delete": "Eliminar",
    "customer.detail.reminder.createdToast": "Recordatorio creado",
    "customer.detail.history": "Historial de pedidos",
    "customer.detail.history.loading": "Cargando…",
    "customer.detail.history.empty": "Sin pedidos todavía. Cuando hagas un pedido para este cliente, aparecerá aquí.",
    "customer.detail.delete": "Eliminar",
    "customer.detail.close": "Cerrar",
    "customer.detail.edit": "Editar",
    "customer.detail.reorderUnavailable": "Este artículo ya no está disponible",
    "customer.detail.bot.connected": "Conectado al bot · desde {date}",
    "customer.detail.bot.invite": "Invitar al bot",
    "customer.detail.bot.loading": "…",
    "customer.detail.bot.inviteHint": "El enlace es válido por 7 días. Cada clic genera uno nuevo.",
    "customer.detail.bot.notConfigured": "El bot para clientes aún no está configurado en esta plataforma.",
    "customer.detail.bot.linkCopied": "Enlace copiado — compártelo con el cliente.",
    "customer.detail.bot.linkError": "No se pudo crear el enlace",
    "customer.detail.order.untitled": "(sin título)",
    "customer.detail.order.dash": "—",
    "customer.detail.reorder": "Repetir pedido",
    "customer.detail.reorder.loading": "…",
    "bulk.title": "Mensaje a clientes",
    "bulk.template": "Plantilla de mensaje",
    "bulk.template.placeholder": "¡Hola {имя}! ...",
    "bulk.template.default": "¡Hola {имя}! Novedades en mi canal — échales un vistazo.",
    "bulk.template.vars": "Sustituye: {var1} — nombre del cliente · {var2} — usuario de Telegram sin @",
    "bulk.summary.bot": "vía bot · un solo clic",
    "bulk.summary.bot.sentOf": "({sent}/{total} enviados)",
    "bulk.summary.manual": "vía enlaces t.me · manualmente desde la lista de abajo",
    "bulk.summary.skip": "sin Telegram — omitidos",
    "bulk.sending": "Enviando…",
    "bulk.sent": "Enviados · {n}",
    "bulk.sendViaBot": "Enviar vía bot · {n}",
    "bulk.openFirst5": "Abrir los primeros 5 t.me",
    "bulk.close": "Cerrar",
    "bulk.list": "Destinatarios vía enlaces t.me",
    "bulk.open": "Abrir",
    "bulk.copy": "Copiar",
    "bulk.copy.title": "Copiar texto",
    "bulk.copied": "Copiado",
    "bulk.sentToast": "Enviados vía bot: {n}",
    "bulk.failedToast": "No entregados: {n}",
    "bulk.errorToast": "No se pudo enviar",
    "bulk.openSomeToast": "Se abrieron los primeros 5 de {n}. Usa los botones de cada fila para el resto.",
    "order.modal.forCustomer": "Para qué cliente",
    "order.modal.reorderTag": " · repetir pedido",
    "order.modal.forMe": "— Para mí —",
    "order.modal.priceWas": "antes ${amt}",
    "order.modal.priceNow": " · ahora ",
    "order.modal.phonePlaceholder": "+7 ...",
    "order.modal.countryDefault": "Rusia",
    "orders.modal.filter.all": "Todos",
    "orders.modal.empty": "Aún no hay pedidos.",
    "orders.modal.loading": "Cargando…",
    "channelfeed.menu": "Menú",
    "channelfeed.search.placeholder": "Buscar por marca, modelo, SKU",
    "channelfeed.search.clear": "Limpiar búsqueda",
    "channelfeed.filter.all": "Todos",
    "channelfeed.sort.newest": "Más recientes",
    "channelfeed.sort.priceAsc": "Precio ↑",
    "channelfeed.sort.priceDesc": "Precio ↓",
    "channelfeed.sort.discount": "Descuento",
    "channelfeed.view.list": "Lista",
    "channelfeed.view.grid": "Cuadrícula",
    "channelfeed.select": "Seleccionar",
    "channelfeed.selectAll": "Seleccionar todo",
    "channelfeed.deselectAll": "Quitar selección",
    "channelfeed.forwardSelected": "Reenviar seleccionados",
    "channelfeed.forwardFiltered": "Reenviar todos",
    "channelfeed.forwardFilteredN": "Reenviar los {n} del filtro",
    "channelfeed.cancelSelect": "Cancelar",
    "channelfeed.selectedCount": "Seleccionados: {n}",
    "channelfeed.selectedShort": "{n} seleccionados",
    "channelfeed.empty.staff": "Aún no hay publicaciones en este canal. Crea la primera.",
    "channelfeed.loadMore": "Cargar más",
    "channelfeed.close": "Cerrar",
    "channelfeed.newPost": "Publicación",
    "channelfeed.newPost.title": "Nueva publicación",
    "channelfeed.history": "Historial",
    "channelfeed.history.title": "Historial de reenvíos y enlaces de origen",
    "channelfeed.findByCode": "Buscar por código",
    "channelfeed.findByCode.title": "Encuentra una tarjeta por el código que te mencionó tu cliente",
    "channelfeed.forward.toggle.title": "Activar la selección de tarjetas para reenviar",
    "channelfeed.forward.locked.title": "Disponible tras la aprobación del canal",
    "channelfeed.forward.locked.label": "🔒 Reenvío",
    "channelfeed.forward.toggle.on": "Cancelar selección",
    "channelfeed.forward.toggle.off": "Seleccionar para reenviar",
    "channelfeed.heroTitle": "Selección\ndel día",
    "channelfeed.closingSoon.one": "{n} compra grupal cierra en las próximas 24h — súmate",
    "channelfeed.closingSoon.many": "{n} compras grupales cierran en las próximas 24h — súmate",
    "channelfeed.filterBar.brand": "Marca",
    "channelfeed.filterBar.brand.count": "Marca · {n}",
    "channelfeed.filterBar.brand.empty": "Sin marcas",
    "channelfeed.filterBar.priceMin": "mín",
    "channelfeed.filterBar.priceMax": "máx",
    "channelfeed.filterBar.anyDiscount": "Cualquier descuento",
    "channelfeed.filterBar.fromPct": "{n}%+",
    "channelfeed.filterBar.sort": "Ordenar",
    "channelfeed.filterBar.sort.newest": "Más recientes primero",
    "channelfeed.filterBar.sort.discountDesc": "Descuento ↓",
    "channelfeed.filterBar.sort.priceAsc": "Precio ↑",
    "channelfeed.filterBar.sort.priceDesc": "Precio ↓",
    "channelfeed.filterBar.sort.brand": "Marca A-Z",
    "channelfeed.filterBar.resultOf": "{n} de {total}",
    "channelfeed.filterBar.clear": "Limpiar",
    "channelfeed.noMatch.title": "Nada coincide con los filtros actuales",
    "channelfeed.noMatch.clear": "Limpiar filtros",
    "channelfeed.priceLocked": "Precio oculto",
    "channelfeed.priceLocked.full": "🔒 Precio oculto",
    "channelfeed.priceLocked.body": "Termina la verificación del canal para ver precios y reenviar tarjetas a tus clientes.",
    "channelfeed.code.copyTitle": "Clic para copiar",
    "channelfeed.code.copied": "Código copiado",
    "channelfeed.premiumSource": "Fuente en Premium ↗",
    "channelfeed.premiumSource.title": "Abrir Premium",
    "channelfeed.toast.forwardSent": "Reenvío enviado",
    "channelfeed.gb.outOf": " de {n}",
    "channelfeed.gb.remainDays": "{d}d {h}h",
    "channelfeed.gb.remainHours": "{h}h {m}m",
    "channelfeed.gb.remainMin": "{m}m",
    "channelfeed.gb.gbLabel": "GB",
    "channelfeed.gb.targetLabel": "GB · OBJETIVO",
    "channelfeed.age.justNow": "ahora mismo",
    "channelfeed.age.min": "hace {n} min",
    "channelfeed.age.hour": "hace {n} h",
    "channelfeed.age.day": "hace {n} d",
    "app.loading": "Cargando…",
    "app.sidebar.home": "Inicio",
    "app.sidebar.store": "Tienda",
    "app.sidebar.store.title": "Mi tienda",
    "app.fab.code": "Buscar por código",
    "app.fab.forwards": "Reenvíos",
    "app.fab.history": "Historial",
    "app.fab.newChat": "Iniciar un chat",
    "app.fab.newGroup": "Crear grupo",
    "app.fab.newChannel": "Crear canal",
    "app.fab.newTopic": "Crear tema",
    "app.notif.bell": "Notificaciones",
    "app.notif.bell.title": "Eventos",
    "app.balance.title": "Saldo de cashback",
    "app.help.title": "Ayuda",
    "app.settings.title": "Ajustes",
    "app.admin.title": "Administrador",
    "app.logout.title": "Cerrar sesión",
    "app.attach.title": "Adjuntar imagen",
    "app.attach.uploading": "Subiendo…",
    "app.attach.imageName": "Imagen",
    "app.send.title": "Enviar",
    "app.message.placeholder": "Mensaje",
    "app.message.captionPlaceholder": "Pie de foto para la imagen (opcional)",
    "app.message.edited": "(editado)",
    "app.message.editingCancel": "Cancelar",
    "app.message.editingSave": "Guardar",
    "app.message.menu.edit": "Editar",
    "app.message.menu.reply":  "Responder",
    "app.message.menu.copy":   "Copiar texto",
    "app.message.menu.pin":    "Fijar",
    "app.message.menu.unpin":  "Desfijar",
    "app.message.copied":      "Texto copiado",
    "app.message.attachment":  "Adjunto",
    "app.message.pinned":      "Mensaje fijado",
    "app.message.replyingTo":  "Responder a {name}",
    "app.reaction.more":       "Más emojis",
    "app.compose.emoji":       "Emoji",
    "app.chat.scrollDown":     "Ir al final",
    "app.voice.record":        "Grabar mensaje de voz",
    "app.voice.recording":     "Grabando…",
    "app.voice.send":          "Enviar",
    "app.voice.micDenied":     "Acceso al micrófono denegado",
    "app.message.menu.delete": "Eliminar",
    "app.message.deleteConfirm": "¿Eliminar este mensaje? No se puede deshacer.",
    "app.chatSearch.title": "Buscar en este chat",
    "app.chatSearch.placeholder": "Buscar mensajes…",
    "app.chatSearch.searching": "Buscando…",
    "app.chatSearch.empty": "Sin coincidencias",
    "app.chatSub.dm": "mensaje directo",
    "app.chatTitle.unknown": "Desconocido",
    "app.empty.dm.title": "Saluda",
    "app.empty.dm.body": "Envía el primer mensaje para iniciar la conversación.",
    "app.empty.topic.title": "Aquí hay silencio",
    "app.empty.topic.body": "Sé el primero en publicar en este tema.",
    "app.empty.staff.dmsCustomer": "Los clientes te contactarán mediante «Lo quiero» en una publicación. También puedes escribirle a alguien directamente.",
    "app.empty.staff.dmsCta": "Iniciar un chat",
    "app.newDm.title": "Nuevo mensaje",
    "app.newDm.empty.customer": "No hay gerentes disponibles ahora mismo. Inténtalo más tarde.",
    "app.newDm.empty.staff": "Aún no hay otros usuarios. Los clientes aparecerán aquí a medida que se registren.",
    "app.adminPanel.title": "Panel de administrador",
    "app.quickAdd.title":     "Agregar rápido desde tienda",
    "quickAdd.store":         "Tienda",
    "quickAdd.register.spec": "Voz descriptiva: \"60 tabletas de 1000 mcg\"",
    "quickAdd.register.editorial": "Voz editorial: materiales, silueta, detalle clave",
    "quickAdd.photos":        "Fotos",
    "quickAdd.camera":        "Cámara",
    "quickAdd.library":       "Galería",
    "quickAdd.photos.max":    "Máximo 4 fotos",
    "quickAdd.uploading":     "Subiendo…",
    "quickAdd.upload.failed": "Error al subir: {err}",
    "quickAdd.brand":         "Marca",
    "quickAdd.title":         "Título / modelo",
    "quickAdd.priceUsd":      "Precio $",
    "quickAdd.msrpUsd":       "Precio normal $",
    "quickAdd.itemNumber":    "N.º de artículo Costco",
    "quickAdd.itemNumber.hint": "De la etiqueta. Permite que el cupón actualice este precio.",
    "quickAdd.upc":           "Código de barras (UPC)",
    "quickAdd.upc.hint":      "El código del producto. Permite buscar por escaneo y deduplicar.",
    "quickAdd.scan":          "Escanear código",
    "scan.hint":              "Apunta la cámara al código de barras",
    "scan.cancel":            "Cancelar",
    "scan.close":             "Cerrar",
    "scan.starting":          "Iniciando cámara…",
    "scanfind.button":        "Escanear para buscar",
    "scanfind.found":         "{n} encontrado(s)",
    "scanfind.none":          "Aún no está en el catálogo",
    "scanfind.resultFound":   "{code} · {n} encontrado(s)",
    "scanfind.resultNone":    "{code} · no encontrado",
    "scanfind.clear":         "Limpiar",
    "scanfind.dupTitle":      "Ya está en el catálogo",
    "scanfind.dupHint":       "Actualiza su precio en la pestaña Precios en vez de volver a añadirlo.",
    "review.assignGender":    "Asignar género:",
    "review.mens":            "Hombre",
    "review.womens":          "Mujer",
    "review.kids":            "Niños",
    "review.moved":           "Movido",
    "edit.title":             "Editar artículo",
    "edit.channel":           "Canal",
    "edit.source":            "Descripción de Costco (inglés)",
    "edit.source.placeholder": "Pega la descripción del producto de Costco para traducir",
    "edit.translate":         "Traducir",
    "edit.translating":       "Traduciendo…",
    "edit.save":              "Guardar",
    "edit.saving":            "Guardando…",
    "edit.saved":             "Guardado",
    "edit.cancel":            "Cancelar",
    "edit.descriptionEs":     "Descripción (ES)",
    "edit.validThrough":      "Precio válido hasta",
    "edit.savePublish":       "Guardar y publicar",
    "edit.published":         "Publicado",
    "edit.photos":            "Fotos",
    "card.saleEnds":          "Válido hasta {date}",
    "scan.filled.item":       "N.º de artículo: {code}",
    "scan.filled.barcode":    "Código de barras: {code}",
    "scan.err.denied":        "Se denegó el acceso a la cámara. Permítela en Ajustes.",
    "scan.err.nocamera":      "No se encontró cámara en este dispositivo.",
    "scan.err.inuse":         "La cámara está en uso por otra app. Ciérrala e inténtalo.",
    "scan.err.generic":       "No se pudo iniciar la cámara. Escribe el número.",
    "quickAdd.descriptionEn": "Descripción (inglés) — fuente",
    "quickAdd.descriptionEn.hint": "El inglés se guarda y se traduce automáticamente a RU + ES al guardar.",
    "quickAdd.description":   "Descripción (RU)",
    "quickAdd.description.placeholder": "Generar con IA o escribir manualmente",
    "quickAdd.generate":      "Generar",
    "quickAdd.generating":    "Pensando…",
    "quickAdd.generate.needFields": "Ingresa marca o título primero",
    "quickAdd.generate.failed": "Generación falló: {err}",
    "quickAdd.notes":         "Nota personal",
    "quickAdd.notes.placeholder": "p.ej. tag .97, último-stock W234",
    "quickAdd.notes.hint":    "No publicado. Para tu propia memoria de precios.",
    "quickAdd.cancel":        "Cancelar",
    "quickAdd.publish":       "Publicar",
    "worklist.tab.add":       "Agregar",
    "worklist.tab.prices":    "Precios",
    "worklist.all":           "Todos",
    "worklist.loading":       "Cargando…",
    "worklist.empty":         "Sin artículos de almacén",
    "worklist.saving":        "Guardando…",
    "worklist.saved":         "Guardado: {n}",
    "worklist.savedPartial":  "Guardado {ok}, {fail} con error",
    "worklist.saveN":         "Guardar {n}",
    "worklist.noChanges":     "Sin cambios",
    "worklist.view.all":      "Todos",
    "worklist.view.toFinish": "Por terminar",
    "worklist.view.published":"Publicados",
    "worklist.draft":         "Borrador",
    "quickAdd.publishing":    "Publicando…",
    "quickAdd.published":     "Publicado",
    "quickAdd.publish.failed": "Error al publicar: {err}",
    "sidebar.quickAdd":       "Desde tienda",
    "app.groups.available": "Disponibles para unirse",
    "app.groups.my": "Mis grupos",
    "app.groups.members": "{n} miembros",
    "app.groups.topics": "{n} miembros · {t} temas",
    "app.groups.join": "Unirse",
    "app.groups.leave": "Salir",
    "app.channels.posts.one": "{n} publicación",
    "app.channels.posts.few": "{n} publicaciones",
    "app.channels.posts.many": "{n} publicaciones",
    "app.channels.cashback": "{n}% cashback",
    "app.upload.failed": "Error al subir: {err}",
    "app.tmpl.title": "Plantillas",
    "app.tmpl.short": "Plant.",
    "app.tmpl.heading": "Plantillas de respuesta",
    "app.tmpl.empty": "Aún no hay plantillas — agrégalas en el panel de administrador.",
    "app.applicationBanner.title": "Solicitud en revisión",
    "app.applicationBanner.body": "Los precios y el reenvío se desbloquean tras la aprobación.",
    "app.applicationBanner.open": "Abrir",
    "app.applicationBanner.view": "Ver",
    "app.applicationBanner.openApp": "Abrir solicitud",
    "app.applicationBanner.locked.title": "Verifica tu canal de ventas",
    "app.applicationBanner.locked.body": "Publica el código de verificación en tu canal principal — ese es el único paso para desbloquear precios y reenvío.",
    "app.applicationBanner.locked.cta": "Empezar",
    "app.applicationBanner.submitted.title": "Solicitud en revisión",
    "app.applicationBanner.submitted.body": "Revisaremos en un plazo de 12 horas. Los precios y el reenvío se desbloquearán automáticamente.",
    "app.applicationBanner.rejected.title": "Solicitud rechazada — necesita correcciones",
    "app.applicationBanner.rejected.body": "Abre la solicitud, mira el motivo y envía una nueva.",
    "app.applicationBanner.pending.title": "Verifica tu canal de ventas",
    "app.applicationBanner.pending.body": "Los precios y el reenvío están ocultos hasta la verificación. Toma unos minutos.",
  },
};

// English is the default — BuyerGram targets multilingual diaspora and
// emerging-market communities, and English is the lowest-friction
// universal fallback. Users pick their language on the auth screen
// (saved to localStorage pre-login, persisted to user.language post-
// signup) or via Settings → Profile → Language once authenticated.
export const LangContext = createContext("en");

/** Convert a UI language code ('en' | 'ru' | 'es') to the matching
 *  BCP-47 locale used by Intl APIs (Intl.DateTimeFormat, localeCompare,
 *  etc.). 'es' maps to es-419 (Latin America) — our copy is neutral
 *  Latin American Spanish; es-419 makes Intl.DateTimeFormat / number
 *  separators behave the way most LatAm readers expect. Default is
 *  en-US, matching the app-wide default language. */
export function localeFor(lang) {
  if (lang === "ru") return "ru-RU";
  if (lang === "es") return "es-419";
  return "en-US";
}

export function LangProvider({ lang, children }) {
  return <LangContext.Provider value={lang || "en"}>{children}</LangContext.Provider>;
}

/**
 * Returns a `t(key, params?)` function bound to the active language. Params
 * are substituted into `{name}` placeholders in the string.
 */
export function useT() {
  const lang = useContext(LangContext);
  return (key, params) => {
    const raw = STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key;
    if (!params) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, p) => (params[p] ?? `{${p}}`));
  };
}

/** Returns the active language code ('ru'|'en'|'es'). Used by
 *  components that want to pick between language-specific server-
 *  supplied strings (e.g. payment instructions, which come from
 *  settings as instructions_ru + instructions_en). Server-supplied
 *  fallback is intentionally English; Spanish callers should pass an
 *  explicit `_es` field only when the server has it. */
export function useLang() {
  return useContext(LangContext);
}

/**
 * Plural-form key picker. Returns `${base}.one`, `${base}.few`, or
 * `${base}.many` based on Russian plural rules (1 / 2-4 / 5+ with the
 * 11-14 carve-out).
 *
 * For English and Spanish — which only have singular/plural — the
 * dictionaries deliberately store the SAME string under `.few` and
 * `.many`, so the Russian-rules output still resolves correctly.
 * That lets call sites stay language-agnostic without threading lang
 * through every component.
 *
 * Pure function (not a hook) so it's safe to call inside `.map()`
 * callbacks, conditional renders, anywhere.
 */
export function plural(n, base) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${base}.one`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${base}.few`;
  return `${base}.many`;
}
